/**
 * Completion (v0.3) — fully context-aware suggestions.
 *
 * The provider walks tokens up to the cursor maintaining a brace-stack
 * that records what kind of body each open `{` introduced (module body,
 * surface body, type body, process body, view body, aspect block). Combined
 * with the token immediately to the left of the cursor and a "statement
 * start" flag (newline since last token, or last token was `{`), we route
 * to one of these context-specific suggestion strategies:
 *
 *   - after-dot          → children of the dotted path
 *   - after-arrow        → every interface or module, qualified (a callee may
 *                          name a module — a synthesized anonymous handler
 *                          interface stands in, §4.13.2). A leading `>` at a
 *                          fresh statement routes here too.
 *   - after-in           → module names that can host children
 *   - after-extends      → kind names with the right base
 *   - after-type-keyword → kind names (the parent for the new type)
 *   - module/surface body  → nested kinds + body keywords + fields from the kind template
 *   - process body       → module names + `alt` / `par`
 *   - view body          → view keywords + module names (for focus/group)
 *   - aspect block       → reused aspect keys + `drop`
 *   - type body          → like module body but with type-template modifiers
 *   - top-level          → `type` / `process` / `view` + every registered kind
 *
 * Mid-expression (not at statement start) suggestions are intentionally
 * narrow: just symbols whose names plausibly fit, plus relevant punctuation
 * keywords. The IDE's prefix filter does the rest.
 */

import {
  CompletionItemKind,
  InsertTextFormat,
  type CompletionItem,
  type CompletionItemLabelDetails,
  type CompletionList,
  type TextEdit,
} from "vscode-languageserver";
import type { QualifiedName, Token, TokenType, UseDecl } from "@archlang/engine";
import {
  spaceChain,
  buildIfaceCandidates,
  buildModuleCandidates,
  aspectDocs,
  aspectDocFor,
  BUILTIN_KINDS,
} from "@archlang/engine";
import type { SymbolEntry, SymbolTable } from "../workspace/symbol-table.js";
import type { ReferenceIndex } from "../workspace/reference-index.js";
import type { StdlibExport } from "../workspace/stdlib-export-index.js";
import type { FileState, Workspace } from "../workspace/workspace.js";
import { isIdentCharCode, isIdentContinueCode } from "../workspace/ident-chars.js";
import type { ResolvedModel, Value } from "@archlang/engine";
import {
  ANONYMOUS_INTERFACE_NAME,
  joinDescriptions,
  makeDescriptionContext,
  renderDescription,
  tokenize,
} from "@archlang/engine";
import type { ResolvedType } from "@archlang/engine";
import { CALC_UNITS, analyzeBlock } from "@archlang/calc";
import { collectStableIds, freshStableId, makeIdMint } from "./auto-id.js";
import { forEachReentryOccurrence } from "../workspace/reentry-labels.js";

/** The default plane every module is on (engine `PRIMARY_PLANE`, not exported
 *  from `@archlang/engine`; mirrored here for the `on <plane-list>` slot). */
const PRIMARY_PLANE = "primary";

const BODY_KEYWORDS = [
  "aspect",
  "required",
  "cascade",
  "cascade *",
  "append",
  "override",
  "drop",
  // `export <interface>` marks an interface callable across a space boundary.
  "export",
] as const;

// Control-flow + terminator step keywords legal at a process statement start
// (§process grammar). SLOT-LOCKED keywords are pruned OUT of this pool and
// offered only where the grammar admits them (P1a): `join`/`race`/`out` in the
// merge clause (after `parallel`), `one` after `select`, `unwind` inside a
// `reversible` body, `branch` inside a `parallel` body, `else` after an `if`.
const PROCESS_STMT_KEYWORDS = [
  "if",
  "select",
  "each",
  "try",
  "parallel",
  "await",
  "reversible",
  "do",
  "go",
  "dist",
  "fail",
  "finish",
  // NOT statement starts (P1b cleanup): `as` is a step SUFFIX (`> X.y as l`),
  // `on` is not a step start, and `catch` follows a `try` block — each offered
  // only where the grammar admits it (`catch` gated below by `catchLegal`).
] as const;

const VIEW_BODY_KEYWORDS = [
  "focus",
  "group",
  "by",
  "show",
  "hide",
  "style",
  "flow",
  "table",
  "matrix",
  "grid",
  "lens",
  "story",
  // `story { … }` body member. Here for the same reason `lane`/`pool` are
  // below, and NOT because `chapter` is legal at view-body level (it is not —
  // the parser rejects it there): scope resolution walks back to the enclosing
  // DECL head, so a nested clause body still reports `view`. Dropping it to
  // "tidy up the view-body list" would silently remove `chapter` completion
  // from inside a story, which is the only place it is offered at all.
  "chapter",
  "knob",
  // `flow … bpmn { … }` body members (bpmn lanes & pools) — the body shares
  // the view scope in the token-scope detector, so its heads complete here.
  "lane",
  "pool",
  "layout",
  "include",
  "exclude",
] as const;

const LABELS_BODY_KEYWORDS = ["required", "drop"] as const;

// v0.11 process-test vocabulary. `when` pin values: the answer words a choice
// resolves to (`fails`/`after`/`takes` also take an argument). `then` assertions:
// the modal + terminator + budget words.
const WHEN_PIN_KEYWORDS = [
  "yes",
  "no",
  "pass",
  "ok",
  "done",
  "never",
  "fails",
  "after",
  "takes",
  "elapses",
  "within",
] as const;

const THEN_ASSERTION_KEYWORDS = [
  "happens",
  "never",
  "sometimes",
  "fail",
  "finish",
  "time",
  "all",
] as const;

// Type-body keywords are the same as body keywords minus `override`
// (that makes sense on instances; in a type template you use `required`).
const TYPE_BODY_KEYWORDS = [
  "aspect",
  "required",
  "cascade",
  "cascade *",
  "append",
  "drop",
] as const;

// Token types that spell an identifier-like word — a partial one being typed
// looks the same whether the lexer matched a reserved keyword or an ordinary
// name (`do█` lexes as DO, `ser█` as IDENTIFIER). Used so the same-line back-
// check treats a half-typed keyword after `use`/`from`/`by`/`export` as a
// partial word to route, not a completed token that dead-ends.
const WORD_KEYWORD_TYPES: ReadonlySet<TokenType> = new Set<TokenType>([
  "TYPE", "IN", "LABELS", "ASPECT", "REQUIRED", "CASCADE", "APPEND", "OVERRIDE",
  "DROP", "PROCESS", "SUBPROCESS", "VIEW", "IF", "ELSE", "SELECT", "ONE", "EACH",
  "TRY", "CATCH", "PARALLEL", "BRANCH", "JOIN", "RACE", "OUT", "AWAIT",
  "REVERSIBLE", "UNWIND", "ON", "DO", "GO", "AS", "DIST", "FAIL", "FINISH",
  "FOCUS", "GROUP", "BY", "LAYOUT", "INCLUDE", "EXCLUDE", "USE", "FROM",
  "EXPORT", "TRUE", "FALSE",
]);

function isWordLike(type: TokenType): boolean {
  return type === "IDENTIFIER" || WORD_KEYWORD_TYPES.has(type);
}

export function provideCompletion(
  workspace: Workspace,
  symbols: SymbolTable,
  uri: string,
  line: number,
  character: number,
  refs?: ReferenceIndex,
  /** Stdlib export catalogue for auto-import (P3). Empty (the default) in
   *  provider unit tests + the bench (no stdlib mounted) → no auto-import items. */
  stdlibExports: readonly StdlibExport[] = []
): CompletionItem[] {
  // Markdown document (§4.15): structured completion inside a `[[ … ]]`
  // reference, plus the foreign value getter `[[ref]]@path`. A bare `@path`
  // interpolation is description-only (forbidden in a document), so it is not
  // offered here. Plain prose gets nothing.
  const docText = workspace.getDocumentText(uri);
  if (docText !== undefined) {
    const off = offsetAt(docText, line, character);
    const calc = calcContext(docText, off);
    if (calc) return calcCompletions(calc.before, calc.region);
    const inner = bracketContext(docText, off);
    if (inner !== null) return refCompletions(workspace, symbols, inner);
    const at = atRefContext(docText, off);
    if (at?.kind === "foreign") return foreignAspectCompletions(workspace, at.ref);
    return [];
  }

  const file = workspace.getFile(uri);
  if (!file) return topLevelSuggestions(workspace, symbols);

  // A `~{ … }` calc region inside an `.arch` description (§4.10.5).
  const off = offsetAt(file.text, line, character);
  const calc = calcContext(file.text, off);
  if (calc) return calcCompletions(calc.before, calc.region);

  // `[[ … ]]` reference inside an `.arch` description takes precedence over the
  // declaration-grammar completion below.
  const refInner = bracketContext(file.text, off);
  if (refInner !== null) return refCompletions(workspace, symbols, refInner);

  // `#` in a declaration header (`module #|`, `type #|`, `process #ab|`): mint a
  // fresh, meaningless, collision-checked stable ID (§auto-id). A `[[#id]]` ref
  // is handled above, so any `#` reaching here is a declaration ID slot.
  const idSlot = stableIdSlot(file.tokens, file.text, off);
  if (idSlot !== null) return generateIdCompletion(workspace, idSlot, line, character, off);

  // A SELECTOR slot (`show`/`hide`/`focus`/`style` subject, a policy rule, a
  // `query` body, `group by in`). Routed here, ahead of the `@`/`@@` and
  // grammar paths: every selector word lexes as an IDENTIFIER, so the
  // token-type-keyed grammar analysis cannot see the slot at all, and its
  // value-slot fallback used to answer a `show █` with the VALUES of whatever
  // `key:` last appeared in the view (`keyBeforeColon` does not stop at a
  // newline) — or, for `query X: █`, with field values for a field named `X`.
  // …but never INSIDE an open string: a glued aspect value (`@@zone:"█"`) is a
  // value slot, and the grammar admits only values there.
  const selSlot =
    stringContextAt(file.tokens, file.text, off) === null ? selectorSlotAt(file.text, off) : null;
  if (selSlot) {
    const selSpace = buildSpaceCtx(workspace, symbols, uri);
    const selItems = applySpaceAwareness(selectorCompletions(workspace, symbols, selSlot), selSpace, uri);
    return finalize(selItems, file.text, line, character, uri, refs, selSpace);
  }

  // `@aspectPath` interpolation off the brackets: bare (the owning node's aspects)
  // or the foreign getter `[[ref]]@path` (the referenced node's aspects).
  //
  // `@` is a SHARED sigil since v0.14 — it also opens a decoration prefix. The
  // two are told apart by POSITION, not by name: a decoration is written on its
  // own line above a declaration (§4.3.1), while an interpolation only ever sits
  // inside a description. Without this split, typing `@` at a declaration
  // position fell straight into `bareInterpCompletions`, found no owning node,
  // and returned NOTHING — the decoration menu was unreachable by the one
  // gesture that should open it.
  const at = atRefContext(file.text, off);
  if (at && at.kind === "bare" && atLineStart(file.text, at.atPos)) {
    return withReplaceRange(decorationItems(workspace), line, character, off - at.atPos);
  }
  if (at) {
    return at.kind === "foreign"
      ? foreignAspectCompletions(workspace, at.ref)
      : bareInterpCompletions(workspace, symbols, uri, at.atPos);
  }

  // A PLAIN open string literal (no `[[…]]`/`@`/calc — those precede this): a
  // view `include`/`exclude` pattern or a classification value. Inside a string
  // the grammar admits only these, so route to them (or nothing) — never dump
  // declaration keywords.
  const space = buildSpaceCtx(workspace, symbols, uri);
  const str = stringContextAt(file.tokens, file.text, off);
  if (str !== null) {
    const items = stringSlotCompletions(file, workspace, symbols, str.quoteOff);
    return finalize(applySpaceAwareness(items, space, uri), file.text, line, character, uri, refs, space);
  }

  const ctx = analyzeContext(file, workspace, line, character);
  const items = selectGrammarCompletions(ctx, workspace, symbols, stdlibExports, uri);
  // Auto-import (P3): at a declaration-kind position (top-level / module /
  // surface / type body statement start, no token-relative slot), offer
  // stdlib-exported kinds the file hasn't `use`d yet — accepting one inserts
  // the missing `use … from …` via `additionalTextEdits`. Gated on a typed
  // prefix so a blank statement start never dumps the whole stdlib.
  if (stdlibExports.length > 0 && ctx.tokenContext === "none" && ctx.atStatementStart) {
    const allow = autoImportBaseFilter(ctx.scope);
    if (allow) {
      items.push(
        ...autoImportKindCompletions(file, uri, workspace, stdlibExports, allow, off)
      );
    }
  }
  // `use <prefix>` (P1.3): offer the stdlib export catalogue as whole-clause
  // `<Type> from <pkg>` imports — the same index that powers auto-import — so
  // `use serv█` resolves to `service from arch.backend` before the package is
  // loaded. Gated on a typed prefix (like auto-import) so a bare `use █` doesn't
  // dump the whole stdlib; `*` stays (demoted) from `suggestUseMembers`.
  if (stdlibExports.length > 0 && ctx.tokenContext === "after-use") {
    items.push(...stdlibUseCompletions(file, workspace, stdlibExports, off));
  }
  // Ghost self-echo (P1.1): drop a ghost whose name is exactly the identifier
  // run under the cursor — it is the token the author is typing, so accepting it
  // would insert what's already there, and its exact-prefix match would steal
  // `preselect`. It is never a real target.
  const typedRun = typedPrefixAt(file.text, off);
  const deEchoed = typedRun
    ? items.filter(
        (it) => !((isGhostEcho(it) || isReusedEcho(it)) && String(it.label) === typedRun)
      )
    : items;
  // Space-boundary honesty + hoist-aware inserts (P2a): drop unexported
  // cross-space types, sink unexported cross-space interfaces, and shorten
  // cross-file module/interface inserts to their minimal unique suffix.
  const cleaned = applySpaceAwareness(deEchoed, space, uri);
  // Replace the half-typed word (not just insert at the cursor), fold in
  // frequency + locality tiers, and bias exact-prefix matches to the top.
  return finalize(cleaned, file.text, line, character, uri, refs, space);
}

/**
 * Lazily fill an item's heavy `documentation` (markdown with ref resolution),
 * deferred from list-building so a keystroke doesn't render docs for every
 * candidate — only the highlighted one is resolved.
 */
export function resolveCompletion(
  workspace: Workspace,
  item: CompletionItem
): CompletionItem {
  const data = item.data as { doc?: string } | undefined;
  if (data?.doc && !item.documentation) {
    item.documentation = {
      kind: "markdown",
      value: renderCompletionDescription(workspace, data.doc),
    };
  }
  return item;
}

function selectGrammarCompletions(
  ctx: AnalyzedContext,
  workspace: Workspace,
  symbols: SymbolTable,
  stdlibExports: readonly StdlibExport[] = [],
  uri?: string
): CompletionItem[] {
  // Token-relative contexts (highest precedence).
  switch (ctx.tokenContext) {
    case "after-dot":
      return suggestDottedChildren(workspace, symbols, ctx.path, uri) ??
        suggestProcessCalleesIfInProcess(ctx, symbols) ??
        [];
    case "after-arrow":
      return suggestProcessCallees(workspace, symbols, ctx.flowModules);
    case "after-in":
      return suggestModuleNames(workspace, symbols);
    case "after-each-in":
      return suggestEachCollection(workspace, symbols);
    case "after-extends":
      return suggestExtendableTypes(workspace);
    case "after-type-keyword":
      return suggestExtendableTypes(workspace);
    case "after-export":
      return suggestAfterExport(workspace);
    case "after-from":
      return suggestPackageNames(workspace);
    case "after-by":
      // Every `by` slot in the language takes a SIGILED getter — `group by`,
      // `sort by`, `order by`, `lane by`, `pool by` (GRAMMAR §View
      // Declarations). Inserting a bare key wrote exactly what the parser
      // diagnoses (`parse.groupByGetterSigiled`).
      return sigiled(aspectKeyCompletions(workspace, false));
    case "after-aspect":
      // Inline `aspect █` / `aspect d█` (P2.3): route to the same reused-key
      // completions the block form offers, inserting `key: ` (assign form).
      return aspectKeyCompletions(workspace, true);
    case "after-use":
      return suggestUseMembers(workspace, stdlibExports);
    case "after-owner":
      return suggestAfterOwner(workspace, symbols, ctx.ownerName);
    // Process-language structural slots (P1a).
    case "after-do":
      return suggestAfterDo(workspace, symbols);
    case "after-go":
      return suggestAfterGo(workspace, ctx.uri, ctx.enclosingProc);
    case "after-unwind-callee":
      return suggestUnwindCallee(workspace, symbols, ctx.unwindCallee, ctx.flowModules);
    case "after-parallel":
      return suggestAfterParallel();
    case "after-select":
      return suggestAfterSelect();
    case "after-await":
      return suggestAwaitHead();
    case "after-on-plane":
      return suggestOnPlane(workspace);
    case "after-on-trigger":
      return suggestOnTrigger();
    case "after-on-caller":
      // `subprocess X on <Caller>` — a fresh caller-param name; offer nothing
      // (never aspect keys / planes, which would mislead into an ASPECT slot).
      return [];
    case "after-owner-marker":
      return suggestOwnerNames(workspace, symbols);
    case "after-snake":
      return suggestAfterSnake();
    case "after-merge-bool":
      return suggestMergeBranchNames(ctx.mergeBranchNames);
    // Structure slots (P1b).
    case "after-drop":
      return suggestInheritedEntities(workspace, ctx.scope, ctx.scopeTypeName);
    case "after-override":
      return suggestOverrideType(workspace);
    case "after-override-type":
      return suggestInheritedEntities(workspace, ctx.scope, ctx.scopeTypeName);
    case "after-focus":
      return aspectKeyCompletions(workspace, false);
    case "after-layout":
      return suggestLayoutAlgorithms();
    case "after-include-pattern":
      return suggestViewPatterns(workspace, symbols, true);
    case "after-kind-keyword":
      // User just typed a kind name and a space — they're about to type
      // the declaration's name. Don't suggest anything; the IDE shouldn't
      // pop up keywords there.
      return [];
  }

  // Statement-start, scope-driven suggestions.
  if (!ctx.atStatementStart) {
    // Same-line saga `unwind` suffix (P2.4): `> Inventory.reserve u█` — after a
    // completed call step inside a `reversible` frame, offer `unwind` to open the
    // compensation clause (the canonical PROCESS.md §8 shape).
    if (ctx.unwindSuffixLegal) return [kwRanked("unwind", RANK_PRIMARY)];
    // A roster value (`aspect key for: █`) — module refs + `as`, no strings.
    if (ctx.rosterValue) return suggestAspectMembership(workspace, symbols, undefined, ctx.valueRefBefore, true);
    // Value position (`key: |`): offer values already assigned to this key —
    // and ONLY those. A module/interface/process name is never a field value,
    // so the symbol table must not be dumped here (the old fall-through did).
    if (ctx.valueKey) {
      // An aspect value (membership or classification): declared modules
      // (membership, NO ghosts §4.4) + previously-used values + `as`.
      if (ctx.aspectValue)
        return suggestAspectMembership(workspace, symbols, ctx.valueKey, ctx.valueRefBefore, false);
      // A view `focus <key>: █` value is an aspect value on that key.
      if (ctx.scope === "view") return aspectValueCompletions(workspace, ctx.valueKey);
      const full =
        ctx.fieldPrefix && ctx.fieldPrefix.length > 0
          ? [...ctx.fieldPrefix, ctx.valueKey].join(".")
          : ctx.valueKey;
      return fieldValueCompletions(workspace, full);
    }
    // Key position inside an aspect block (e.g. right after a comma): the author
    // is starting a new aspect key, not a value — offer reusable keys, not names.
    if (ctx.scope === "labels") return aspectBodySuggestions(workspace, ctx.existingAspectKeys);
    // Otherwise a bare identifier slot — a new declaration's name, or a free-form
    // reference. Promote only the ghosts already referenced; never dump every
    // declared symbol (you cannot reuse an existing name for a new declaration),
    // and never leak aspects or the synthesized `(anonymous)` sentinel.
    return suggestNamesOnly(workspace);
  }

  switch (ctx.scope) {
    case "top":
      return topLevelSuggestions(workspace, symbols);
    case "module":
      return moduleBodySuggestions(
        workspace,
        ctx.scopeTypeName,
        "module",
        ctx.onBlock,
        symbols,
        ctx.scopeQualifiedName
      );
    case "surface":
      return moduleBodySuggestions(workspace, ctx.scopeTypeName, "surface");
    case "process":
      return processBodySuggestions(workspace, symbols, ctx.proc);
    case "view":
      return viewBodySuggestions(workspace, symbols);
    case "test":
      return testBodySuggestions();
    case "when":
      return whenBodySuggestions();
    case "then":
      return thenBodySuggestions();
    case "labels":
      return aspectBodySuggestions(workspace, ctx.existingAspectKeys);
    case "type":
      return typeBodySuggestions(workspace, ctx.scopeTypeName);
    case "interface":
      return interfaceBodySuggestions();
    case "field-body":
      return fieldBodySuggestions(workspace, ctx);
  }
}

/**
 * Attach a replace `textEdit` (covering the half-typed identifier under the
 * cursor) and a `filterText` to every item, and float exact-prefix matches to
 * the top. Without a replace range, completing `Orders.Cr|` → `Create` would
 * append rather than replace in clients that don't word-detect dotted paths.
 */
function finalize(
  items: CompletionItem[],
  text: string,
  line: number,
  character: number,
  uri: string,
  refs?: ReferenceIndex,
  space?: SpaceCtx
): CompletionItem[] {
  const offset = offsetAt(text, line, character);
  // Word boundaries mirror the lexer's identifier rule: ID_Continue runs joined
  // by INTERNAL hyphens only. A leading/trailing `-` is NOT part of the word, so
  // the replace range never swallows a stray hyphen the lexer left as its own
  // token (`Create-` → the `-` stays; accepting `Create` must not delete it).
  const HYPHEN = 0x2d;
  let start = offset;
  while (start > 0) {
    const c = text.charCodeAt(start - 1);
    if (isIdentContinueCode(c)) { start--; continue; }
    if (c === HYPHEN && start - 2 >= 0 && isIdentContinueCode(text.charCodeAt(start - 2))) {
      start--; // internal hyphen (ident char on its left)
      continue;
    }
    break;
  }
  const word = text.slice(start, offset);
  const wordLower = word.toLowerCase();
  // The word under the cursor may extend PAST it (mid-word completion:
  // `Pay|ments`). `insertRange` ends at the cursor (word-start → cursor);
  // `replaceRange` swallows the trailing suffix (word-start → word-END).
  // Identifiers never span a newline, so `end` stays on this line.
  let end = offset;
  while (end < text.length) {
    const c = text.charCodeAt(end);
    if (isIdentContinueCode(c)) { end++; continue; }
    if (c === HYPHEN && isIdentContinueCode(text.charCodeAt(end + 1))) {
      end++; // internal hyphen (ident char on its right)
      continue;
    }
    break;
  }
  const insertRange = {
    start: { line, character: character - (offset - start) },
    end: { line, character },
  };
  const replaceRange =
    end > offset
      ? {
          start: insertRange.start,
          end: { line, character: character + (end - offset) },
        }
      : null;
  // How many items exact-prefix-match the typed word — a lone winner earns
  // `preselect` (P2a item 4); a shared prefix is ambiguous, so nobody does.
  let prefixMatches = 0;
  if (wordLower) {
    for (const it of items) if (String(it.label).toLowerCase().startsWith(wordLower)) prefixMatches++;
  }
  for (const it of items) {
    const newText = it.insertText ?? String(it.label);
    // Mid-word (a suffix trails the cursor): an InsertReplaceEdit so accepting
    // replaces the whole word (P2.10), not just the typed prefix — leaving no
    // stray `ments` after `Pay|ments`. The client-capability filter degrades it
    // to a plain TextEdit for clients without insertReplace support (see
    // `toCompletionList`). At word-end (the common case) stay a plain TextEdit —
    // byte-identical to the pre-P2b shape.
    it.textEdit = replaceRange
      ? { newText, insert: insertRange, replace: replaceRange }
      : { range: insertRange, newText };

    // An auto-import `use …` additionalTextEdit that lands at the SAME position
    // as this item's primary edit start would OVERLAP it (LSP forbids overlapping
    // additionalTextEdits). This happens only on a fresh single-line file
    // (`serv█` at column 0, no existing `use` block, so the `use` inserts at the
    // word-start). Fold such a zero-width insert into the primary newText as a
    // prefix and drop it — one clean, non-overlapping edit.
    if (it.additionalTextEdits && it.additionalTextEdits.length > 0) {
      const ps = insertRange.start;
      let prefix = "";
      const kept: typeof it.additionalTextEdits = [];
      for (const ate of it.additionalTextEdits) {
        const r = ate.range;
        const zeroWidth = r.start.line === r.end.line && r.start.character === r.end.character;
        if (zeroWidth && r.start.line === ps.line && r.start.character === ps.character) {
          prefix += ate.newText;
        } else {
          kept.push(ate);
        }
      }
      if (prefix) {
        const te = it.textEdit;
        it.textEdit =
          "range" in te
            ? { range: te.range, newText: prefix + te.newText }
            : { insert: te.insert, replace: te.replace, newText: prefix + te.newText };
        it.additionalTextEdits = kept.length > 0 ? kept : undefined;
      }
    }
    if (!it.filterText) it.filterText = String(it.label);

    // Within a category bucket, prefer symbols that are near the cursor
    // (locality tier: same file > same space > same package > imported) and are
    // referenced more (frequency). `sortText` is `<rank><locality><invFreq>
    // <label>`; lower sorts first.
    const data = it.data as { id?: string; uri?: string } | undefined;
    if (refs && data?.id && it.sortText) {
      const rank = it.sortText[0]!;
      const near = space ? space.tier(data.uri) : data.uri === uri ? "0" : "1";
      const count = refs.referencesTo(data.id).length;
      const invFreq = String(Math.max(0, 9999 - count)).padStart(4, "0");
      it.sortText = `${rank}${near}${invFreq}${String(it.label).toLowerCase()}`;
    }

    if (wordLower) {
      const lw = String(it.label).toLowerCase();
      if (lw.startsWith(wordLower)) {
        // Prefix match — the strongest signal; prepend a top bucket.
        it.sortText = `0${it.sortText ?? lw}`;
        if (prefixMatches === 1) it.preselect = true;
      } else if (lw.includes(wordLower)) {
        // Substring match — float above non-matches, below prefix matches
        // ('0~' sorts after '0'+any letter/digit but before category '1'…).
        it.sortText = `0~${it.sortText ?? lw}`;
      } else if (isSubsequence(wordLower, lw)) {
        // camelCase-subsequence match (`OCr` → `OrderCreated`): weaker than a
        // substring, still ahead of non-matches ('0~~' sorts after any '0~<x>'
        // substring bucket but before category '1'…).
        it.sortText = `0~~${it.sortText ?? lw}`;
      }
    }
  }
  return items;
}

/** Whether `needle`'s characters appear in `haystack` in order (a camelCase
 *  subsequence fit: `ocr` ⊆ `ordercreated`). Both already lowercased. */
function isSubsequence(needle: string, haystack: string): boolean {
  if (needle.length === 0) return false;
  let i = 0;
  for (let j = 0; j < haystack.length && i < needle.length; j++) {
    if (haystack[j] === needle[i]) i++;
  }
  return i === needle.length;
}

function isIdentChar(code: number): boolean {
  // v0.8.2: match the lexer (Unicode ID_Continue + internal hyphen), not ASCII.
  return isIdentCharCode(code);
}

// =============================================================================
// Client-capability shaping + transport list (P2b). The provider always SETS
// the modern fields (`labelDetails`, `InsertReplaceEdit`, snippet insert text);
// the connected client's advertised capabilities gate which of them survive to
// the wire. `ArchlangServices` supplies the caps — defaulting to full support,
// so provider/bench tests that call the service directly (no client handshake)
// still see every field.
// =============================================================================

/** Which modern CompletionItem features the connected client can consume. */
export interface ClientCompletionCaps {
  /** Client renders `labelDetails` (VS Code; Monaco ≥0.34). */
  readonly labelDetails: boolean;
  /** Client honors `InsertReplaceEdit` textEdits (replace-to-word-end). */
  readonly insertReplace: boolean;
  /** Client expands `${1:…}` snippet insert text. */
  readonly snippets: boolean;
}

/** Everything on — the shape provider unit tests + the bench assume (they call
 *  the service unmediated by a client handshake). */
export const FULL_COMPLETION_CAPS: ClientCompletionCaps = {
  labelDetails: true,
  insertReplace: true,
  snippets: true,
};

/** Above this the list is truncated to its top-ranked slice and flagged
 *  `isIncomplete`, so the client re-queries as the prefix narrows (LSP §
 *  CompletionList.isIncomplete). Well above any single legitimate slot. */
export const MAX_COMPLETION_ITEMS = 100;

/** Wrap a provider result as a capability-shaped `CompletionList`: strip the
 *  fields the client can't consume, then cap oversized lists (`isIncomplete`). */
export function toCompletionList(
  items: CompletionItem[],
  caps: ClientCompletionCaps = FULL_COMPLETION_CAPS
): CompletionList {
  for (const it of items) applyClientCaps(it, caps);
  if (items.length <= MAX_COMPLETION_ITEMS) return { items, isIncomplete: false };
  // Oversized: keep the genuine top-N in client-sort order so the surviving
  // slice is the best candidates, not an arbitrary prefix of build order.
  const top = [...items].sort(byClientSort).slice(0, MAX_COMPLETION_ITEMS);
  return { items: top, isIncomplete: true };
}

/** The client's own ordering: `sortText` (fallback label), label as tiebreak. */
function byClientSort(a: CompletionItem, b: CompletionItem): number {
  const ka = a.sortText ?? a.label;
  const kb = b.sortText ?? b.label;
  if (ka < kb) return -1;
  if (ka > kb) return 1;
  return a.label.localeCompare(b.label);
}

/** Strip / degrade the item fields the client didn't advertise support for.
 *  Mutates the freshly-built item in place (the provider owns it per request). */
function applyClientCaps(it: CompletionItem, caps: ClientCompletionCaps): void {
  if (!caps.labelDetails && it.labelDetails) delete it.labelDetails;
  if (!caps.insertReplace && it.textEdit && "insert" in it.textEdit) {
    // Degrade InsertReplaceEdit → a plain TextEdit over the insert range
    // (word-start → cursor) — the pre-P2b replace behavior.
    it.textEdit = { range: it.textEdit.insert, newText: it.textEdit.newText };
  }
  if (!caps.snippets && it.insertTextFormat === InsertTextFormat.Snippet) {
    const plain = stripSnippetPlaceholders(it.insertText ?? String(it.label));
    it.insertText = plain;
    it.insertTextFormat = InsertTextFormat.PlainText;
    if (it.textEdit) it.textEdit.newText = plain;
  }
}

/** Reduce snippet insert text to what a plain-text client should insert:
 *  `${1:default}` → `default`, `${1|a,b|}` → `a`, `${1}`/`$1`/`$0` → "". */
function stripSnippetPlaceholders(snippet: string): string {
  return snippet
    .replace(/\$\{\d+\|([^,|}]*)[^}]*\}/g, "$1") // choice → first option
    .replace(/\$\{\d+:([^}]*)\}/g, "$1") // placeholder → its default
    .replace(/\$\{\d+\}/g, "")
    .replace(/\$\d+/g, "")
    .replace(/\\([$}\\])/g, "$1"); // unescape literal $ } \
}

// =============================================================================
// Space awareness (P2a) — locality tiers, cross-space boundary honesty, and
// hoist-aware insert text (§4.11). The only hoisting facility re-exported from
// `@archlang/engine` is `spaceChain`; the suffix index + nearest-first scoped
// resolution below mirror engine/hoist-index.ts (`buildScopedModuleIndex` /
// `resolveScoped` / `sameSpace`, not re-exported) over the LSP symbol model.
// =============================================================================

/** A candidate owner (module or surface, an interface owner-path segment) for a
 *  written path-suffix, tagged with its declaring space. */
interface OwnerCand {
  readonly path: string;
  readonly space: QualifiedName | undefined;
}

interface SpaceCtx {
  /** The current file's space (undefined = package root / single-space). */
  readonly currentSpace: QualifiedName | undefined;
  /** False when the workspace declares no named spaces — every space op no-ops
   *  (single-space packages behave exactly as before P2a). */
  readonly multiSpace: boolean;
  /** The landed space of the file a symbol was declared in (v0.11 twin
   *  disambiguation — pass 4). */
  spaceOfUri(dataUri: string | undefined): QualifiedName | undefined;
  /** Locality-tier digit for a symbol defined in `dataUri`: same file (0) >
   *  same space (1) > same package (2) > imported (3). */
  tier(dataUri: string | undefined): string;
  /** Type names hidden from the current file's space — unexported types
   *  internal to another space; completing one teaches a hard
   *  CROSS_SPACE_TYPE_NOT_VISIBLE (§4.11), so they are dropped. */
  readonly hiddenTypes: ReadonlySet<string>;
  /** `"unexported"` when interface `qname` is reachable but not `export`ed
   *  across the boundary from the current space (down-rank + mark), else
   *  undefined (local / same-space / exported — offered normally). */
  interfaceBoundary(qname: string): "unexported" | undefined;
  /** The minimal unique path-suffix that resolves `qname` (a module/surface, or
   *  an interface whose owner hoists + verbatim leaf) from the current space.
   *  Returns the full `qname` when nothing shorter is unambiguous. */
  hoistSuffix(qname: string, isInterface: boolean): string;
  /** §4.11 surface elision: the short `Module.leaf` form for interface `qname`
   *  when its leaf names exactly one interface across the owning module's
   *  surface tree (and nothing else claims the short spelling). Returns `qname`
   *  unchanged when not elidable. */
  elideSurface(qname: string): string;
}

/** Segment-equality of two spaces (undefined = package root). Mirrors
 *  engine/hoist-index `sameSpace` (not re-exported). */
function sameSpaceLocal(a: QualifiedName | undefined, b: QualifiedName | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

/**
 * Resolve a written path-suffix against space-tagged candidates from a referrer
 * in `referrerSpace` — the sole full path it names, `"ambiguous"`, or undefined.
 * Nearest-first: the closest space on the referrer's chain with ≥1 match wins
 * (exactly one → resolve; 2+ → ambiguous); a name reachable only elsewhere still
 * resolves if globally unique. Mirrors engine/hoist-index `resolveScoped`.
 */
function resolveScopedLocal(
  cands: readonly OwnerCand[] | undefined,
  referrerSpace: QualifiedName | undefined
): string | "ambiguous" | undefined {
  if (!cands || cands.length === 0) return undefined;
  if (cands.length === 1) return cands[0]!.path;
  for (const s of spaceChain(referrerSpace)) {
    const here = cands.filter((c) => sameSpaceLocal(c.space, s));
    if (here.length === 1) return here[0]!.path;
    if (here.length > 1) return "ambiguous";
  }
  const distinct = new Set(cands.map((c) => c.path));
  return distinct.size === 1 ? cands[0]!.path : "ambiguous";
}

/**
 * Walk every module/surface (an interface owner-path segment) and every
 * interface in the resolved model, yielding qualified names + declaring files.
 * The single traversal that feeds the hidden-type set, the interface-boundary
 * index, and the owner-suffix index.
 */
function walkSpaceSymbols(
  model: ResolvedModel,
  onOwner: (qname: string, file: string | undefined) => void,
  onInterface: (qname: string, exported: boolean, file: string | undefined, moduleQ: string) => void
): void {
  const visitSurface = (s: ResolvedModel["modules"][number]["surfaces"][number], parent: string, ownerFile: string | undefined, moduleQ: string): void => {
    const q = `${parent}.${s.name}`;
    const file = s.decl.source?.file ?? ownerFile;
    onOwner(q, file);
    for (const i of s.interfaces) onInterface(`${q}.${i.name}`, i.decl.exported === true, i.decl.source?.file ?? file, moduleQ);
    for (const sub of s.surfaces) visitSurface(sub, q, file, moduleQ);
  };
  const visit = (mods: ResolvedModel["modules"], parent: string): void => {
    for (const m of mods) {
      const q = parent ? `${parent}.${m.name}` : m.name;
      const file = m.decl.source?.file;
      onOwner(q, file);
      for (const i of m.interfaces) onInterface(`${q}.${i.name}`, i.decl.exported === true, i.decl.source?.file ?? file, q);
      for (const s of m.surfaces) visitSurface(s, q, file, q);
      visit(m.modules, q);
    }
  };
  visit(model.modules, "");
}

/** Every path-suffix of a dotted qualified name, longest-first excluded (built
 *  short→long so the shortest unique one is found first). */
function pathSuffixes(qname: string): string[] {
  const segs = qname.split(".");
  const out: string[] = [];
  for (let k = 1; k <= segs.length; k++) out.push(segs.slice(segs.length - k).join("."));
  return out;
}

function buildSpaceCtx(workspace: Workspace, symbols: SymbolTable, uri: string): SpaceCtx {
  void symbols;
  const model = workspace.model();
  const spaces = model.spaces;
  const currentSpace = spaces?.get(uri);
  const multiSpace = !!spaces && spaces.size > 0;

  // A type is hidden from the current space only when the bare name resolves to
  // NOTHING visible here. `registry.kinds` is a collapsed bare-name map (one
  // winner per name), so it can't decide this alone — a same-named type in
  // another space may win the slot while the author's OWN (or an ancestor/root)
  // declaration is what actually resolves. Decide via `registry.byId` (every
  // declaration, space-qualified) over the referrer's space chain, plus an
  // "exported anywhere" escape (an exported sibling is reachable via `use`).
  const byId = model.registry.byId;
  const exportedTypeNames = new Set<string>();
  for (const [key, rt] of byId) {
    if (rt.decl?.exported) exportedTypeNames.add(key.slice(key.indexOf("::") + 2));
  }
  const typeResolvesFromHere = (name: string): boolean => {
    for (const s of spaceChain(currentSpace)) {
      const key = (s && s.length ? s.join(".") : "") + "::" + name;
      if (byId.has(key)) return true;
    }
    return false;
  };
  const hiddenTypes = new Set<string>();
  if (multiSpace) {
    for (const [name, kind] of model.registry.kinds) {
      const f = kind.decl?.source?.file;
      if (!f) continue; // base kind / imported (root) — never space-internal
      const sp = spaces!.get(f);
      if (sp === undefined) continue; // package-root type — globally visible
      if (typeResolvesFromHere(name)) continue; // own / ancestor / root decl — visible
      if (exportedTypeNames.has(name)) continue; // exported elsewhere — usable via `use`
      hiddenTypes.add(name);
    }
  }

  // Owner-suffix index (modules + surfaces) + interface boundary map, one walk.
  const ownerCands = new Map<string, OwnerCand[]>();
  const ifaceSpace = new Map<string, QualifiedName | undefined>();
  const ifaceExported = new Map<string, boolean>();
  // §4.11 elision inputs: each interface's owning MODULE qname (the elision
  // boundary), computed in the same walk.
  const ifaceModule = new Map<string, string>();
  walkSpaceSymbols(
    model,
    (qname, file) => {
      const cand: OwnerCand = { path: qname, space: file ? spaces?.get(file) : undefined };
      for (const suf of pathSuffixes(qname)) {
        const list = ownerCands.get(suf);
        if (list) list.push(cand);
        else ownerCands.set(suf, [cand]);
      }
    },
    (qname, exported, file, moduleQ) => {
      ifaceSpace.set(qname, file ? spaces?.get(file) : undefined);
      ifaceExported.set(qname, exported);
      ifaceModule.set(qname, moduleQ);
    }
  );

  const minimalOwnerSuffix = (ownerPath: string): string => {
    for (const suf of pathSuffixes(ownerPath)) {
      if (resolveScopedLocal(ownerCands.get(suf), currentSpace) === ownerPath) return suf;
    }
    return ownerPath;
  };

  // Lazily-built engine candidate universes — shared by surface-elision and
  // leaf-aware hoisting (both need the same uniqueness checks).
  let _ifaceCands: ReturnType<typeof buildIfaceCandidates> | undefined;
  let _moduleCands: ReturnType<typeof buildModuleCandidates> | undefined;
  const ensureIfaceCands = () => (_ifaceCands ??= buildIfaceCandidates(model));
  const ensureModuleCands = () => (_moduleCands ??= buildModuleCandidates(model.modules));

  /** True iff `spelling` names exactly `targetQname` in the engine's universe. */
  const isUniqueTo = (spelling: string, targetQname: string): boolean => {
    const cands = ensureIfaceCands().get(spelling);
    if (!cands) return false;
    const distinct = [...new Set(cands.map((c) => c.value))];
    return distinct.length === 1 && distinct[0] === targetQname;
  };

  // §4.11 surface elision: interface full path → the short `Module.leaf` form,
  // present only when the ENGINE's own spelling universe reads the short form
  // back as exactly that interface (unique leaf across the module's surface
  // tree, no same-spelling interface elsewhere) and no exact module path claims
  // it (a submodule would win via the module ladder). Lazily built — most
  // completion requests never touch interface callees.
  let elideMap: Map<string, string> | undefined;
  const ensureElideMap = (): Map<string, string> => {
    if (elideMap) return elideMap;
    elideMap = new Map();
    const moduleCands = ensureModuleCands();
    for (const [qname, moduleQ] of ifaceModule) {
      const leaf = qname.slice(qname.lastIndexOf(".") + 1);
      const short = `${moduleQ}.${leaf}`;
      if (short === qname) continue; // direct interface — nothing to elide
      if (!isUniqueTo(short, qname)) continue; // ambiguous or claimed elsewhere
      if (moduleCands.get(short)?.includes(short)) continue; // exact module path wins
      elideMap.set(qname, short);
    }
    return elideMap;
  };

  return {
    currentSpace,
    multiSpace,
    spaceOfUri(dataUri) {
      return dataUri ? spaces?.get(dataUri) : undefined;
    },
    tier(dataUri) {
      if (dataUri && dataUri === uri) return "0"; // same file
      if (!multiSpace) return "1"; // single-space: cross-file = same space
      const s = dataUri ? spaces!.get(dataUri) : undefined;
      // A symbol whose file the workspace never spaced (a dependency) is imported.
      if (dataUri && !spaces!.has(dataUri)) return "3";
      return sameSpaceLocal(s, currentSpace) ? "1" : "2";
    },
    hiddenTypes,
    interfaceBoundary(qname) {
      if (!multiSpace) return undefined;
      if (!ifaceSpace.has(qname)) return undefined;
      const sp = ifaceSpace.get(qname);
      if (sp === undefined || sameSpaceLocal(sp, currentSpace)) return undefined;
      return ifaceExported.get(qname) ? undefined : "unexported";
    },
    hoistSuffix(qname, isInterface) {
      if (!qname.includes(".")) return qname;
      if (isInterface) {
        // Leaf-aware hoisting (§4.11): try the shortest spelling that the
        // engine resolves back to exactly this interface AND that no module
        // path claims (a submodule named `Orders.Charge` would steal it via
        // the module ladder). We walk the owner suffix segments from shortest
        // to longest — stopping at the conservative owner-based form — so the
        // inserted text matches what the §4.11 reference-canonicalizer agrees
        // is minimal. Surface ELISION is handled separately (`elideSurface`,
        // applied before this hoist).
        const dot = qname.lastIndexOf(".");
        const ownerPath = qname.slice(0, dot);
        const leaf = qname.slice(dot + 1);
        const ownerSuf = minimalOwnerSuffix(ownerPath);
        const ownerSegs = ownerSuf.split(".");
        const moduleCands = ensureModuleCands();
        // Try progressively shorter owner prefixes: 1 segment, 2, … ownerSegs.length.
        // At i=0 the candidate is the full ownerSuf + leaf (conservative fallback).
        for (let i = ownerSegs.length - 1; i >= 0; i--) {
          const prefixStr = ownerSegs.slice(i).join(".");
          const candidate = prefixStr + "." + leaf;
          // Guard: reject when a DIFFERENT concrete module wins the prefix in scope.
          // A same-named local module (e.g. local `Orders`) that resolves first
          // would steal the prefix and mis-direct the reference. "ambiguous" means
          // cross-space module collision only — the interface spelling can still be
          // unique, so let isUniqueTo decide; undefined means no module at all.
          const prefixResolved = resolveScopedLocal(ownerCands.get(prefixStr), currentSpace);
          if (prefixResolved !== undefined && prefixResolved !== "ambiguous" && prefixResolved !== ownerPath) continue;
          if (!isUniqueTo(candidate, qname)) continue; // ambiguous in engine universe
          if (moduleCands.get(candidate)?.includes(candidate)) continue; // module wins
          return candidate;
        }
        return ownerSuf + "." + leaf;
      }
      return minimalOwnerSuffix(qname);
    },
    elideSurface(qname) {
      return ensureElideMap().get(qname) ?? qname;
    },
  };
}

/**
 * Post-pass over the built list applying space-boundary honesty + hoisting
 * (P2a):
 *   1. drop unexported cross-space TYPE kinds (hard error to name — §4.11);
 *   2. sink + mark unexported cross-space INTERFACE callees (soft coupling);
 *   3. shorten a cross-file module/interface insert to its minimal unique
 *      suffix, keeping the full path in `labelDetails.detail`.
 * A single-space workspace (`multiSpace` false) skips 1–2 entirely; hoisting
 * (3) still runs but only rewrites cross-FILE refs.
 */
function applySpaceAwareness(
  items: CompletionItem[],
  space: SpaceCtx,
  uri: string
): CompletionItem[] {
  // (4) pre-scan for v0.11 TWINS: the same label offered by symbol items from
  // MORE THAN ONE space. The other-space twin must not insert the bare label —
  // it would silently re-resolve to the referrer's own space's entity.
  const spacesByLabel = new Map<string, Set<string>>();
  if (space.multiSpace) {
    for (const it of items) {
      const d = it.data as { id?: string; uri?: string } | undefined;
      if (d?.id === undefined) continue;
      const key = String(it.label);
      const set = spacesByLabel.get(key) ?? new Set<string>();
      set.add((space.spaceOfUri(d.uri) ?? []).join("."));
      spacesByLabel.set(key, set);
    }
  }

  const out: CompletionItem[] = [];
  for (const it of items) {
    const label = String(it.label);
    const detail = it.detail ?? "";

    // (1) Hard-filter unexported cross-space type kinds.
    if (space.multiSpace && /^kind\b/.test(detail) && space.hiddenTypes.has(label)) continue;

    const data = it.data as { id?: string; uri?: string; qname?: string } | undefined;
    const isInterface = / on /.test(detail);
    const isOwnerSym =
      data?.id !== undefined && (isInterface || /^module\b/.test(detail) || /^surface\b/.test(detail));

    // (3a) §4.11 surface elision: an interface callee whose leaf is unique
    // across its module's surface tree inserts the short `Module.leaf` form —
    // same-file and cross-file alike (the engine resolves the elision
    // everywhere). The full structural path stays visible via labelDetails and
    // stays accepted when typed out.
    const elided =
      isInterface && data?.id !== undefined && label.includes(".") && !it.insertText
        ? space.elideSurface(label)
        : label;

    // (3) Hoist-aware insert: a cross-file module/interface ref inserts the
    // minimal unique suffix; the full path stays visible via labelDetails.
    if (isOwnerSym && elided.includes(".") && data?.uri && data.uri !== uri && !it.insertText) {
      const suffix = space.hoistSuffix(elided, isInterface);
      if (suffix !== label) {
        it.insertText = suffix;
        it.labelDetails = { ...it.labelDetails, detail: ` ${label}` };
      }
    } else if (elided !== label && !it.insertText) {
      it.insertText = elided;
      it.labelDetails = { ...it.labelDetails, detail: ` ${label}` };
    }

    // (4) v0.11 twin disambiguation: this label is offered from ≥2 spaces.
    // The OTHER-space twin inserts the space-EXTENDED written form
    // (`Alpha.Api` — the only spelling that names it from here; a bare or
    // suffix insert re-resolves to the referrer's own space) and carries its
    // space as the label chip (display stays bare — D5).
    if (space.multiSpace && (isOwnerSym || isInterface) && data?.qname !== undefined) {
      const twins = spacesByLabel.get(label);
      if (twins !== undefined && twins.size > 1) {
        const mySpace = space.spaceOfUri(data.uri);
        if (mySpace && mySpace.length > 0 && !sameSpaceLocal(mySpace, space.currentSpace)) {
          it.insertText = `${mySpace.join(".")}.${data.qname}`;
          it.labelDetails = { ...it.labelDetails, detail: ` ${mySpace.join(".")}` };
        }
      }
    }

    // (2) Down-rank + mark an unexported cross-space interface callee.
    if (isInterface && space.interfaceBoundary(label) === "unexported") {
      it.labelDetails = { ...it.labelDetails, description: "not exported — boundary" };
      it.sortText = `${RANK_BOUNDARY}${label.toLowerCase()}`;
    }

    out.push(it);
  }
  return out;
}

// =============================================================================
// Context analysis
// =============================================================================

type Scope =
  | "top"
  | "module"
  | "surface"
  | "interface"
  | "process"
  | "view"
  | "labels"
  | "type"
  | "field-body"
  // v0.11 process tests — a `test { … }` decl body and its `when`/`then` blocks.
  | "test"
  | "when"
  | "then";

type TokenContext =
  | "none"
  | "after-dot"
  | "after-arrow"
  | "after-in"
  | "after-each-in"
  | "after-extends"
  | "after-type-keyword"
  | "after-kind-keyword"
  | "after-export"
  | "after-from"
  | "after-by"
  | "after-aspect" // inline `aspect █` / `aspect d█` — reused aspect keys (P2.3)
  | "after-use"
  | "after-owner" // `<Owner> █` / `<Owner> i█` — owned control-flow keywords
  // Process-language slots (P1a):
  | "after-do" // subprocess names, then processes
  | "after-go" // enclosing process's `as` labels
  | "after-unwind-callee" // `unwind >` — forward step's callee first
  | "after-parallel" // merge keywords (join/race/out) + await/select/each
  | "after-select" // `one` count keyword
  | "after-await" // duration/cron/condition/race snippet heads
  | "after-on-plane" // root/module `on <plane-list>`
  | "after-on-trigger" // `reversible on <trigger>` — `error`
  | "after-on-caller" // `subprocess X on <Caller>` — a fresh param name
  | "after-owner-marker" // `dist <owner>` / `| <owner>` — module/actor names
  | "after-snake" // `\ █` — the snake glyph is call-only: only `>` follows
  | "after-merge-bool" // `parallel join A and █` — branch names
  // Structure slots (P1b):
  | "after-drop" // `drop █` — inherited entity names only (§4.8)
  | "after-override" // `override █` — `required` + registered kinds (the new type)
  | "after-override-type" // `override <kind> █` — inherited entity names (§4.8)
  | "after-focus" // view `focus █` — aspect keys
  | "after-layout" // view `layout █` — the layout enum
  | "after-include-pattern"; // view `include █` / `exclude █` — quoted glob patterns

interface AnalyzedContext {
  scope: Scope;
  /** The typeName declared on the surrounding block, if any. */
  scopeTypeName?: string;
  /** For `field-body` scope: dotted path of the parent field whose
   * body the cursor is inside. */
  fieldPrefix?: readonly string[];
  /** True if cursor is at the start of a fresh statement. */
  atStatementStart: boolean;
  tokenContext: TokenContext;
  /** Set when tokenContext === "after-dot". */
  path: readonly string[];
  /** The key whose value the cursor is typing (`domain: |` → `"domain"`), for
   *  value-identity completion. Resolved against aspects or fields by scope. */
  valueKey?: string;
  /** Module names already named in the enclosing process flow — their callees
   *  rank first after `>` (proximity). Empty outside a process. */
  flowModules?: ReadonlySet<string>;
  /** Aspect keys already written in the current aspect block — not re-offered. */
  existingAspectKeys?: ReadonlySet<string>;
  /** URI of the file being completed (for process-local `go` label scoping). */
  uri: string;
  /** Process-body slot flags — drives slot-aware keyword pruning + the header
   *  zone. Present only when `scope === "process"`. */
  proc?: ProcCtx;
  /** Byte span of the enclosing process/subprocess body, for scoping `go`
   *  re-entry labels to it. */
  enclosingProc?: {
    readonly startOffset: number;
    readonly endOffset: number;
    readonly exclude: readonly { readonly start: number; readonly end: number }[];
  };
  /** For `after-unwind-callee`: the forward step's callee module (compensation
   *  targets the SAME callee), the exact forward interface (a step never
   *  compensates ITSELF — hard-sunk), and the previous step's callee (the #1
   *  mistake). */
  unwindCallee?: {
    readonly forwardModule?: string;
    readonly forwardInterface?: string;
    readonly previousModule?: string;
  };
  /** For `after-owner`: the owner identifier the statement leads with (`Orders`
   *  in `Orders █`). */
  ownerName?: string;
  /** True in a same-line saga `unwind` suffix slot (`> X.reserve u█` inside a
   *  `reversible` frame) — offer `unwind`, nothing else (P2.4). */
  unwindSuffixLegal?: boolean;
  /** For `after-merge-bool`: branch names declared in the enclosing parallel
   *  block (a boolean merge refers to them by name). */
  mergeBranchNames?: readonly string[];
  /** True inside a top-level `on <plane> { … }` presence/inclusion block —
   *  module-body suggestions also offer existing module names. */
  onBlock?: boolean;
  /** True when the value slot is an aspect membership/classification value
   *  (`aspect key: █`, or a `key: █` in an `aspect { }` block) — offers modules
   *  (membership) + previously-used values, never a plain field-value dump. */
  aspectValue?: boolean;
  /** True when the value slot is an aspect ROSTER value (`aspect key for: █`) —
   *  module refs + `as` only, no classification strings. */
  rosterValue?: boolean;
  /** True when a complete bare module ref already precedes the cursor in an
   *  aspect/roster value — offer `as <instance>` to name the member. */
  valueRefBefore?: boolean;
  /** Qualified name of the enclosing module (`Orders`, `Orders.Inner`), used to
   *  match ghost interfaces for stub promotion. Present only in module scope. */
  scopeQualifiedName?: string;
}

/** Process-body slot flags (§process grammar): which slot-locked keywords are
 *  legal at THIS statement start, and whether the cursor is in the header zone
 *  (before the first action, where field keys are legal). */
interface ProcCtx {
  readonly unwindLegal: boolean; // inside a `reversible` body top-level
  readonly branchLegal: boolean; // inside a `parallel` body
  readonly elseLegal: boolean; // previous sibling was an `if` / `else` construct
  readonly catchLegal: boolean; // previous sibling was a `try` block/one-liner
  readonly headerZone: boolean; // before the first action (field keys legal)
}

/** Which control opened a process sub-block — drives keyword pruning + `else`
 *  legality. `undefined` on a frame marks the process/subprocess DECL body
 *  itself (used for header-zone detection). */
type ProcBlockKind =
  | "reversible"
  | "parallel"
  | "select"
  | "if"
  | "else"
  | "each"
  | "try"
  | "catch"
  | "branch"
  | "block";

interface BraceFrame {
  scope: Scope;
  typeName?: string;
  /** Token index of the `{` that opened this frame. */
  startIdx: number;
  /** For `field-body` scope: dotted path of the parent field whose
   * body the cursor is inside (e.g. `["widget"]`). Sub-field
   * completions prepend this prefix. */
  fieldPrefix?: readonly string[];
  /** For a process sub-block: the control that opened it (`undefined` on the
   *  process/subprocess decl body). */
  procBlock?: ProcBlockKind;
  /** True for a top-level `on <plane> { … }` presence/inclusion block — module
   *  scope, but also admits bare module inclusion refs. */
  onBlock?: boolean;
  /** The declared instance name on this frame's head (`Orders` for
   *  `microservice Orders {`), for building the enclosing qualified name. */
  declName?: string;
}

/**
 * Walk tokens up to (but not including) any token whose start lies at or beyond
 * `offset`, maintaining the brace-frame scope stack. Shared by `analyzeContext`
 * and the in-string slot detector (which needs the scope at the opening quote).
 */
function buildBraceStack(
  tokens: readonly Token[],
  workspace: Workspace,
  offset: number
): { stack: BraceFrame[]; lastIdx: number; aheadIdx: number } {
  const stack: BraceFrame[] = [];
  let lastIdx = -1;
  let aheadIdx = -1;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.type === "EOF") break;
    if (t.span.start.offset >= offset) {
      aheadIdx = i;
      break;
    }
    lastIdx = i;

    if (t.type === "LBRACE") {
      const parentScope = stack[stack.length - 1]?.scope;
      const frame = inferFrame(tokens, i, workspace, parentScope);
      stack.push({ ...frame, startIdx: i });
    } else if (t.type === "RBRACE") {
      stack.pop();
    }
  }
  return { stack, lastIdx, aheadIdx };
}

function analyzeContext(
  file: FileState,
  workspace: Workspace,
  line: number,
  character: number
): AnalyzedContext {
  const offset = offsetAt(file.text, line, character);
  const tokens = file.tokens;

  const { stack, lastIdx, aheadIdx } = buildBraceStack(tokens, workspace, offset);

  const lastTok = lastIdx >= 0 ? tokens[lastIdx] : undefined;
  const innerFrame = stack[stack.length - 1];
  const scope: Scope = innerFrame?.scope ?? "top";
  const scopeTypeName = innerFrame?.typeName;
  const fieldPrefix = innerFrame?.fieldPrefix;

  // A word-like token glued to the cursor (`do█`, `Pay█`, mid-word `Pa█y`) is the
  // half-typed run: it must be classified by the token BEFORE it (a partial slot
  // value), never treated as a completed positional keyword. Only a keyword with
  // a gap before the cursor (`do █`) is a real "after-<keyword>" position.
  const runGlued =
    !!lastTok && isWordLike(lastTok.type) && lastTok.span.end.offset >= offset;

  // Detect cursor token-relative position.
  let tokenContext = classifyTokenContext(tokens, lastIdx, runGlued);
  const path =
    tokenContext === "after-dot"
      ? collectDottedPath(tokens, lastTok?.type === "DOT" ? lastIdx : lastIdx - 1)
      : [];

  // Statement-start = a boundary (newline / `{` / `}` / `;` / process `:`
  // one-liner / nothing) before the run under the cursor.
  const atStatementStart = computeStatementStart(file.text, tokens, lastIdx, scope, offset);

  // A leading `>` (anonymous bare-step head, §4.13) sitting at/after the cursor
  // on a fresh statement: the author is writing a process step, so route to the
  // after-arrow (process-callee) completion rather than field / body keywords.
  if (
    tokenContext === "none" &&
    atStatementStart &&
    aheadIdx >= 0 &&
    tokens[aheadIdx]?.type === "GT"
  ) {
    tokenContext = "after-arrow";
  }

  // Value position (`key: …`) gets value-identity completion (label values or
  // field values, depending on scope) rather than the generic name fallback.
  const inValueSlot = !atStatementStart && tokenContext === "none";
  const valueKey = inValueSlot ? keyBeforeColon(tokens, lastIdx) : undefined;

  // Aspect membership / classification vs. roster vs. plain field (§4.4). In a
  // `labels` (aspect `{ }`) block every value is an aspect value; in a module
  // body the value is an aspect value only when the `key:` is headed by the
  // `aspect` keyword, and a roster when `aspect key for:` heads it.
  let aspectValue = false;
  let rosterValue = false;
  let valueRefBefore = false;
  if (inValueSlot && valueKey !== undefined) {
    const colonIdx = governingColon(tokens, lastIdx);
    if (scope === "labels") aspectValue = true;
    else if (colonIdx >= 0) {
      const k = classifyAspectValueSlot(tokens, colonIdx);
      aspectValue = k === "membership";
      rosterValue = k === "roster";
    }
    if ((aspectValue || rosterValue) && colonIdx >= 0) {
      // A complete bare ref already sits in the value (before the run) → offer
      // `as` to name the member's instance.
      const runStart = runStartOffset(tokens, lastIdx, offset);
      for (let j = colonIdx + 1; j < tokens.length; j++) {
        const t = tokens[j]!;
        if (t.span.start.offset >= runStart) break;
        if (t.type === "IDENTIFIER") valueRefBefore = true;
      }
    }
  }

  // Qualified name of the enclosing module (for ghost-interface stub promotion).
  let scopeQualifiedName: string | undefined;
  if (scope === "module") {
    const parts: string[] = [];
    let ok = true;
    for (const f of stack) {
      if (f.scope !== "module" && f.scope !== "surface") continue;
      if (f.declName) parts.push(f.declName);
      else { ok = false; break; }
    }
    if (ok && parts.length > 0) scopeQualifiedName = parts.join(".");
  }

  // Proximity: the modules already named anywhere in the enclosing process flow
  // (the outermost `process` frame on the stack, so nested if/each are covered).
  let flowModules: Set<string> | undefined;
  const processFrame = stack.find((f) => f.scope === "process");
  // The INNERMOST process/subprocess frame — a subprocess nests inside a process
  // body, so `go` re-entry and the header zone belong to the tightest enclosing
  // one, not the outermost.
  const innerProcessFrame = [...stack].reverse().find((f) => f.scope === "process");
  if (processFrame) {
    flowModules = new Set<string>();
    for (let j = processFrame.startIdx + 1; j <= lastIdx; j++) {
      const t = tokens[j];
      if (t?.type === "IDENTIFIER") flowModules.add(t.value);
    }
    // Self-call proximity (P2.6): a step almost always targets a DIFFERENT
    // module, so at an explicit `<Caller> > █` drop the current step's caller
    // from the proximity set — its own interfaces stay offered, just unboosted.
    if (tokenContext === "after-arrow") {
      const callerTop = currentStepCallerTop(tokens, lastIdx, offset, runGlued);
      if (callerTop) flowModules.delete(callerTop);
    }
  }

  // The aspect keys already written in the current aspect block (an IDENTIFIER
  // run immediately followed by `:`), so they aren't re-offered.
  let existingAspectKeys: Set<string> | undefined;
  if (scope === "labels" && innerFrame) {
    existingAspectKeys = new Set<string>();
    for (let j = innerFrame.startIdx + 1; j <= lastIdx; j++) {
      if (tokens[j]?.type === "COLON") {
        const key = keyBeforeColon(tokens, j);
        if (key) existingAspectKeys.add(key);
      }
    }
  }

  // Byte span of the enclosing process/subprocess body — scopes `go` re-entry
  // labels to the process the cursor is in. `exclude` carves out nested
  // subprocess bodies (their `as` labels are a separate re-entry namespace), so
  // labels never leak parent↔child or between sibling subprocesses.
  const enclosingProc = innerProcessFrame
    ? {
        startOffset: tokens[innerProcessFrame.startIdx]!.span.start.offset,
        endOffset: matchingRBraceOffset(tokens, innerProcessFrame.startIdx, file.text.length),
        exclude: nestedSubprocessSpans(tokens, innerProcessFrame.startIdx, file.text.length),
      }
    : undefined;

  // Process-body slot flags — slot-aware keyword pruning + the header zone.
  let proc: ProcCtx | undefined;
  if (scope === "process") {
    const refIdx = refIndexBeforeRun(tokens, lastIdx, offset);
    proc = {
      unwindLegal: innerFrame?.procBlock === "reversible",
      branchLegal: innerFrame?.procBlock === "parallel",
      elseLegal: precedingSiblingControl(tokens, refIdx, ELSE_BLOCKS, ELSE_ONELINERS),
      catchLegal: precedingSiblingControl(tokens, refIdx, CATCH_BLOCKS, CATCH_ONELINERS),
      headerZone:
        !!innerProcessFrame &&
        innerFrame === innerProcessFrame &&
        !hasActionBefore(tokens, innerProcessFrame.startIdx, lastIdx),
    };
  }

  // Owner slot (P1.2): `Orders █` / `Orders i█` — the statement so far is a lone
  // owner identifier (a module/actor or a ghost caller in the flow) with a gap or
  // a partial word after it. Offer the owned control-flow keyword set + a `> `
  // step-arrow rather than dead silence. Only when nothing else has claimed the
  // slot (not after a `>`/`.`/keyword, not a value slot).
  let ownerName: string | undefined;
  if (
    scope === "process" &&
    tokenContext === "none" &&
    valueKey === undefined &&
    !aspectValue &&
    !rosterValue
  ) {
    const ownerIdx = runGlued && lastTok && isWordLike(lastTok.type) ? lastIdx - 1 : lastIdx;
    const ownerTok = ownerIdx >= 0 ? tokens[ownerIdx] : undefined;
    const runTok = runGlued ? lastTok : undefined;
    const gapAfterOwner =
      ownerTok !== undefined &&
      (runTok
        ? runTok.span.start.offset > ownerTok.span.end.offset &&
          runTok.span.start.line === ownerTok.span.end.line
        : offset > ownerTok.span.end.offset);
    if (
      ownerTok?.type === "IDENTIFIER" &&
      gapAfterOwner &&
      tokenBeginsStatement(tokens, ownerIdx, file.text, scope)
    ) {
      tokenContext = "after-owner";
      ownerName = ownerTok.value;
    }
  }

  // Same-line saga `unwind` suffix (P2.4): `> Inventory.reserve u█` — after a
  // completed call step on this line inside a `reversible` frame, `unwind` opens
  // the compensation clause. Not at a fresh statement start (that path already
  // offers `unwind` via `unwindLegal`).
  let unwindSuffixLegal = false;
  if (scope === "process" && proc?.unwindLegal && tokenContext === "none" && !atStatementStart) {
    const refIdx = refIndexBeforeRun(tokens, lastIdx, offset);
    if (tokens[refIdx]?.type === "IDENTIFIER" && arrowOnStatementBefore(tokens, refIdx)) {
      unwindSuffixLegal = true;
    }
  }

  // Saga `unwind` callee: the forward step's callee (compensation targets the
  // same callee) + the previous step's callee (down-ranked — the #1 mistake).
  let unwindCallee:
    | { forwardModule?: string; forwardInterface?: string; previousModule?: string }
    | undefined;
  if (tokenContext === "after-unwind-callee") {
    const arrowIdx =
      lastTok?.type === "GT" ? lastIdx : lastIdx - 1; // the `>` of the unwind clause
    unwindCallee = parseUnwindCallee(tokens, arrowIdx);
  }

  // Branch names declared in the enclosing parallel block — a boolean merge
  // refers to them by name (the body may sit AFTER the cursor).
  let mergeBranchNames: string[] | undefined;
  if (tokenContext === "after-merge-bool") {
    mergeBranchNames = scanParallelBranchNames(tokens, lastIdx);
  }

  return {
    scope,
    scopeTypeName,
    fieldPrefix,
    atStatementStart,
    tokenContext,
    path,
    valueKey,
    flowModules,
    existingAspectKeys,
    uri: file.uri,
    proc,
    enclosingProc,
    unwindCallee,
    mergeBranchNames,
    onBlock: innerFrame?.onBlock,
    aspectValue,
    rosterValue,
    valueRefBefore,
    scopeQualifiedName,
    ownerName,
    unwindSuffixLegal,
  };
}

/**
 * Whether the token at `idx` begins a fresh statement in its body — a boundary
 * (`{`/`}`/`;`, a process `:` one-liner head, a newline, or nothing) sits
 * immediately before it. Mirrors `computeStatementStart`'s reference logic but
 * asks about an ARBITRARY token (the leading owner), not the cursor's run.
 */
function tokenBeginsStatement(
  tokens: readonly Token[],
  idx: number,
  text: string,
  scope: Scope
): boolean {
  if (idx <= 0) return true;
  const prev = tokens[idx - 1]!;
  const cur = tokens[idx]!;
  if (prev.type === "LBRACE" || prev.type === "RBRACE" || prev.type === "SEMICOLON") return true;
  if (prev.type === "COLON") {
    return (
      scope === "process" &&
      (keyBeforeColon(tokens, idx - 1) === undefined ||
        isControlOneLinerColon(tokens, idx - 1))
    );
  }
  return text.slice(prev.span.end.offset, cur.span.start.offset).includes("\n");
}

/** Whether a forward call arrow (`>`) sits earlier on the SAME statement as the
 *  reference token at `refIdx` — i.e. a completed call step precedes the cursor
 *  (the `<call> unwind` suffix shape). Stops at a block/`;` boundary or newline. */
function arrowOnStatementBefore(tokens: readonly Token[], refIdx: number): boolean {
  for (let i = refIdx; i >= 0; i--) {
    const t = tokens[i]!;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") return false;
    if (i < refIdx) {
      const next = tokens[i + 1]!;
      if (t.span.end.line < next.span.start.line) return false; // crossed a newline
    }
    if (t.type === "GT") return true;
  }
  return false;
}

/** The top-segment module of the caller immediately left of the arrow the cursor
 *  follows (`Orders > █` → `Orders`; `Orders.Inner > █` → `Orders`). Undefined
 *  for a leading bare `> █` (no explicit caller on this line). */
function currentStepCallerTop(
  tokens: readonly Token[],
  lastIdx: number,
  offset: number,
  runGlued: boolean
): string | undefined {
  void offset;
  const last = lastIdx >= 0 ? tokens[lastIdx] : undefined;
  let gtIdx = -1;
  if (last?.type === "GT") gtIdx = lastIdx;
  else if (runGlued && last && isWordLike(last.type) && tokens[lastIdx - 1]?.type === "GT")
    gtIdx = lastIdx - 1;
  if (gtIdx <= 0) return undefined;
  const gt = tokens[gtIdx]!;
  const before = tokens[gtIdx - 1];
  // No caller on this line (a leading `> █`): the token before `>` sits on an
  // earlier line — it belongs to the previous statement, not this step's caller.
  if (!before || before.span.end.line < gt.span.start.line) return undefined;
  let i = gtIdx - 1;
  let top: string | undefined;
  while (i >= 0 && tokens[i]?.type === "IDENTIFIER") {
    top = tokens[i]!.value;
    if (tokens[i - 1]?.type === "DOT") i -= 2;
    else break;
  }
  return top;
}

/**
 * Given an LBRACE at `i`, walk backwards through preceding tokens to
 * determine what kind of body was just opened.
 */
function inferFrame(
  tokens: readonly Token[],
  lbraceIdx: number,
  workspace: Workspace,
  parentScope?: Scope
): Omit<BraceFrame, "startIdx"> {
  // Collect the run of tokens immediately preceding LBRACE up to the
  // previous statement boundary (newline before a non-trivial token, or
  // a brace).
  const head = collectDeclHead(tokens, lbraceIdx);

  // Any block opened while already inside a process is a step sub-block —
  // `if { }`, `each { }`, a `select` case body, `try`/`catch`, a `parallel`
  // branch, a `reversible` saga. No declarations nest in a process, so it stays
  // process scope (the head-based inference below would otherwise misread
  // `if Cond {` as a module). Record WHICH control opened it so keyword pruning
  // (unwind/branch/else) and header-zone detection can read it back.
  if (parentScope === "process") return { scope: "process", procBlock: classifyProcBlock(head) };

  // Inside a `test { … }` body (v0.11): its `when`/`then` blocks are their own
  // scopes; any other nested block stays test scope. `when`/`then` are contextual
  // identifiers, so match on the head's first token value.
  if (parentScope === "test") {
    const w = head[0];
    if (w?.type === "IDENTIFIER" && w.value === "when") return { scope: "when" };
    if (w?.type === "IDENTIFIER" && w.value === "then") return { scope: "then" };
    return { scope: "test" };
  }
  // A block nested in a `when`/`then` (e.g. `then parallel { … }`) keeps that scope.
  if (parentScope === "when") return { scope: "when" };
  if (parentScope === "then") return { scope: "then" };

  if (head.length === 0) return { scope: "module" };

  // Top-level (or module-body) `on <plane> { … }` presence/inclusion block: a
  // module-scope body that ALSO admits bare module inclusion refs. Marked so
  // `moduleBodySuggestions` adds existing module names (GRAMMAR.md `on_decl`).
  if (head[0]!.type === "ON") return { scope: "module", onBlock: true };

  // Field body: `[modifiers] field[.path][: value] { ... }`. Detect by
  // walking head, skipping leading field-modifier keywords (`required`,
  // `cascade`, optional `*`, `append`), then looking for IDENTIFIER —
  // optionally dotted — followed (optionally) by a COLON.
  const fieldPrefix = extractFieldBodyPrefix(head);
  if (fieldPrefix) return { scope: "field-body", fieldPrefix };

  const first = head[0]!;
  if (first.type === "LABELS" || first.type === "ASPECT") return { scope: "labels" };
  // A `process`/`subprocess` decl body is process scope; its `procBlock` stays
  // undefined, marking it the decl body (not a control sub-block) so the header
  // zone (fields before the first action) is detectable.
  if (first.type === "PROCESS" || first.type === "SUBPROCESS") return { scope: "process" };
  if (first.type === "VIEW") return { scope: "view" };
  if (first.type === "TYPE") {
    // `type <kind> <name> [extends <parent>]` — the typeName for the
    // new type is the *first identifier* after `type`.
    const typeTok = head[1];
    return {
      scope: "type",
      typeName: typeTok?.type === "IDENTIFIER" ? typeTok.value : undefined,
    };
  }

  // A `test [#id] "name" [of P] { … }` decl body (v0.11). `test` is contextual
  // (an IDENTIFIER), but the STRING name distinguishes the head from a kind decl
  // (`kind Name`), which never carries a string. A real registered kind named
  // `test` (unlikely) wins, keeping the module reading.
  if (
    first.type === "IDENTIFIER" &&
    first.value === "test" &&
    head.some((t) => t.type === "STRING") &&
    !workspace.model().registry.kinds.has("test")
  ) {
    return { scope: "test" };
  }

  // Otherwise the first token is the kind name on a declaration.
  if (first.type !== "IDENTIFIER") return { scope: "module" };
  const typeName = first.value;
  // The declared instance name — the LAST identifier in the head (after the
  // kind + an optional stable id): `microservice Orders {` → `Orders`.
  const declName = lastIdentifierValue(head);
  const kind = workspace.model().registry.kinds.get(typeName);
  const baseType = kind?.baseType;
  if (baseType === "surface") return { scope: "surface", typeName, declName };
  if (baseType === "interface") return { scope: "interface", typeName };
  // Process / view as base kinds shouldn't appear on inline declarations,
  // but treat them defensively.
  if (baseType === "process") return { scope: "process", typeName };
  if (baseType === "view") return { scope: "view", typeName };
  return { scope: "module", typeName, declName };
}

/** The value of the last IDENTIFIER token in a declaration head (the instance
 *  name, after the kind + an optional stable id), or undefined. */
function lastIdentifierValue(head: readonly Token[]): string | undefined {
  for (let i = head.length - 1; i >= 1; i--) {
    if (head[i]!.type === "IDENTIFIER") return head[i]!.value;
  }
  return undefined;
}

/**
 * Which control keyword opened a process sub-block. Scans the block's head for
 * the first control keyword; a plain owner block (`Owner { }`) or a bare `{ }`
 * is `"block"`. Drives keyword pruning (`unwind`/`branch`) + `else` legality.
 */
function classifyProcBlock(head: readonly Token[]): ProcBlockKind {
  for (const t of head) {
    switch (t.type) {
      case "REVERSIBLE": return "reversible";
      case "PARALLEL": return "parallel";
      case "SELECT": return "select";
      case "IF": return "if";
      case "ELSE": return "else";
      case "EACH": return "each";
      case "TRY": return "try";
      case "CATCH": return "catch";
      case "BRANCH": return "branch";
    }
  }
  return "block";
}

/**
 * Try to interpret the declaration head as a field whose body the
 * cursor is now inside. Returns the dotted path prefix when matched,
 * otherwise null (so caller falls through to other frame inference).
 *
 * Grammar (head consumed up to but not including the LBRACE):
 *     ('required' | 'cascade' '*'? | 'append')* IDENTIFIER ('.' IDENTIFIER)* (':' value-tokens)?
 *
 * Heuristic-only: we don't fully validate the value tokens, just look
 * for IDENTIFIER (possibly dotted) following the modifier prefix.
 */
function extractFieldBodyPrefix(head: readonly Token[]): readonly string[] | null {
  let i = 0;
  // Skip leading modifier keywords + optional `*` after `cascade`.
  while (i < head.length) {
    const t = head[i]!;
    if (t.type === "REQUIRED" || t.type === "APPEND") { i++; continue; }
    if (t.type === "CASCADE") {
      i++;
      if (head[i]?.type === "STAR") i++;
      continue;
    }
    break;
  }
  if (head[i]?.type !== "IDENTIFIER") return null;
  const path: string[] = [(head[i] as Token & { value: string }).value];
  i++;
  while (head[i]?.type === "DOT" && head[i + 1]?.type === "IDENTIFIER") {
    path.push((head[i + 1] as Token & { value: string }).value);
    i += 2;
  }
  // The path is a field-body candidate when: nothing else follows
  // (e.g. `widget {`), OR the next token is COLON followed by some
  // value tokens (e.g. `widget: arch-module {`).
  if (i === head.length) return path;
  if (head[i]?.type === "COLON") return path;
  return null;
}

/**
 * From the LBRACE position, walk backward and gather every token that's
 * part of the same declaration head, up to the previous statement
 * boundary.
 */
function collectDeclHead(
  tokens: readonly Token[],
  lbraceIdx: number
): Token[] {
  const out: Token[] = [];
  for (let j = lbraceIdx - 1; j >= 0; j--) {
    const t = tokens[j]!;
    if (
      t.type === "LBRACE" ||
      t.type === "RBRACE" ||
      t.type === "EOF"
    ) {
      break;
    }
    // Stop at a statement boundary: a newline between this token and the next
    // one already collected. Without this, a run of bodiless preceding decls
    // (`type module microservice` with no braces) bleeds into the head and the
    // leading `type` mis-infers the scope.
    const next = tokens[j + 1];
    if (next && t.span.end.line < next.span.start.line) break;
    out.unshift(t);
  }
  return out;
}

function classifyTokenContext(
  tokens: readonly Token[],
  lastIdx: number,
  runGlued: boolean
): TokenContext {
  if (lastIdx < 0) return "none";
  const last = tokens[lastIdx]!;

  if (last.type === "DOT") return "after-dot";
  // A saga `unwind >` callee is compensation-first (the same callee the forward
  // step called, PROCESS.md §8); a plain `>` is a generic callee.
  if (last.type === "GT") {
    return isUnwindArrow(tokens, lastIdx) ? "after-unwind-callee" : "after-arrow";
  }
  // The snake glyph (`\`, §4.13.4) is CALL-ONLY: the only token that may follow
  // it is the call arrow. Unlike `|`/`dist` it never takes an owner name, so it
  // gets its own slot rather than the owner-marker list. Not gated on
  // `runGlued` — a glyph is never word-like, so `\█` and `\ █` are the same slot.
  if (last.type === "BACKSLASH") return "after-snake";
  // A loop `in` (`each item in █`) heads a FREE-FORM collection (GRAMMAR
  // each_head — never resolved/validated), so ghosts stay legal there; a
  // placement `in` (`in Target`) is a hard IN_TARGET_NOT_FOUND, so it excludes
  // them. Route the two differently.
  //
  // Only when the `in` is a COMPLETED token (a gap before the cursor). A glued
  // `in█` run is a half-typed keyword (P2.8): treat it like `on█` — fall through
  // so it classifies as a partial keyword and the top-level list offers the `in`
  // placement snippet, rather than an already-complete placement whose module
  // list is empty under the client word-filter and whose accept eats the `in`.
  if (last.type === "IN" && !runGlued) {
    return isEachLoopIn(tokens, lastIdx) ? "after-each-in" : "after-in";
  }

  // Positional keyword slots — a completed slot keyword with a gap before the
  // cursor (`group by █`, `do █`). When the keyword is instead glued to the
  // cursor it is the half-typed run (`group by do█`), so fall through to the
  // back-check, which classifies by the token BEFORE the run.
  if (!runGlued) {
    // `export …` — about to type `type` / `use` / `subprocess` or an interface
    // kind to publish across the boundary (v0.5).
    if (last.type === "EXPORT") return "after-export";
    // `use … from …` — about to name the source package (v0.5).
    if (last.type === "FROM") return "after-from";
    // `group by …` — the grouping key is an aspect key.
    if (last.type === "BY") return "after-by";
    // Inline `aspect █` (no block brace yet) — the reused aspect keys, same as the
    // `aspect { }` block form (P2.3). A completed `aspect {` is an LBRACE (labels
    // scope), so an ASPECT token here is always the inline lead.
    if (last.type === "ASPECT") return "after-aspect";
    // `use …` — about to name an exported member to import (before `from`).
    if (last.type === "USE") return "after-use";

    // Process-language structural slots (P1a). `do`/`go`/`select`/`parallel`/
    // `await` are process-only keywords; `on` is scope-disambiguated (plane list
    // vs. reversible trigger vs. subprocess caller).
    if (last.type === "DO") return "after-do";
    if (last.type === "GO") return "after-go";
    if (last.type === "SELECT") return "after-select";
    if (last.type === "PARALLEL") return "after-parallel";
    if (last.type === "AWAIT") return "after-await";
    if (last.type === "ON") return classifyOn(tokens, lastIdx);

    // View slots (P1b). `focus <key>` (aspect key), `layout <enum>`,
    // `include`/`exclude <pattern>` (the pattern is a quoted glob).
    if (last.type === "FOCUS") return "after-focus";
    if (last.type === "LAYOUT") return "after-layout";
    if (last.type === "INCLUDE" || last.type === "EXCLUDE") return "after-include-pattern";
  }
  // A parallel boolean merge (`join (█`, `join A and █`): the operands are
  // branch names. Fires after the opening `(` or a completed connective.
  if (
    (last.type === "LPAREN" || isBoolConnective(last)) &&
    inParallelMergeClause(tokens, lastIdx)
  ) {
    return "after-merge-bool";
  }

  // After `<extends-keyword> ` — but we don't have an EXTENDS token; the
  // grammar uses `type <kind> <name> in <parent>` for type extension via
  // parent-position OR inline kinds. Skip extends handling for now.

  // User typing a partial word GLUED to the cursor: classify by the token before
  // the run so a half-typed callee/parent/slot value still gets the right list.
  // The run may be an IDENTIFIER or a keyword the lexer matched (`group by do|` →
  // DO), so both count — otherwise the C1 dead-end (`Pay|` empty) would strand
  // every same-line slot. Gated on `runGlued`: a word-like token with a gap
  // before the cursor is a COMPLETED token starting a fresh position, not a
  // partial run — `A > B.C\n█` is a new statement, not `after-dot` on `C`.
  if (runGlued && isWordLike(last.type)) {
    const prevTok = tokens[lastIdx - 1];
    const prev = prevTok?.type;
    // DOT / GT / IN keep their pre-existing (cross-line-tolerant) semantics.
    if (prev === "DOT") return "after-dot"; // `Orders.Cre|`
    if (prev === "GT") {
      return isUnwindArrow(tokens, lastIdx - 1) ? "after-unwind-callee" : "after-arrow"; // `Orders > Inv|`
    }
    if (prev === "IN") {
      return isEachLoopIn(tokens, lastIdx - 1) ? "after-each-in" : "after-in"; // `Orders in Pl|`
    }
    // USE / FROM / BY / EXPORT: only when the keyword sits on the SAME line as
    // the partial word. A dangling `use` / `export` on the previous line is an
    // incomplete statement; a word on a NEW line starts a fresh statement, so
    // the back-check must not reach across the newline and hijack it into the
    // import-member / after-export list.
    const sameLine =
      prevTok !== undefined && last.span.start.line === prevTok.span.end.line;
    if (sameLine && prev === "USE") return "after-use"; // `use ser|`
    if (sameLine && prev === "FROM") return "after-from"; // `from ar|`
    if (sameLine && prev === "BY") return "after-by"; // `group by do|`
    if (sameLine && prev === "ASPECT") return "after-aspect"; // `aspect d|` (P2.3)
    if (sameLine && prev === "EXPORT") return "after-export"; // `export ty|`
    // Process-language slots — same-line partials after the slot keyword.
    if (sameLine && prev === "DO") return "after-do"; // `Order do char|`
    if (sameLine && prev === "GO") return "after-go"; // `go che|`
    if (sameLine && prev === "SELECT") return "after-select"; // `select on|`
    if (sameLine && prev === "PARALLEL") return "after-parallel"; // `parallel jo|`
    if (sameLine && prev === "AWAIT") return "after-await"; // `await 1|`
    if (sameLine && prev === "ON") return classifyOn(tokens, lastIdx - 1); // `on dep|`
    if (sameLine && prev === "FOCUS") return "after-focus"; // `focus dom|`
    if (sameLine && prev === "LAYOUT") return "after-layout"; // `layout el|`
    // Owner-marker positions: `dist Pay|` / `| Pay|` — the partial is an owner.
    // `\` is NOT one of them: the snake glyph takes no owner (its caller is the
    // previous sibling's tail), so a word glued after it is never a name to
    // complete — route to the call-only slot, whose lone `>` the client filter
    // drops, rather than dumping module names into an illegal position.
    if (sameLine && (prev === "DIST" || prev === "PIPE")) return "after-owner-marker";
    if (sameLine && prev === "BACKSLASH") return "after-snake";
    // A parallel boolean merge operand (`join A and cr|`, `join (cr|`).
    if (sameLine && (prev === "LPAREN" || isBoolConnective(prevTok))) {
      if (inParallelMergeClause(tokens, lastIdx - 1)) return "after-merge-bool";
    }
  }

  // drop / override target slots (§4.8). The override clause spans
  // `override [required] <kind> <name>`; classify by the reference token before
  // the run (the run itself is the half-typed target being filtered).
  {
    const clauseRef = runGlued && isWordLike(last.type) ? lastIdx - 1 : lastIdx;
    const rt = clauseRef >= 0 ? tokens[clauseRef] : undefined;
    const sameLineClause =
      !runGlued || (rt !== undefined && last.span.start.line === rt.span.end.line);
    if (sameLineClause && rt) {
      if (rt.type === "DROP") return "after-drop";
      const ovr = classifyOverrideSlot(tokens, clauseRef);
      if (ovr === "name") return "after-override-type";
      if (ovr === "type") return "after-override";
    }
  }

  // Just typed `type ` keyword (preparing to type a typeName position).
  if (last.type === "TYPE") return "after-type-keyword";

  // Just typed a kind name on a declaration line. Detect via:
  //   prev token is IDENTIFIER and prev-prev is on a different line (i.e.
  //   prev started a new statement). Or we're at top level / body-start
  //   and the previous token is the very first token of the line and is
  //   a known kind. We approximate: the previous IDENTIFIER is followed by
  //   whitespace-only up to cursor and either there is no further token
  //   between or the cursor is after a space — in that case let the editor
  //   handle name typing without keyword noise.
  // We can't easily distinguish "kind name" without access to the registry
  // here; the caller does that test above.

  return "none";
}

/**
 * Whether the `>` at `gtIdx` is a saga `unwind` arrow (`unwind Y >` /
 * `unwind >`) rather than a forward call arrow. Skips an optional caller
 * (qualified name or `|`) to see whether `unwind` immediately precedes.
 */
function isUnwindArrow(tokens: readonly Token[], gtIdx: number): boolean {
  return unwindOf(tokens, gtIdx) >= 0;
}

/** The `unwind` token index for the arrow at `gtIdx`, or -1 if not an unwind
 *  arrow. Skips the arrow's optional caller (`unwind Payment >` / `unwind >`). */
function unwindOf(tokens: readonly Token[], gtIdx: number): number {
  let i = gtIdx - 1;
  // Only `|` is skipped, never `\`. GRAMMAR `caller = qualified_name | "|"` —
  // the snake glyph is not a caller, and an `unwind` has no sibling tail for it
  // to continue (§4.13.4 rule 5), so `unwind \ >` is always a parse error. The
  // accurate reading of that text is a well-formed snake STEP whose preceding
  // `unwind` lost its action — i.e. an ordinary forward arrow — so skipping the
  // `\` to claim the arrow as an unwind arrow would model ungrammatical text as
  // grammatical AND scan across a step boundary.
  if (tokens[i]?.type === "PIPE") {
    i--;
  } else {
    while (tokens[i]?.type === "IDENTIFIER") {
      if (tokens[i - 1]?.type === "DOT") i -= 2;
      else { i--; break; }
    }
  }
  return tokens[i]?.type === "UNWIND" ? i : -1;
}

/**
 * Classify an `on` token by its statement head: `reversible on <trigger>`,
 * `subprocess X on <Caller>`, or (default) a plane list. Scans back to the
 * statement boundary.
 */
function classifyOn(tokens: readonly Token[], onIdx: number): TokenContext {
  for (let i = onIdx - 1; i >= 0; i--) {
    const t = tokens[i]!;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") break;
    const next = tokens[i + 1];
    if (next && t.span.end.line < next.span.start.line) break; // statement boundary
    if (t.type === "REVERSIBLE") return "after-on-trigger";
    if (t.type === "SUBPROCESS") return "after-on-caller";
  }
  return "after-on-plane";
}

/** A boolean-merge connective (`and` / `or`, case-insensitive) — lexed as an
 *  ordinary identifier, so matched by value. */
function isBoolConnective(tok: Token | undefined): boolean {
  if (!tok || tok.type !== "IDENTIFIER") return false;
  const v = tok.value.toLowerCase();
  return v === "and" || v === "or";
}

/**
 * Whether `idx` sits inside a `parallel` merge clause — a `parallel` followed by
 * `join`/`race` earlier on the statement, before the body `{` is reached.
 */
function inParallelMergeClause(tokens: readonly Token[], idx: number): boolean {
  let sawJoinRace = false;
  for (let i = idx; i >= 0; i--) {
    const t = tokens[i]!;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") return false;
    const next = tokens[i + 1];
    if (i < idx && next && t.span.end.line < next.span.start.line) return false; // statement boundary
    if (t.type === "JOIN" || t.type === "RACE") sawJoinRace = true;
    if (t.type === "PARALLEL") return sawJoinRace;
  }
  return false;
}

/** Matching `{` index for the `}` at `rbraceIdx`, or -1. */
function matchingLBrace(tokens: readonly Token[], rbraceIdx: number): number {
  let depth = 0;
  for (let i = rbraceIdx; i >= 0; i--) {
    const t = tokens[i]!.type;
    if (t === "RBRACE") depth++;
    else if (t === "LBRACE") { depth--; if (depth === 0) return i; }
  }
  return -1;
}

/** Byte offset of the `}` matching the `{` at `lbraceIdx`, or `fallback`. */
function matchingRBraceOffset(
  tokens: readonly Token[],
  lbraceIdx: number,
  fallback: number
): number {
  let depth = 0;
  for (let i = lbraceIdx; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.type === "LBRACE") depth++;
    else if (t.type === "RBRACE") { depth--; if (depth === 0) return t.span.end.offset; }
  }
  return fallback;
}

function collectDottedPath(tokens: readonly Token[], dotIdx: number): string[] {
  if (dotIdx < 0) return [];
  if (tokens[dotIdx]?.type !== "DOT") return [];
  const path: string[] = [];
  let i = dotIdx - 1;
  while (i >= 0 && tokens[i]?.type === "IDENTIFIER") {
    path.unshift(tokens[i]!.value);
    if (i - 1 >= 0 && tokens[i - 1]?.type === "DOT") {
      i -= 2;
    } else {
      break;
    }
  }
  return path;
}

/**
 * Whether the cursor begins a fresh statement in the enclosing body.
 *
 * A half-typed word touching the cursor (`Pay█`) is the statement *being
 * typed*, so the question is where that RUN begins, not where it ends — the C1
 * bug measured from the run's end, so a trailing identifier always read as
 * mid-statement and the list came back empty. Boundary set (before the run, or
 * before the bare cursor when no run touches it): a newline, `{`, `}`, `;`
 * (v0.9.0 inline member separator), a process `:` one-liner body, or no prior
 * token. A field/aspect value `:` (`team: Pay█`) is NOT a boundary — that stays
 * a value slot (resolved by `keyBeforeColon`).
 */
function computeStatementStart(
  text: string,
  tokens: readonly Token[],
  lastIdx: number,
  scope: Scope,
  offset: number
): boolean {
  if (lastIdx < 0) return true;
  const lastTok = tokens[lastIdx]!;

  // Where does the statement begin, and what token precedes it? A word-like
  // token the cursor sits WITHIN (`Pay█ments`) — or ends exactly at
  // (`Pay█`) — is the partial being typed, so the statement begins at its
  // start and we look at the token before it. The run touches the cursor when
  // the cursor is strictly after the token's start and at/before its end.
  let runStart = offset;
  let refIdx = lastIdx;
  if (
    isWordLike(lastTok.type) &&
    lastTok.span.start.offset < offset &&
    offset <= lastTok.span.end.offset
  ) {
    runStart = lastTok.span.start.offset;
    refIdx = lastIdx - 1;
  }
  if (refIdx < 0) return true;

  const refTok = tokens[refIdx]!;
  if (
    refTok.type === "LBRACE" ||
    refTok.type === "RBRACE" ||
    refTok.type === "SEMICOLON"
  ) {
    return true;
  }
  if (refTok.type === "COLON") {
    // A process `:` one-liner body starts a step; a header field colon
    // (`sla: value`) does not. The one-liner colon follows a control head: a
    // quoted condition (no resolvable field-key), an identifier condition after
    // `if`/`catch`/`branch`, or a `select` case label. The header field colon
    // has an identifier key with none of that context — it stays a value slot.
    return (
      scope === "process" &&
      (keyBeforeColon(tokens, refIdx) === undefined ||
        isControlOneLinerColon(tokens, refIdx))
    );
  }
  return text.slice(refTok.span.end.offset, runStart).includes("\n");
}

/**
 * The index of the token immediately BEFORE the run under the cursor — the same
 * reference point `computeStatementStart` uses (skip a word-like token the
 * cursor sits within/ends at). -1 when the run is the first token.
 */
function refIndexBeforeRun(tokens: readonly Token[], lastIdx: number, offset: number): number {
  if (lastIdx < 0) return -1;
  const lastTok = tokens[lastIdx]!;
  if (
    isWordLike(lastTok.type) &&
    lastTok.span.start.offset < offset &&
    offset <= lastTok.span.end.offset
  ) {
    return lastIdx - 1;
  }
  return lastIdx;
}

/** Start offset of the half-typed run under the cursor (a word-like token the
 *  cursor sits within/ends at), or the bare cursor offset when none touches it. */
function runStartOffset(tokens: readonly Token[], lastIdx: number, offset: number): number {
  if (lastIdx < 0) return offset;
  const lastTok = tokens[lastIdx]!;
  if (
    isWordLike(lastTok.type) &&
    lastTok.span.start.offset < offset &&
    offset <= lastTok.span.end.offset
  ) {
    return lastTok.span.start.offset;
  }
  return offset;
}

/** The colon governing the value slot at `lastIdx` (walking back to a `:`), or
 *  -1 when a brace / `;` bounds the statement first (no value slot). */
function governingColon(tokens: readonly Token[], lastIdx: number): number {
  for (let i = lastIdx; i >= 0; i--) {
    const t = tokens[i]!;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") return -1;
    if (t.type === "COLON") return i;
  }
  return -1;
}

/** Index of the FIRST segment of a (possibly dotted) key whose last segment is
 *  at `endIdx` (`a.b.c` → the index of `a`). */
function keyHeadIdx(tokens: readonly Token[], endIdx: number): number {
  let i = endIdx;
  while (i - 2 >= 0 && tokens[i - 1]?.type === "DOT" && tokens[i - 2]?.type === "IDENTIFIER") i -= 2;
  return i;
}

/**
 * Classify a module-body value slot governed by the colon at `colonIdx`:
 *   - `"membership"` — `aspect <key> : █` (the key is headed by `aspect`);
 *   - `"roster"`     — `aspect <key> for : █` (place-side membership, §1.4);
 *   - `null`         — a plain field value.
 */
function classifyAspectValueSlot(tokens: readonly Token[], colonIdx: number): "membership" | "roster" | null {
  const before1 = tokens[colonIdx - 1];
  if (!before1) return null;
  // Roster: `for` sits between the aspect key and the colon.
  if (before1.type === "IDENTIFIER" && before1.value === "for") {
    const keyEnd = colonIdx - 2;
    if (tokens[keyEnd]?.type !== "IDENTIFIER") return null;
    return tokens[keyHeadIdx(tokens, keyEnd) - 1]?.type === "ASPECT" ? "roster" : null;
  }
  // Inline membership: the key immediately before the colon is headed by `aspect`.
  if (before1.type !== "IDENTIFIER") return null;
  return tokens[keyHeadIdx(tokens, colonIdx - 1) - 1]?.type === "ASPECT" ? "membership" : null;
}

/**
 * Classify an `override` clause target slot by the reference token before the
 * run (`override [required] <kind> <name>`): `"type"` = the new-kind position
 * (`override █` / `override required █`), `"name"` = the inherited-entity name
 * position (`override <kind> █`), `null` = not an override clause.
 */
function classifyOverrideSlot(tokens: readonly Token[], refIdx: number): "type" | "name" | null {
  if (refIdx < 0) return null;
  const seq: Token[] = [];
  for (let i = refIdx; i >= 0; i--) {
    const t = tokens[i]!;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") break;
    seq.unshift(t);
    const prev = tokens[i - 1];
    if (prev && prev.span.end.line < t.span.start.line) break; // statement start
  }
  if (seq.length === 0 || seq[0]!.type !== "OVERRIDE") return null;
  let k = 1;
  if (seq[k]?.type === "REQUIRED") k++;
  if (seq[k]?.type === "IDENTIFIER") {
    // A kind identifier is present → the name slot, unless the name is already
    // typed too (kind + name), in which case we're past the clause.
    return seq[k + 1]?.type === "IDENTIFIER" ? null : "name";
  }
  return "type";
}

// Which preceding sibling construct makes a follower keyword legal at this
// process statement start: `else` follows an `if`/`else`; `catch` follows a
// `try`. Blocks are matched by the opener's control (`classifyProcBlock`);
// one-liners by a keyword before the `:` body separator.
const ELSE_BLOCKS: ReadonlySet<ProcBlockKind> = new Set(["if", "else"]);
const ELSE_ONELINERS: ReadonlySet<TokenType> = new Set<TokenType>(["IF", "ELSE"]);
const CATCH_BLOCKS: ReadonlySet<ProcBlockKind> = new Set(["try"]);
const CATCH_ONELINERS: ReadonlySet<TokenType> = new Set<TokenType>(["TRY"]);

/**
 * Whether a follower keyword is legal at this process statement start because
 * the immediately preceding sibling was one of `blockKinds` (a `{ }` block) or a
 * one-liner headed by one of `oneLinerTypes` (`if …: step`, `try …: step`). §3.
 */
function precedingSiblingControl(
  tokens: readonly Token[],
  refIdx: number,
  blockKinds: ReadonlySet<ProcBlockKind>,
  oneLinerTypes: ReadonlySet<TokenType>
): boolean {
  if (refIdx < 0) return false;
  const refTok = tokens[refIdx]!;
  // Previous sibling is a block: check the opener's head.
  if (refTok.type === "RBRACE") {
    const opener = matchingLBrace(tokens, refIdx);
    if (opener < 0) return false;
    return blockKinds.has(classifyProcBlock(collectDeclHead(tokens, opener)));
  }
  // Previous sibling is a one-liner: find its first token, scan for the keyword
  // before the first COLON (the one-liner body separator).
  let start = refIdx;
  for (let j = refIdx; j >= 0; j--) {
    const t = tokens[j]!;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") { start = j + 1; break; }
    const prev = tokens[j - 1];
    if (prev && prev.span.end.line < t.span.start.line) { start = j; break; }
    start = j;
  }
  for (let j = start; j <= refIdx; j++) {
    const tt = tokens[j]!.type;
    if (tt === "COLON") break;
    if (oneLinerTypes.has(tt)) return true;
  }
  return false;
}

/**
 * Whether an ACTION (a call arrow or a control/terminator keyword) appears
 * between the process body's `{` and the cursor — i.e. the header zone (where
 * bare strings are the description and `field`/`aspect` members are legal) has
 * ended (PROCESS.md §1). Header members (fields, descriptions, `aspect`,
 * `subprocess`) are not actions.
 */
function hasActionBefore(tokens: readonly Token[], procStartIdx: number, lastIdx: number): boolean {
  // Only actions DIRECTLY in the process body (depth 0) end the header zone. A
  // `>` / control keyword inside a nested `subprocess { … }` header member (a
  // legal header member, parser.ts) or any other sub-block is not this process's
  // action, so we track brace depth and inspect depth-0 tokens only.
  let depth = 0;
  for (let j = procStartIdx + 1; j <= lastIdx; j++) {
    const t = tokens[j]!;
    if (t.type === "LBRACE") { depth++; continue; }
    if (t.type === "RBRACE") { depth--; continue; }
    if (depth !== 0) continue;
    if (t.type === "GT" || t.type === "PIPE" || t.type === "BACKSLASH") return true;
    if (ACTION_KEYWORDS.has(t.type)) return true;
    // An owner-anchored note (`Caller "text"`) is a real step (parser sets
    // sawAction), so it ends the header zone — unlike a bare prose description
    // (`"text"` at statement start, prev is `{`/`;`/newline). Distinguish by the
    // token before the string: an IDENTIFIER owner ⇒ note step.
    if (t.type === "STRING" && tokens[j - 1]?.type === "IDENTIFIER") return true;
  }
  return false;
}

/**
 * Byte spans of `subprocess … { … }` bodies declared DIRECTLY in the process
 * body opened at `procStartIdx` (depth-1 members). A nested subprocess's `as`
 * labels are a distinct re-entry namespace, so `go` completion carves these out.
 */
function nestedSubprocessSpans(
  tokens: readonly Token[],
  procStartIdx: number,
  textLen: number
): { start: number; end: number }[] {
  const spans: { start: number; end: number }[] = [];
  let depth = 0;
  for (let i = procStartIdx; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.type === "LBRACE") { depth++; continue; }
    if (t.type === "RBRACE") { depth--; if (depth === 0) break; continue; }
    if (depth === 1 && t.type === "SUBPROCESS") {
      for (let j = i + 1; j < tokens.length; j++) {
        const b = tokens[j]!;
        if (b.type === "LBRACE") {
          spans.push({
            start: b.span.start.offset,
            end: matchingRBraceOffset(tokens, j, textLen),
          });
          break;
        }
        if (b.type === "SEMICOLON" || b.type === "RBRACE") break; // bodyless forward-ref
      }
    }
  }
  return spans;
}

/** Process keywords that MARK a step/action (ending the header zone). Excludes
 *  header members (`aspect`, `subprocess`) and pure sub-clause keywords. */
const ACTION_KEYWORDS: ReadonlySet<TokenType> = new Set<TokenType>([
  "IF", "SELECT", "EACH", "TRY", "PARALLEL", "REVERSIBLE", "AWAIT", "DIST",
  "DO", "GO", "FAIL", "FINISH",
]);

/**
 * From an `unwind` arrow (`unwind >` at `arrowIdx`), the forward step's callee
 * top module (compensation targets the SAME callee) and the previous reversible
 * item's forward callee (the #1 saga mistake, down-ranked). PROCESS.md §8.
 */
function parseUnwindCallee(
  tokens: readonly Token[],
  arrowIdx: number
): { forwardModule?: string; forwardInterface?: string; previousModule?: string } {
  const unwindIdx = unwindOf(tokens, arrowIdx);
  if (unwindIdx < 0) return {};
  // Forward step: the nearest `>` before `unwind` on this item; its call target's
  // first segment is the callee's top module, and the full dotted target is the
  // exact forward interface (which a step never compensates with itself — P2.5).
  let fwdGt = -1;
  let forwardModule: string | undefined;
  let forwardInterface: string | undefined;
  for (let i = unwindIdx - 1; i >= 0; i--) {
    const t = tokens[i]!;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") break;
    if (t.type === "GT") {
      fwdGt = i;
      const tgt = tokens[i + 1];
      forwardModule = tgt?.type === "IDENTIFIER" ? tgt.value : undefined;
      forwardInterface = collectForwardTarget(tokens, i + 1);
      break;
    }
  }
  let previousModule: string | undefined;
  if (fwdGt >= 0) previousModule = previousForwardCalleeTop(tokens, fwdGt);
  return { forwardModule, forwardInterface, previousModule };
}

/** The full dotted call target starting at `startIdx` (`Inventory . reserve` →
 *  `Inventory.reserve`), or undefined when no identifier is there. */
function collectForwardTarget(tokens: readonly Token[], startIdx: number): string | undefined {
  if (tokens[startIdx]?.type !== "IDENTIFIER") return undefined;
  const segs: string[] = [tokens[startIdx]!.value];
  let i = startIdx + 1;
  while (tokens[i]?.type === "DOT" && tokens[i + 1]?.type === "IDENTIFIER") {
    segs.push(tokens[i + 1]!.value);
    i += 2;
  }
  return segs.join(".");
}

/** The forward callee top module of the reversible item BEFORE the one whose
 *  forward arrow is at `curForwardGt` — the nearest earlier non-unwind `>`. */
function previousForwardCalleeTop(tokens: readonly Token[], curForwardGt: number): string | undefined {
  for (let j = curForwardGt - 1; j >= 0; j--) {
    const t = tokens[j]!;
    if (t.type === "LBRACE" || t.type === "RBRACE") return undefined; // left the saga block
    if (t.type === "GT" && !isUnwindArrow(tokens, j)) {
      const tgt = tokens[j + 1];
      return tgt?.type === "IDENTIFIER" ? tgt.value : undefined;
    }
  }
  return undefined;
}

/**
 * Branch names declared in the `parallel` block enclosing the merge clause at
 * `idx` (`branch <name>` inside `{ … }`). The body may sit AFTER the cursor, so
 * this scans forward from the `parallel` keyword's block.
 */
function scanParallelBranchNames(tokens: readonly Token[], idx: number): string[] {
  // Find the `parallel` keyword heading this merge clause.
  let parIdx = -1;
  for (let i = idx; i >= 0; i--) {
    const t = tokens[i]!;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") break;
    if (t.type === "PARALLEL") { parIdx = i; break; }
  }
  if (parIdx < 0) return [];
  // Find the block `{` that opens after the merge clause.
  let braceIdx = -1;
  for (let i = parIdx + 1; i < tokens.length; i++) {
    if (tokens[i]!.type === "LBRACE") { braceIdx = i; break; }
  }
  if (braceIdx < 0) return [];
  const names: string[] = [];
  const seen = new Set<string>();
  let depth = 0;
  for (let i = braceIdx; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.type === "LBRACE") depth++;
    else if (t.type === "RBRACE") { depth--; if (depth === 0) break; }
    // Only THIS block's own branches (depth 1). A nested `parallel` inside a
    // branch body declares branches at depth ≥3 that belong to the inner merge,
    // not this one (GRAMMAR bool_atom references the enclosing block's branches).
    else if (t.type === "BRANCH" && depth === 1) {
      const nameTok = tokens[i + 1];
      if (nameTok?.type === "IDENTIFIER" && !seen.has(nameTok.value)) {
        seen.add(nameTok.value);
        names.push(nameTok.value);
      }
    }
  }
  return names;
}

/**
 * Whether the `in` at `inIdx` is a LOOP `in` — `each <var> in █` — as opposed
 * to a placement `in Target`. A loop `in` sits on the same line as a preceding
 * `each` keyword (the `each_head` is single-line: `each (var) in coll`).
 */
function isEachLoopIn(tokens: readonly Token[], inIdx: number): boolean {
  const inTok = tokens[inIdx];
  if (!inTok) return false;
  const line = inTok.span.start.line;
  for (let i = inIdx - 1; i >= 0; i--) {
    const t = tokens[i]!;
    if (t.span.start.line < line) break; // crossed to a previous line
    if (t.type === "EACH") return true;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") break;
  }
  return false;
}

// =============================================================================
// In-string slots (P1b) — view patterns + classification values
// =============================================================================

/**
 * Completions for a plain open string literal: a view `include`/`exclude`
 * pattern (module-path globs) or a classification value (previously-used string
 * values on the key). Anything else (a description, an `await`/`if` condition)
 * yields nothing — a string admits no declaration keywords.
 */
function stringSlotCompletions(
  file: FileState,
  workspace: Workspace,
  symbols: SymbolTable,
  quoteOff: number
): CompletionItem[] {
  const tokens = file.tokens;
  const { stack, lastIdx } = buildBraceStack(tokens, workspace, quoteOff);
  const scope = stack[stack.length - 1]?.scope ?? "top";
  const prevTok = lastIdx >= 0 ? tokens[lastIdx] : undefined;
  if (!prevTok) return [];

  // View include/exclude pattern list — the first pattern or a `,` continuation.
  if (scope === "view") {
    if (prevTok.type === "INCLUDE" || prevTok.type === "EXCLUDE") {
      return suggestViewPatterns(workspace, symbols, false);
    }
    if (prevTok.type === "COMMA" && inPatternList(tokens, lastIdx)) {
      return suggestViewPatterns(workspace, symbols, false);
    }
  }

  // Classification string value: `[aspect] key : "…"`.
  if (prevTok.type === "COLON") {
    const key = keyBeforeColon(tokens, lastIdx);
    if (key === undefined) return [];
    const isAspect =
      scope === "labels" ||
      tokens[keyHeadIdx(tokens, lastIdx - 1) - 1]?.type === "ASPECT";
    return stringValueCompletions(workspace, key, isAspect ? "labels" : "fields");
  }
  return [];
}

/** Whether `commaIdx` continues a view `include`/`exclude` pattern list on the
 *  same statement (`include "a", █`). */
function inPatternList(tokens: readonly Token[], commaIdx: number): boolean {
  for (let i = commaIdx; i >= 0; i--) {
    const t = tokens[i]!;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") return false;
    if (t.type === "INCLUDE" || t.type === "EXCLUDE") return true;
  }
  return false;
}

/** Previously-used STRING values on `key`, inserted BARE (the cursor is already
 *  inside the quotes). */
function stringValueCompletions(
  workspace: Workspace,
  key: string,
  which: "fields" | "labels"
): CompletionItem[] {
  const out: CompletionItem[] = [];
  for (const [val, quoted] of collectAssignedValues(workspace, key, which)) {
    if (!quoted) continue; // only string values belong inside a string literal
    out.push(
      ranked(
        {
          label: val,
          kind: CompletionItemKind.Value,
          detail: `value of ${key}`,
          insertText: val,
          data: { reused: true }, // self-echo guard (see valueItems / P1.1)
        },
        RANK_PRIMARY
      )
    );
  }
  return out;
}

/**
 * Whether the `:` at `colonIdx` is a process ONE-LINER body colon whose head is
 * an identifier — `if ready: █`, `catch timeout: █`, `branch fast: █`, or a
 * `select` case label (`gold: █`). These begin a step; a header field colon
 * (`sla: █`, identifier key with no control context) does not.
 */
function isControlOneLinerColon(tokens: readonly Token[], colonIdx: number): boolean {
  // Walk back over the (possibly dotted) identifier head to its first segment.
  let i = colonIdx - 1;
  while (i >= 0 && tokens[i]?.type === "IDENTIFIER") {
    if (tokens[i - 1]?.type === "DOT") i -= 2;
    else break;
  }
  const head = tokens[i];
  const before = tokens[i - 1];
  if (
    head &&
    before &&
    before.span.end.line === head.span.start.line &&
    (before.type === "IF" || before.type === "CATCH" || before.type === "BRANCH")
  ) {
    return true;
  }
  // A `select` case label: the enclosing block was opened by `select …`.
  return enclosingBlockIsSelect(tokens, colonIdx);
}

/**
 * Whether the innermost block enclosing `fromIdx` was opened by a `select` head
 * (`Owner? select count? head? { … }`) — i.e. the cursor is among that block's
 * case labels.
 */
function enclosingBlockIsSelect(tokens: readonly Token[], fromIdx: number): boolean {
  let depth = 0;
  let opener = -1;
  for (let i = fromIdx - 1; i >= 0; i--) {
    const t = tokens[i]!.type;
    if (t === "RBRACE") depth++;
    else if (t === "LBRACE") {
      if (depth === 0) { opener = i; break; }
      depth--;
    }
  }
  if (opener < 0) return false;
  // Scan the opener's head (back to a statement boundary) for `select`.
  for (let i = opener - 1; i >= 0; i--) {
    const t = tokens[i]!;
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") break;
    const next = tokens[i + 1];
    if (next && t.span.end.line < next.span.start.line) break; // statement boundary
    if (t.type === "SELECT") return true;
  }
  return false;
}

function offsetAt(text: string, line: number, character: number): number {
  let cur = 0;
  let lineStart = 0;
  for (let i = 0; i < text.length; i++) {
    if (cur === line) break;
    if (text[i] === "\n") {
      cur++;
      lineStart = i + 1;
    }
  }
  return Math.min(text.length, lineStart + character);
}

// =============================================================================
// `[[ … ]]` reference completion (§4.10 / §4.15)
// =============================================================================

/**
 * If `offset` sits inside an unclosed `[[ … ` reference, return the partial
 * inner text (between `[[` and the cursor). Refs are single-line, so a newline
 * or a closing `]]` before the cursor means we are not inside one.
 */
function bracketContext(text: string, offset: number): string | null {
  const open = text.lastIndexOf("[[", offset - 1);
  if (open < 0) return null;
  const between = text.slice(open + 2, offset);
  if (between.includes("]]") || between.includes("\n")) return null;
  return between;
}

// =============================================================================
// `#` stable-id slot — mint a random ID (the auto-id TODO)
// =============================================================================

/**
 * If `offset` sits in a declaration's stable-id slot — a `#` (optionally with a
 * partial id already typed) that directly follows a kind keyword, e.g.
 * `module #|`, `type #ab|`, `process #|` — return the offset of the `#` so the
 * generated item replaces the whole `#…` token. A `[[#id]]` reference is caught
 * earlier by `bracketContext`, so it never reaches here. Returns null otherwise.
 */
function stableIdSlot(
  tokens: readonly Token[],
  text: string,
  offset: number
): { hash: number } | null {
  // Walk back over the partial id chars already typed after the `#`.
  let i = offset;
  while (i > 0 && isAlnumCode(text.charCodeAt(i - 1))) i--;
  if (i === 0 || text[i - 1] !== "#") return null;
  const hash = i - 1;
  // The `#` must directly follow the kind keyword (an identifier-ish word)
  // across only inline whitespace — that's the one place a declaration id is
  // grammatical (`<kind> #id <name>` / `type #id <parent> <name>`).
  let j = hash;
  while (j > 0 && (text[j - 1] === " " || text[j - 1] === "\t")) j--;
  if (j === 0 || !isIdentChar(text.charCodeAt(j - 1))) return null;
  // Not inside a string literal: a `#` in a description (`"item #1"`) just
  // happens to follow a word and must not offer an id.
  if (stringContextAt(tokens, text, hash) !== null) return null;
  return { hash };
}

/**
 * If `offset` falls inside a string literal, the offset of its opening quote;
 * else null. Token-first so it is immune to quote-parity pollution from EARLIER
 * constructs: a `//`/`/* *​/` comment or a `"""…"""` verbatim block (whose body
 * may hold stray `"`) is a single lexed unit, not a running toggle — the raw
 * quote-counting predecessor mis-parsed both and killed all downstream
 * completion once P1b routed open strings through here.
 *
 * Two cases:
 *  1. A CLOSED string (`"…"` or `"""…"""`) is a STRING token whose span contains
 *     the cursor.
 *  2. An UNTERMINATED string being typed (`include "Pay|`) has NO token — the
 *     lexer swallows to EOF and only errors. So after the last token that ends
 *     at/before the cursor, scan the raw slice for the opening `"`, skipping
 *     comment trivia (once a quote opens, all trailing text is string content,
 *     so the first unescaped `"` is the opener).
 */
function stringContextAt(
  tokens: readonly Token[],
  text: string,
  offset: number
): { quoteOff: number } | null {
  let scanStart = 0;
  for (const t of tokens) {
    if (t.type === "EOF") break;
    if (t.span.start.offset >= offset) break;
    if (
      t.type === "STRING" &&
      offset > t.span.start.offset &&
      offset < t.span.end.offset
    ) {
      return { quoteOff: t.span.start.offset };
    }
    if (t.span.end.offset <= offset) scanStart = t.span.end.offset;
  }
  // Unterminated-string scan over the post-last-token trivia + partial content.
  for (let i = scanStart; i < offset; i++) {
    const c = text[i];
    if (c === "/" && text[i + 1] === "/") {
      const nl = text.indexOf("\n", i);
      if (nl < 0 || nl >= offset) return null;
      i = nl;
      continue;
    }
    if (c === "/" && text[i + 1] === "*") {
      const close = text.indexOf("*/", i + 2);
      if (close < 0 || close + 2 > offset) return null;
      i = close + 1;
      continue;
    }
    if (c === '"' && text[i - 1] !== "\\") return { quoteOff: i };
  }
  return null;
}

function isAlnumCode(code: number): boolean {
  return (
    (code >= 48 && code <= 57) || // 0-9
    (code >= 65 && code <= 90) || // A-Z
    (code >= 97 && code <= 122) // a-z
  );
}

/** The single "generate a random stable ID" item for a `#` declaration slot. It
 *  replaces the `#…` under the cursor with a freshly minted, collision-checked
 *  ID; ranked alone at the top since it's the only meaningful suggestion here. */
function generateIdCompletion(
  workspace: Workspace,
  slot: { hash: number },
  line: number,
  character: number,
  offset: number
): CompletionItem[] {
  const id = freshStableId(collectStableIds(workspace.model()));
  // Replace only the partial id AFTER the `#`, not the `#` itself. `#` is the
  // trigger character; clients (Monaco) handle a trigger-char completion like a
  // `.`-member completion — the inserted text and the replace range sit AFTER
  // the trigger, and the trigger char is NOT part of the filter word. Covering
  // the `#` makes Monaco filter the item out (the in-range `#` won't match the
  // id) and the widget falls back to word suggestions. So: range starts at the
  // char after `#`, and we insert the bare id.
  const afterHash = slot.hash + 1;
  const range = {
    start: { line, character: character - (offset - afterHash) },
    end: { line, character },
  };
  return [
    {
      // Label keeps the `#` so the item reads as a stable id; insert/filter use
      // the bare id since the `#` is already in the buffer.
      label: `#${id}`,
      kind: CompletionItemKind.Constant,
      detail: "generate a random stable ID",
      documentation: {
        kind: "markdown" as const,
        value:
          "Mint a fresh, **meaningless**, collision-checked stable ID.\n\n" +
          "IDs must be meaningless and permanent — they anchor identity across renames and moves.",
      },
      insertText: id,
      filterText: id,
      textEdit: { range, newText: id },
      sortText: "0",
    },
  ];
}

// =============================================================================
// `~{ … }` calc-region completion (§4.10.5)
// =============================================================================

/**
 * If `offset` is inside an unclosed `~{ … ` calc region, return the text before
 * the cursor plus the full region body (for in-scope dimension/tag analysis).
 * Calc regions may span lines, so only a closing `}` ends one.
 */
function calcContext(text: string, offset: number): { before: string; region: string } | null {
  // a calc region opens with `~{` (estimate) or `={` (precise)
  const open = Math.max(text.lastIndexOf("~{", offset - 1), text.lastIndexOf("={", offset - 1));
  if (open < 0) return null;
  const before = text.slice(open + 2, offset);
  if (before.includes("}")) return null; // region already closed before the cursor
  const close = text.indexOf("}", offset);
  return { before, region: text.slice(open + 2, close < 0 ? text.length : close) };
}

function calcItem(
  label: string,
  kind: CompletionItemKind,
  detail: string,
  rank: string
): CompletionItem {
  return { label, kind, detail, sortText: `${rank}${label}`, filterText: label };
}

/**
 * Context-aware calc completion inside `~{ … }`. Reads the local sub-context from
 * the text before the cursor (after `#` → tags; after `?` → result specs; after
 * `/` → denominators; otherwise units + dimensions in scope) and surfaces the
 * dimensions already used in this region first.
 */
function calcCompletions(before: string, region: string): CompletionItem[] {
  const { dims, tags } = analyzeBlock(region);
  const line = before.slice(before.lastIndexOf("\n") + 1);
  // the half-typed word is identifier chars only — `/` and `#` are sub-context
  // triggers, not part of the token being completed.
  const partial = line.match(/[A-Za-z0-9]*$/)?.[0] ?? "";
  const pre = line.slice(0, line.length - partial.length).replace(/\s+$/, "");
  const last = pre.slice(-1);

  const dimItems = dims.map((d) => calcItem(d, CompletionItemKind.Variable, "dimension in scope", "0"));
  const units = (cats: ReadonlyArray<UnitCategory>): CompletionItem[] =>
    CALC_UNITS.filter((u) => cats.includes(u.category)).map((u) =>
      calcItem(u.label, CompletionItemKind.Unit, u.detail, "1")
    );

  // After `#` — a bucket tag.
  if (last === "#") return tags.map((t) => calcItem(`#${t}`, CompletionItemKind.EnumMember, "tag", "0"));

  // After `?` (the query marker) the cursor is in result-spec position.
  if (line.includes("?")) {
    return [
      calcItem("**", CompletionItemKind.Operator, "show the derivation", "0"),
      ...["/s", "/min", "/h", "/d", "/mo", "/y"].map((r) =>
        calcItem(r, CompletionItemKind.Unit, `per ${r.slice(1)}`, "1")
      ),
      ...dimItems,
      ...units(["data", "money"]),
    ];
  }

  // After `/` — a denominator (per …).
  if (last === "/") return [...units(["time"]), ...dimItems];

  // Otherwise: dimensions in scope first, then the unit vocabulary.
  return [...dimItems, ...units(["data", "time", "money"])];
}

type UnitCategory = "data" | "time" | "money";

/**
 * Suggestions inside a `[[ … ]]`:
 *   - `@…`      → aspect.keys / key:value identities
 *   - `Doc#…`   → section slugs of the named document
 *   - otherwise → element names + document names + aspect/`#section` starters
 */
function refCompletions(
  workspace: Workspace,
  symbols: SymbolTable,
  inner: string
): CompletionItem[] {
  if (inner.startsWith("@")) return aspectRefCompletions(workspace, symbols, false);

  // `[[#…]]` — element ref by stable id. Offer the declared stable ids (the `#`
  // is already typed, so the leaf id alone).
  if (inner.startsWith("#")) return stableIdCompletions(symbols);

  const hash = inner.indexOf("#");
  if (hash >= 0 && !inner.startsWith("#")) {
    return sectionRefCompletions(workspace, inner.slice(0, hash).trim());
  }

  const out: CompletionItem[] = [];
  // Element names — referenceable by simple or qualified name.
  for (const sym of symbols.declared()) {
    if (sym.kind === "aspect") continue;
    out.push(symbolCompletion(workspace, sym, sym.qualifiedName));
  }
  // Documents — referenceable as `[[Doc]]` / `[[Doc#section]]`.
  for (const doc of workspace.documents()) {
    out.push({ label: doc.name, kind: CompletionItemKind.File, detail: "document" });
  }
  // Aspect starters (prefixed, since `@` not yet typed).
  out.push(...aspectRefCompletions(workspace, symbols, true));
  return out;
}

/**
 * Aspect-key completions for `[[@…]]`. `withPrefix` controls whether the `@`
 * marker is included in the label (true in the general `[[` context where the
 * author hasn't typed it yet; false once they have).
 */
function aspectRefCompletions(
  workspace: Workspace,
  symbols: SymbolTable,
  withPrefix: boolean
): CompletionItem[] {
  const at = withPrefix ? "@" : "";
  const out: CompletionItem[] = [];
  const seen = new Set<string>();
  const add = (label: string, detail: string): void => {
    if (seen.has(label)) return;
    seen.add(label);
    out.push({ label, kind: CompletionItemKind.EnumMember, detail });
  };
  for (const sym of symbols.declared()) {
    if (sym.kind !== "aspect") continue;
    // Aspect `qualifiedName` is `key.path="value"`; recover the key + value.
    const eq = sym.qualifiedName.indexOf("=");
    const key = eq >= 0 ? sym.qualifiedName.slice(0, eq) : sym.qualifiedName;
    add(`${at}${key}`, "aspect key");
    if (sym.name) add(`${at}${key}:${sym.name}`, "aspect");
  }
  return out;
}

/** Section-slug completions for `[[Doc#…]]` against the named document. */
function sectionRefCompletions(
  workspace: Workspace,
  docName: string
): CompletionItem[] {
  const doc = workspace.documents().find((d) => d.name === docName);
  if (!doc) return [];
  return doc.sections.map((s) => ({
    label: s.slug,
    kind: CompletionItemKind.Reference,
    detail: s.text,
  }));
}

// =============================================================================
// `@aspectPath` interpolation completion (§4.10.3) — bare + foreign getter
// =============================================================================

type AtContext =
  | { readonly kind: "bare"; readonly path: string; readonly atPos: number }
  | { readonly kind: "foreign"; readonly ref: string; readonly path: string; readonly atPos: number };

/**
 * If the cursor sits in a `@aspectPath` interpolation (NOT inside a `[[ … ]]` —
 * the caller checks that first), classify it. A `@` glued to a preceding `]]`
 * is the foreign getter `[[ref]]@path` (reads the referenced node's aspect); any
 * other `@` after a non-identifier char is a bare interpolation (the owning
 * node's aspect). Mirrors the ASPECT_RE / `[[ref]]@path` grammar in core refs.ts.
 */
function atRefContext(text: string, offset: number): AtContext | null {
  // Walk back over the (possibly empty, possibly dotted) label path.
  let i = offset;
  while (i > 0 && isAspectPathChar(text.charCodeAt(i - 1))) i--;
  const atPos = i - 1;
  if (atPos < 0 || text[atPos] !== "@") return null;
  const path = text.slice(i, offset);
  // A path token may not start mid-segment with a dot, and `@` cannot sit on a
  // line of its own far from text — but those are rendering concerns; for
  // completion the `@` + an identifier-ish run is enough.

  // Foreign getter: `@` immediately preceded by `]]` closing a `[[ … ]]`.
  if (text.slice(atPos - 2, atPos) === "]]") {
    const open = text.lastIndexOf("[[", atPos);
    if (open < 0) return null;
    const closeBeforeAt = text.indexOf("]]", open);
    if (closeBeforeAt !== atPos - 2) return null; // the `]]` must be this ref's
    const ref = text.slice(open + 2, atPos - 2).trim();
    if (!ref) return null;
    return { kind: "foreign", ref, path, atPos };
  }

  // Bare interpolation: the char before `@` must be a line start or a
  // non-identifier (grammar: `foo@bar` is not a ref).
  const before = atPos > 0 ? text.charCodeAt(atPos - 1) : 10 /* \n */;
  if (isIdentChar(before)) return null;
  return { kind: "bare", path, atPos };
}

/**
 * Declared + built-in decorations as `@name` items.
 *
 * Shared by the statement-start declaration grammar and the `@`-typed path
 * below — the same list either way, so typing `@` narrows the menu instead of
 * landing somewhere that offers something different.
 */
function decorationItems(workspace: Workspace): CompletionItem[] {
  const out: CompletionItem[] = [];
  for (const [name, decl] of workspace.model().decorationRegistry?.kinds ?? []) {
    const primary = decl.fields[0]?.name;
    out.push({
      label: `@${name}`,
      kind: CompletionItemKind.Event,
      detail: decl.builtin ? "built-in decoration" : "decoration",
      insertTextFormat: InsertTextFormat.Snippet,
      insertText: primary ? `@${name}($\{1:${primary}-value})` : `@${name}`,
      documentation: {
        kind: "markdown" as const,
        value:
          (decl.descriptions?.join("\n\n") ?? `Decoration \`@${name}\``) +
          (decl.fields.length
            ? `\n\nFields: ${decl.fields.map((f) => `\`${f.name}: ${f.type}\``).join(", ")}`
            : "") +
          (decl.targets?.length ? `\n\nTargets: ${decl.targets.join(", ")}` : ""),
      },
      sortText: `${RANK_SYMBOL}2${name.toLowerCase()}`,
    });
  }
  return out;
}

/** Only whitespace between `pos` and the start of its line — the shape a
 *  decoration prefix always has, and an interpolation never does. */
function atLineStart(text: string, pos: number): boolean {
  for (let i = pos - 1; i >= 0; i--) {
    const c = text[i];
    if (c === "\n") return true;
    if (c !== " " && c !== "\t" && c !== "\r") return false;
  }
  return true;
}

/** Pin each item's edit over the whole `@prefix` run, so accepting one REPLACES
 *  the `@` the user already typed instead of appending after it (`@@planned`).
 *  `@name` never spans a newline, so this is plain column arithmetic — the same
 *  shape `finalize` uses for a half-typed word. */
function withReplaceRange(
  items: CompletionItem[],
  line: number,
  character: number,
  span: number,
): CompletionItem[] {
  const range = {
    start: { line, character: Math.max(0, character - span) },
    end: { line, character },
  };
  return items.map((it) => ({
    ...it,
    textEdit: { range, newText: it.insertText ?? String(it.label) },
  }));
}

function isAspectPathChar(code: number): boolean {
  return isIdentChar(code) || code === 46 /* . */;
}

/** Label-key completions on the node a foreign getter `[[ref]]@…` references. */
function foreignAspectCompletions(workspace: Workspace, ref: string): CompletionItem[] {
  const index = workspace.descriptionIndex();
  const target = ref.startsWith("#")
    ? index.resolveId(ref.slice(1).trim())
    : index.resolveName(ref)[0];
  if (!target) return [];
  return aspectKeyItems(aspectKeysFor(workspace.model(), target.qualifiedName));
}

/** Label-key completions on the node that OWNS the description being edited
 *  (the element whose body the cursor's string literal sits in). */
function bareInterpCompletions(
  workspace: Workspace,
  symbols: SymbolTable,
  uri: string,
  atPos: number
): CompletionItem[] {
  const owner = symbols.resolveAt(uri, atPos);
  if (!owner) return [];
  return aspectKeyItems(aspectKeysFor(workspace.model(), owner.qualifiedName));
}

function aspectKeyItems(keys: readonly string[]): CompletionItem[] {
  const seen = new Set<string>();
  const out: CompletionItem[] = [];
  for (const key of keys) {
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ranked({ label: key, kind: CompletionItemKind.Field, detail: "label" }, RANK_PRIMARY));
  }
  return out;
}

/** The label-key paths declared on the element with this qualified name
 *  (module / surface / interface), or [] if not found. */
function aspectKeysFor(model: ResolvedModel, qname: string): string[] {
  const keysOf = (labels: readonly { path: readonly string[]; drop?: boolean }[]): string[] =>
    labels.filter((l) => !l.drop).map((l) => l.path.join("."));
  let found: string[] | undefined;
  const visitSurface = (s: ResolvedModel["modules"][number]["surfaces"][number], parent: string): void => {
    const q = `${parent}.${s.name}`;
    if (q === qname) found = keysOf(s.body.aspects);
    for (const i of s.interfaces) if (`${q}.${i.name}` === qname) found = keysOf(i.body.aspects);
    for (const sub of s.surfaces) visitSurface(sub, q);
  };
  const visit = (mods: ResolvedModel["modules"], parent: string): void => {
    for (const m of mods) {
      const q = parent ? `${parent}.${m.name}` : m.name;
      if (q === qname) found = keysOf(m.body.aspects);
      for (const i of m.interfaces) if (`${q}.${i.name}` === qname) found = keysOf(i.body.aspects);
      for (const s of m.surfaces) visitSurface(s, q);
      visit(m.modules, q);
    }
  };
  visit(model.modules, "");
  return found ?? [];
}

/** Stable-id completions for `[[#…]]`: the authored ids only (synthesized
 *  symbol ids are prefixed `kind:` and carry a colon). */
function stableIdCompletions(symbols: SymbolTable): CompletionItem[] {
  const seen = new Set<string>();
  const out: CompletionItem[] = [];
  for (const sym of symbols.declared()) {
    if (!sym.id || sym.id.includes(":")) continue; // synthesized id, not authored
    if (seen.has(sym.id)) continue;
    seen.add(sym.id);
    out.push({
      label: sym.id,
      kind: CompletionItemKind.Reference,
      detail: sym.qualifiedName,
    });
  }
  return out;
}

// =============================================================================
// Suggestion builders
// =============================================================================

function topLevelSuggestions(
  workspace: Workspace,
  symbols: SymbolTable
): CompletionItem[] {
  const out: CompletionItem[] = [];
  // Declaration keywords rank ahead of kinds; `type` leads (the one you reach
  // for to define a vocabulary). Within the primary bucket the COMMON decl
  // keywords (`process`/`view`/`use`/`subprocess`) sub-rank ahead of the RARE /
  // advanced ones (`export`/`in`/`on`) — a secondary sort key, not a new scheme
  // (P2.1). The first `type` tab-stop is a choice enum of valid parent BASE
  // kinds (process/view aren't valid parents, §grammar — P3.6).
  const COMMON = "0";
  const RARE = "1";
  // Scaffolds for id-BEARING declarations arrive with a freshly minted stable
  // id baked in (BEST-PRACTICES: ids are meaningless + permanent, tooling
  // mints them) — the author never hand-invents one and never depends on a
  // later save-time mint. One lazy minter per request keeps same-list ids
  // mutually distinct and collision-checked against the workspace.
  const mint = makeIdMint(() => workspace.model());
  out.push(
    rerank(snippetKw("type", `type #${mint()} \${1|module,interface,surface|} \${2:Name}`), `${RANK_FLOW}type`)
  );
  for (const [label, insert] of [
    ["process", `process #${mint()} \${1:Name} {\n\t$0\n}`],
    ["view", `view #${mint()} \${1:Name} {\n\t$0\n}`],
    ["use", "use ${1:Type} from ${2:package}"],
    ["subprocess", `subprocess #${mint()} \${1:Name} {\n\t$0\n}`],
  ] as const) {
    out.push(subRanked(snippetKw(label, insert), RANK_PRIMARY, COMMON));
  }
  // `in Target { … }` places declarations into an existing container. It is a
  // legal root declaration (GRAMMAR.md Top-Level); the bare one-liner form
  // (`in p …`) is a parse error without a following decl/block, so scaffold the
  // block form. Rare/advanced → the RARE sub-bucket.
  out.push(subRanked(snippetKw("in", "in ${1:Target} {\n\t$0\n}"), RANK_PRIMARY, RARE));
  // `decoration Name { … }` (v0.14) — defines an annotation kind. Rare/advanced:
  // most users APPLY decorations far more often than they define one.
  out.push(
    subRanked(
      snippetKw(
        "decoration",
        "decoration ${1:name} {\n\ttargets: ${2:module}\n\t${3:field}: ${4|marker,text,number,bool,ref|}\n}",
      ),
      RANK_PRIMARY,
      RARE,
    ),
  );
  // `test "name" of P { when … then … }` (v0.11) — a process test. Rare/advanced.
  out.push(
    subRanked(
      snippetKw("test", `test #${mint()} "\${1:name}" of \${2:Process} {\n\twhen {\n\t\t$3\n\t}\n\tthen {\n\t\t$0\n\t}\n}`),
      RANK_PRIMARY,
      RARE
    )
  );
  // `on plane { … }` births declarations on a plane (§1.2 plane presence). Its
  // doc is distinct from KEYWORD_DOCS.on, which documents the process-scope `on`
  // (saga `reversible on error` trigger / `subprocess X on Caller` owner) — a
  // different construct the root snippet does not insert.
  const onItem = subRanked(snippetKw("on", "on ${1:plane} {\n\t$0\n}"), RANK_PRIMARY, RARE);
  onItem.documentation = {
    kind: "markdown",
    value:
      "Declare a plane's **presence**: `on <plane> { … }` births the block's " +
      "declarations on that plane (GRAMMAR.md Top-Level, §1.2 planes).",
  };
  out.push(onItem);
  out.push(subRanked(kw("export", CompletionItemKind.Keyword), RANK_PRIMARY, RARE));
  for (const [name, kind] of workspace.model().registry.kinds) {
    // `process`/`view` are offered above as snippet keywords — don't re-emit
    // them as base kinds (the duplicate-listing bug).
    if (kind.baseType === "process" || kind.baseType === "view") continue;
    const item = typeCompletion(name, kind.baseType, true, requiredFieldPaths(kind), kindDoc(kind), mint);
    // Module-rooted kinds lead interface-rooted ones — a bare root interface is
    // rare, so 40 interface kinds must not drown `module`/`surface`/`service`
    // (P2.1). A container-first secondary key inside the kind (symbol) bucket.
    item.sortText = `${RANK_SYMBOL}${baseKindOrder(kind.baseType)}${name.toLowerCase()}`;
    out.push(item);
  }
  // Declared + built-in decorations, offered with the `@` already typed so the
  // completion is usable from a bare declaration position too.
  out.push(...decorationItems(workspace));
  // Declared modules / actors as bare-step callers — the process-first draft
  // floor (`Orders > …` is a legal root declaration, §4.13.1). Ranked below the
  // kinds (a new declaration is the more common root intent) but above ghosts.
  // Legality gate: a surface is NOT a legal caller (PROCESS_CALLER_TODO), and a
  // top-level bare step must begin with an IDENTIFIER token — so a module whose
  // name lexes as a keyword (`join`) can never head a root step and is dropped.
  for (const sym of symbols.declared()) {
    if (sym.kind !== "module") continue;
    if (!nameLexesAsIdentifier(sym.qualifiedName)) continue;
    out.push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_CALLER));
  }
  return out;
}

/** Whether `name` lexes as a leading IDENTIFIER token — i.e. it can legally
 *  head a bare process step (`<identifier> > …`). A keyword-as-name module
 *  (`join`, `select`) lexes to its keyword token and cannot. */
function nameLexesAsIdentifier(name: string): boolean {
  const toks = tokenize(name).tokens;
  return toks.length > 0 && toks[0]!.type === "IDENTIFIER";
}

/** Overwrite an item's sortText with an explicit rank. */
function rerank<T extends CompletionItem>(item: T, sortText: string): T {
  item.sortText = sortText;
  return item;
}

function moduleBodySuggestions(
  workspace: Workspace,
  ownerTypeName: string | undefined,
  kindScope: "module" | "surface",
  onBlock?: boolean,
  symbols?: SymbolTable,
  scopeQualifiedName?: string
): CompletionItem[] {
  const out: CompletionItem[] = [];
  // Fresh stable ids for the id-bearing scaffolds below (same lazy-per-request
  // minter as topLevelSuggestions).
  const mint = makeIdMint(() => workspace.model());
  // Stub promotion (flagship UX): this module is referenced in a flow with
  // interfaces it doesn't declare — offer those ghost interfaces as declarations,
  // top-ranked. The one moment the model knows exactly what belongs in the body
  // (`ghostPaths().interfaces`, §4.13).
  if (scopeQualifiedName && kindScope === "module") {
    out.push(...stubPromotionCompletions(workspace, scopeQualifiedName));
  }
  // A top-level `on <plane> { … }` block ALSO admits bare module inclusion refs
  // (add the plane to an existing module) — offer declared module names on top
  // of the normal declaration set (GRAMMAR.md `on_decl`).
  if (onBlock && symbols) {
    for (const sym of symbols.declared()) {
      if (sym.kind !== "module") continue;
      out.push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_SYMBOL));
    }
  }
  // `aspect` is the lead keyword of a module/surface body — float it to the top.
  out.push(kwRanked("aspect", RANK_FLOW));
  // A blessed description scaffold (P3.5), ranked below aspect + the template
  // fields but above the structural tail — a pass-1 author reaches for it early.
  out.push(descriptionScaffoldItem(`${RANK_PRIMARY}1description`));
  // Pass-1 authoring order (P2.2): template FIELDS and interface KINDS rank above
  // the structural-keyword tail (`required`/`cascade`/`override`/…). A module body
  // hosts nested processes / subprocesses; a surface does not. All secondary keys
  // inside the primary bucket — a re-ordering, not new items.
  if (kindScope === "module") {
    out.push(subRanked(kw("process", CompletionItemKind.Keyword), RANK_PRIMARY, "4"));
    out.push(subRanked(kw("subprocess", CompletionItemKind.Keyword), RANK_PRIMARY, "4"));
    // A `test` is legal in a module body (v0.11) — below process/subprocess.
    out.push(
      subRanked(
        snippetKw("test", `test #${mint()} "\${1:name}" of \${2:Process} {\n\twhen {\n\t\t$3\n\t}\n\tthen {\n\t\t$0\n\t}\n}`),
        RANK_PRIMARY,
        "4a"
      )
    );
  }
  // The structural body modifiers — the tail, below fields + interface kinds.
  for (const k of BODY_KEYWORDS) {
    if (k === "aspect") continue;
    out.push(subRanked(kw(k, CompletionItemKind.Keyword), RANK_PRIMARY, "5"));
  }
  // Nested kinds valid in this body: a module nests modules + surfaces +
  // interfaces; a surface nests only surfaces + interfaces (spec §4.2 — surfaces
  // group interfaces and cannot contain modules). An interface kind is the common
  // fill in a module body, so it sub-ranks INTO the primary bucket ahead of the
  // structural tail; module/surface kinds sink to the symbol tier below (P2.2).
  for (const [name, kind] of workspace.model().registry.kinds) {
    const base = kind.baseType;
    if (base === "process" || base === "view") continue;
    if (kindScope === "surface" && base === "module") continue;
    const item = typeCompletion(name, base, true, requiredFieldPaths(kind), kindDoc(kind), mint);
    item.sortText =
      base === "interface"
        ? `${RANK_PRIMARY}2${name.toLowerCase()}`
        : `${RANK_SYMBOL}${baseKindOrder(base)}${name.toLowerCase()}`;
    out.push(item);
  }
  // Template fields from the owner kind — the primary things an instance fills.
  // Hand-authored scalar fields (`team`) rank at the TOP of the primary bucket
  // (below aspect); cosmetic cascade-GROUP config (the builtin `widget.*`
  // element bundle — icon/color/dimensions, rarely hand-edited) sinks below the
  // description + interface kinds, so pass-1 authoring isn't buried under it.
  if (ownerTypeName) {
    const kind = workspace.model().registry.kinds.get(ownerTypeName);
    if (kind) {
      // Group roots carry `group`; their leaves (`widget.icon`) don't, but share
      // the root's first path segment — down-rank the whole family.
      const groupRoots = new Set(
        kind.stampedBody.fields.filter((f) => f.group).map((f) => f.path[0])
      );
      for (const f of kind.stampedBody.fields) {
        const path = f.path.join(".");
        const isGroupField = f.group === true || groupRoots.has(f.path[0]);
        out.push(
          subRanked(
            {
              label: path,
              kind: CompletionItemKind.Field,
              detail: f.required ? "required field" : "field",
              insertText: `${path}: `,
            },
            RANK_PRIMARY,
            isGroupField ? "3" : "0"
          )
        );
      }
    }
  }
  return out;
}

/**
 * Inside an interface body. The grammar (parseInterfaceBody) accepts only
 * description, `labels`, `drop`, the field modifiers (`required`/`cascade`/
 * `append`), and field assignments — NOT `override` and no sub-declarations.
 */
function interfaceBodySuggestions(): CompletionItem[] {
  return [
    kwRanked("aspect", RANK_FLOW),
    kwRanked("required", RANK_PRIMARY),
    kwRanked("cascade", RANK_PRIMARY),
    kwRanked("append", RANK_PRIMARY),
    kwRanked("drop", RANK_PRIMARY),
  ];
}

function processBodySuggestions(
  workspace: Workspace,
  symbols: SymbolTable,
  proc?: ProcCtx
): CompletionItem[] {
  const out: CompletionItem[] = [];
  // A one-shot scaffold for the most common line in a process body.
  out.push(snippetItem("step", "${1:Caller} > ${2:Callee}", "process step"));
  // Control-flow keywords rank below declared callers but ABOVE ghost modules —
  // the grammar word you need outranks a draft you only referenced.
  for (const k of PROCESS_STMT_KEYWORDS) {
    out.push(kwRanked(k, RANK_SYMBOL));
  }
  // Slot-locked keywords, offered only where the grammar admits them.
  if (proc?.unwindLegal) out.push(kwRanked("unwind", RANK_SYMBOL));
  if (proc?.branchLegal) out.push(kwRanked("branch", RANK_SYMBOL));
  if (proc?.elseLegal) out.push(kwRanked("else", RANK_SYMBOL));
  // `catch` continues a `try` — offered only right after a `try` block/one-liner.
  if (proc?.catchLegal) out.push(kwRanked("catch", RANK_SYMBOL));
  // Header zone (before the first action): process field keys are legal here —
  // and only here (PROCESS_FIELD_AFTER_ACTION suppresses them after an action).
  // A blessed description scaffold (P3.5) rides along, ranked modestly so it never
  // crowds the step/keyword lead.
  if (proc?.headerZone) {
    out.push(descriptionScaffoldItem(`${RANK_KEYWORD}0description`));
    for (const key of collectProcessFieldKeys(workspace.model())) {
      out.push(
        ranked(
          {
            label: key,
            kind: CompletionItemKind.Field,
            detail: "process field",
            insertText: `${key}: `,
          },
          RANK_PRIMARY
        )
      );
    }
  }
  // Callers: declared modules/actors first, then ghost modules a flow already
  // names but hasn't defined. A surface is NOT a legal caller
  // (PROCESS_CALLER_TODO) — it appears after `>` as a callee, never here.
  for (const sym of symbols.declared()) {
    if (sym.kind !== "module") continue;
    out.push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_PRIMARY));
  }
  out.push(...ghostModuleCompletions(workspace));
  return out;
}

function viewBodySuggestions(
  workspace: Workspace,
  symbols: SymbolTable
): CompletionItem[] {
  const out: CompletionItem[] = [];
  // View keywords are what a view body is for — rank above the module names.
  for (const k of VIEW_BODY_KEYWORDS) {
    out.push(kwRanked(k, RANK_PRIMARY));
  }
  for (const sym of symbols.declared()) {
    if (sym.kind !== "module" && sym.kind !== "surface") continue;
    out.push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_SYMBOL));
  }
  return out;
}

/** Inside a `test { … }` body (v0.11): the two blocks it holds. `when` pins the
 *  nondeterminism, `then` asserts the trace. */
function testBodySuggestions(): CompletionItem[] {
  return [
    subRanked(snippetKw("when", "when {\n\t$0\n}"), RANK_PRIMARY, "0"),
    subRanked(snippetKw("then", "then {\n\t$0\n}"), RANK_PRIMARY, "0"),
  ];
}

/** Inside a `when { … }` block: the pin-VALUE answer words (the pin KEY is a
 *  choice reference the author types; the answer is the vocabulary). */
function whenBodySuggestions(): CompletionItem[] {
  return WHEN_PIN_KEYWORDS.map((k) => kwRanked(k, RANK_PRIMARY));
}

/** Inside a `then { … }` block: the modal + terminator + budget assertion words. */
function thenBodySuggestions(): CompletionItem[] {
  const out = THEN_ASSERTION_KEYWORDS.map((k) => kwRanked(k, RANK_PRIMARY));
  out.push(subRanked(snippetKw("parallel", "parallel {\n\t$0\n}"), RANK_PRIMARY, "1"));
  return out;
}

function aspectBodySuggestions(
  workspace: Workspace,
  existingKeys?: ReadonlySet<string>
): CompletionItem[] {
  const out: CompletionItem[] = [];
  for (const k of LABELS_BODY_KEYWORDS) {
    out.push(kwRanked(k, RANK_PRIMARY));
  }
  out.push(...aspectKeyCompletions(workspace, true, existingKeys));
  return out;
}

/**
 * Distinct label keys reused across the workspace. `assign` inserts `key: `
 * (declaring a label) vs the bare key (`group by <key>`).
 */
function aspectKeyCompletions(
  workspace: Workspace,
  assign: boolean,
  existingKeys?: ReadonlySet<string>
): CompletionItem[] {
  const out: CompletionItem[] = [];
  const seen = new Set<string>();
  const offer = (key: string, reused: boolean): void => {
    if (seen.has(key)) return;
    seen.add(key);
    // Don't re-offer a key already present in the current labels block.
    if (existingKeys?.has(key)) return;
    // A language pack's doc for the key gives it a real detail + hover body
    // (lazily rendered via `resolveCompletion` from `data.doc`).
    const doc = aspectDocFor(key);
    out.push(
      ranked(
        {
          label: key,
          kind: CompletionItemKind.Field,
          detail: doc ? doc.summary : "label",
          insertText: assign ? `${key}: ` : key,
          // `reused` marks a model-derived echo: the tolerant parser turns the
          // half-typed key (`aspect d█`) into a real aspect key, so it re-appears
          // in `collectAspectKeys`. The self-echo filter drops it when it equals
          // the typed run (same treatment as a ghost echo, P1.1).
          data: { reused, ...(doc ? { doc: doc.detail ?? doc.summary } : {}) },
        },
        RANK_PRIMARY
      )
    );
  };
  for (const key of collectAspectKeys(workspace.model())) offer(key, true);
  // Also surface keys a language pack documents but the model doesn't yet carry,
  // so the convention is discoverable before it's first used.
  for (const doc of aspectDocs()) offer(doc.key, false);
  return out;
}

/**
 * Members importable via `use … from …` (before the source package is named):
 * every type exported by a loaded dependency, completing the whole clause
 * (`service from arch.cloud.aws`), plus `*` for a wildcard import.
 */
function suggestUseMembers(
  workspace: Workspace,
  stdlibExports: readonly StdlibExport[] = []
): CompletionItem[] {
  const out: CompletionItem[] = [];
  const seen = new Set<string>();
  for (const { name, pkg } of workspace.exportedTypes()) {
    const key = `${name}|${pkg}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(
      ranked(
        {
          label: name,
          kind: CompletionItemKind.Class,
          detail: `from ${pkg}`,
          insertText: `${name} from ${pkg}`,
        },
        RANK_SYMBOL
      )
    );
  }
  // Whole-package `use █` (no prefix) offers the stdlib catalogue too — but ONLY
  // when the prefix is empty here would flood the list, so the prefix-gated
  // catalogue is added by `stdlibUseCompletions` in `provideCompletion`. This
  // path just marks `*` present (demoted below every concrete target —
  // BEST-PRACTICES treats `use *` from the stdlib as an anti-pattern).
  void stdlibExports;
  out.push(rerank(snippetItem("*", "* from ${1:package}", "import everything"), `${RANK_KEYWORD}*`));
  return out;
}

/**
 * Stdlib export catalogue as whole-clause `use` targets (P1.3): `use serv█` →
 * `service from arch.backend`. Mirrors auto-import — the same index, prefix-
 * gated so a bare `use █` doesn't dump the whole stdlib — but inserts the full
 * `<Type> from <pkg>` clause (no `additionalTextEdits`, the `use` line IS the
 * edit). Deduped against already-loaded dependency exports.
 */
function stdlibUseCompletions(
  file: FileState,
  workspace: Workspace,
  stdlibExports: readonly StdlibExport[],
  offset: number
): CompletionItem[] {
  const word = typedPrefixAt(file.text, offset);
  if (word.length === 0) return [];
  const wl = word.toLowerCase();
  const imported = importedUseKeys(file.file.uses);
  const depPairs = new Set(workspace.exportedTypes().map((t) => `${t.name}|${t.pkg}`));
  const out: CompletionItem[] = [];
  const seen = new Set<string>();
  for (const ex of stdlibExports) {
    if (!ex.kindName.toLowerCase().startsWith(wl)) continue;
    if (imported.wildcardPkgs.has(ex.packageName)) continue;
    const key = `${ex.kindName}|${ex.packageName}`;
    if (imported.pairs.has(key) || depPairs.has(key) || seen.has(key)) continue;
    seen.add(key);
    // Same tie-break as auto-import (P3.1): an exact name-match leads, then a
    // CORE package (`arch.backend`), so `use serv█` surfaces `service` (backend)
    // above `server`/`serverless_function` from peripheral packages.
    const sub =
      ex.kindName.toLowerCase() === wl ? "0" : CORE_STDLIB_PACKAGES.has(ex.packageName) ? "1" : "2";
    const item = subRanked(
      {
        label: ex.kindName,
        kind: baseKindToCompletionKind(ex.rootBase),
        detail: `from ${ex.packageName}`,
        labelDetails: { description: ex.packageName },
        insertText: `${ex.kindName} from ${ex.packageName}`,
        data: { doc: ex.doc },
      },
      RANK_SYMBOL,
      sub
    );
    out.push(item);
    if (out.length >= AUTO_IMPORT_MAX) break;
  }
  return out;
}

/**
 * Suggestions inside a field body — `widget: arch-module { … }` or
 * `cascade * foo: bar { … }`. Members carry the parent's path as a
 * prefix (`widget.icon: x`, not `icon: x`), but inside the body the
 * author writes the bare leaf name. Surface:
 *   - modifier keywords (`required`, `cascade`, `append`)
 *   - known leaf names cascaded for the prefix in scope (best-effort
 *     lookup across the workspace's resolved types)
 */
function fieldBodySuggestions(
  workspace: Workspace,
  ctx: AnalyzedContext,
): CompletionItem[] {
  const out: CompletionItem[] = [];
  // Field-body members may carry the usual modifiers EXCEPT
  // explicit `cascade` inside a `cascade *` group body. We can't
  // cheaply tell from here whether the body is a group body, so
  // just offer the supersets — both are syntactically grammatical
  // and the parser will diagnose redundancy.
  for (const k of ["required", "cascade", "append"] as const) {
    out.push(kw(k, CompletionItemKind.Keyword));
  }
  // Cross-workspace sub-field name suggestions: walk every kind's
  // stamped body for fields under the same prefix and offer the
  // leaf names. Useful when the author is filling in a body
  // declared elsewhere (`widget.icon`, `widget.color`, …).
  const groupPrefix = ctx.fieldPrefix;
  const seen = new Set<string>();
  if (groupPrefix && groupPrefix.length > 0) {
    for (const [, kind] of workspace.model().registry.kinds) {
      for (const f of kind.stampedBody.fields) {
        if (f.path.length <= groupPrefix.length) continue;
        let matches = true;
        for (let i = 0; i < groupPrefix.length; i++) {
          if (f.path[i] !== groupPrefix[i]) { matches = false; break; }
        }
        if (!matches) continue;
        const leaf = f.path.slice(groupPrefix.length).join(".");
        if (seen.has(leaf)) continue;
        seen.add(leaf);
        out.push({
          label: leaf,
          kind: CompletionItemKind.Field,
          detail: `member of ${groupPrefix.join(".")}`,
          insertText: `${leaf}: `,
        });
      }
    }
  }
  return out;
}

function typeBodySuggestions(
  workspace: Workspace,
  ownerTypeName?: string
): CompletionItem[] {
  const out: CompletionItem[] = [];
  for (const k of TYPE_BODY_KEYWORDS) {
    out.push(kw(k, CompletionItemKind.Keyword));
  }
  for (const [name, kind] of workspace.model().registry.kinds) {
    if (kind.baseType === "process" || kind.baseType === "view") continue;
    out.push(typeCompletion(name, kind.baseType, true, requiredFieldPaths(kind), kindDoc(kind)));
  }
  if (ownerTypeName) {
    const parentType = workspace.model().registry.kinds.get(ownerTypeName);
    if (parentType) {
      for (const f of parentType.stampedBody.fields) {
        const path = f.path.join(".");
        out.push({
          label: path,
          kind: CompletionItemKind.Field,
          detail: "inherited field",
          insertText: `${path}: `,
        });
      }
    }
  }
  return out;
}

function suggestDottedChildren(
  workspace: Workspace,
  symbols: SymbolTable,
  path: readonly string[],
  uri?: string
): CompletionItem[] | null {
  if (path.length === 0) return null;
  const ownerKey = path.join(".");
  // v0.8.2 hoisting: the owner may be written SHORT (`Auth.` for `Platform.Auth`).
  // Resolve it and key children/ghosts by its FULL qualified name, else the
  // lookup misses and no members are offered. Referrer-aware (v0.13): a twin
  // owner name resolves to THIS file's space's module.
  const ownerSym = symbols.resolve(ownerKey, uri);
  const ownerKnown = ownerSym !== undefined;
  const ownerFqn = ownerSym?.qualifiedName ?? ownerKey;
  const out: CompletionItem[] = [];

  // Declared members (interfaces, surfaces, sub-modules) — insert the leaf,
  // since the editor already has `Owner.` before the cursor.
  if (ownerKnown) {
    for (const c of symbols.childrenOf(ownerFqn)) {
      // Synthesized children (a stub interface, the (anonymous) handler) come
      // via the ghost path below — ranked as drafts, sentinel filtered — so
      // skip them here to avoid double-listing / leaking the sentinel.
      if (c.synthesized) continue;
      out.push(symbolCompletion(workspace, c, undefined, RANK_PRIMARY));
    }
  }

  // Ghost interfaces the owner is referenced with but doesn't declare. Works
  // whether the owner itself is declared or a ghost (`GhostMod.<iface>`).
  const prefix = `${ownerFqn}.`;
  for (const ip of workspace.ghostPaths().interfaces) {
    if (!ip.startsWith(prefix)) continue;
    if (ip.endsWith(`.${ANONYMOUS_INTERFACE_NAME}`)) continue;
    const leaf = ip.slice(prefix.length);
    if (leaf.includes(".")) continue; // direct members only
    out.push(ghostItem(ip, "interface", CompletionItemKind.Method, leaf));
  }

  // Neither a known owner nor any ghost member → not a member-completion site.
  if (!ownerKnown && out.length === 0) return null;
  return out;
}

function suggestProcessCalleesIfInProcess(
  _ctx: AnalyzedContext,
  _symbols: SymbolTable
): null {
  return null;
}

function suggestProcessCallees(
  workspace: Workspace,
  symbols: SymbolTable,
  flowModules?: ReadonlySet<string>
): CompletionItem[] {
  // Proximity: an interface whose owner is a module already named in THIS flow
  // is the likeliest next callee — rank it ahead of every other interface.
  const inFlow = (ownerName?: string): boolean =>
    !!flowModules && !!ownerName && flowModules.has(ownerName.split(".")[0]!);
  const out: CompletionItem[] = [];
  for (const sym of symbols.declared()) {
    // A process callee may name a declared interface OR a module / surface —
    // a module callee stands in with a synthesized anonymous handler interface
    // until an interface is specified (§4.13.2). An interface is the most
    // precise callee, so it ranks above a bare module.
    if (sym.kind === "interface") {
      const rank = inFlow(sym.ownerName) ? RANK_FLOW : RANK_PRIMARY;
      out.push(symbolCompletion(workspace, sym, sym.qualifiedName, rank));
    } else if (sym.kind === "module" || sym.kind === "surface") {
      out.push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_SYMBOL));
    }
  }
  // Ghost interfaces/modules the flow already references but hasn't defined.
  out.push(...ghostInterfaceCompletions(workspace));
  out.push(...ghostModuleCompletions(workspace));
  return out;
}

function suggestModuleNames(
  workspace: Workspace,
  symbols: SymbolTable
): CompletionItem[] {
  const out: CompletionItem[] = [];
  // Declared hosts only — NOT ghosts. A missing `in` target is a hard
  // IN_TARGET_NOT_FOUND (spec §4.16), so proposing an undeclared module here
  // would push the author straight into an error.
  for (const sym of symbols.declared()) {
    if (sym.kind !== "module" && sym.kind !== "surface") continue;
    out.push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_PRIMARY));
  }
  return out;
}

/**
 * After a loop `in` (`each item in █`). The collection head is FREE-FORM
 * (GRAMMAR each_head — rendered, never resolved or validated), so a ghost a
 * flow already references is a legitimate collection: offer declared hosts AND
 * ghost modules. (Contrast `suggestModuleNames` for a placement `in`, where a
 * missing target is a hard IN_TARGET_NOT_FOUND and ghosts are excluded.)
 */
function suggestEachCollection(
  workspace: Workspace,
  symbols: SymbolTable
): CompletionItem[] {
  const out = suggestModuleNames(workspace, symbols);
  out.push(...ghostModuleCompletions(workspace));
  return out;
}

// =============================================================================
// Process-language structural slots (P1a)
// =============================================================================

/**
 * After `do`: subprocess names (the target resolves subprocess-first,
 * PROCESS.md §7), then process names ranked below. A process shadowed by a
 * same-named subprocess is annotated. No ghost channel exists for subprocesses,
 * so only declared targets are offered.
 */
function suggestAfterDo(workspace: Workspace, symbols: SymbolTable): CompletionItem[] {
  const out: CompletionItem[] = [];
  const subNames = new Set<string>();
  for (const sym of symbols.declared()) {
    if (sym.kind !== "subprocess") continue;
    subNames.add(sym.name);
    out.push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_PRIMARY));
  }
  for (const sym of symbols.declared()) {
    if (sym.kind !== "process") continue;
    const item = symbolCompletion(workspace, sym, sym.qualifiedName, RANK_SYMBOL);
    if (subNames.has(sym.name)) {
      item.detail = `${item.detail ?? "process"} — shadowed by a same-named subprocess (do resolves subprocess-first)`;
    }
    out.push(item);
  }
  return out;
}

/**
 * After `go`: the `as` re-entry labels of the ENCLOSING process only. Reuses the
 * complete re-entry registry (incl. `unwind … as` labels), scoped to the
 * process body's byte span. PROCESS.md §7.
 */
function suggestAfterGo(
  workspace: Workspace,
  uri: string,
  enclosingProc:
    | {
        readonly startOffset: number;
        readonly endOffset: number;
        readonly exclude: readonly { readonly start: number; readonly end: number }[];
      }
    | undefined
): CompletionItem[] {
  if (!enclosingProc) return [];
  const out: CompletionItem[] = [];
  const seen = new Set<string>();
  forEachReentryOccurrence(workspace, (occ) => {
    if (occ.role !== "as" || occ.uri !== uri) return;
    if (
      occ.span.start.offset < enclosingProc.startOffset ||
      occ.span.end.offset > enclosingProc.endOffset
    ) {
      return;
    }
    // A label inside a nested subprocess body is that subprocess's own re-entry
    // namespace, not this process's — exclude it.
    if (
      enclosingProc.exclude.some(
        (ex) => occ.span.start.offset >= ex.start && occ.span.end.offset <= ex.end
      )
    ) {
      return;
    }
    if (seen.has(occ.label)) return;
    seen.add(occ.label);
    out.push(
      ranked(
        { label: occ.label, kind: CompletionItemKind.EnumMember, detail: "re-entry label (`as`)" },
        RANK_PRIMARY
      )
    );
  });
  return out;
}

const PREV_STEP_WARNING =
  "⚠ This is the **previous** step's callee. A step's `unwind` compensates the " +
  "SAME step — target the forward step's own callee (`reserve → release`), not the " +
  "prior one. A failed step didn't complete, and the engine's rethrow already fires " +
  "earlier steps' `unwind`s in reverse (PROCESS.md §8).";

/**
 * After `unwind >`: the forward step's callee-module interfaces first
 * (compensation targets the SAME callee: `reserve → release`, PROCESS.md §8).
 * Interfaces of the PREVIOUS step's callee are explicitly down-ranked + warned
 * (the #1 saga-modeling mistake).
 */
function suggestUnwindCallee(
  workspace: Workspace,
  symbols: SymbolTable,
  info: { forwardModule?: string; forwardInterface?: string; previousModule?: string } | undefined,
  flowModules?: ReadonlySet<string>
): CompletionItem[] {
  const fwd = info?.forwardModule;
  const fwdIface = info?.forwardInterface;
  const prev = info?.previousModule;
  const inFlow = (ownerName?: string): boolean =>
    !!flowModules && !!ownerName && flowModules.has(ownerName.split(".")[0]!);
  const out: CompletionItem[] = [];
  for (const sym of symbols.declared()) {
    if (sym.kind === "interface") {
      // The exact forward interface is the step compensating ITSELF — nonsense
      // (P2.5). Keep it offerable but hard-sink it below every real compensation.
      if (fwdIface && sym.qualifiedName === fwdIface) {
        const item = symbolCompletion(workspace, sym, sym.qualifiedName, RANK_BOUNDARY);
        item.detail = `${item.detail ?? "interface"} — the forward step itself; a step never compensates itself`;
        item.data = undefined; // keep the note; don't let resolveCompletion overwrite it
        out.push(item);
        continue;
      }
      const top = sym.ownerName?.split(".")[0];
      if (fwd && top === fwd) {
        out.push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_FLOW));
      } else if (prev && prev !== fwd && top === prev) {
        const item = symbolCompletion(workspace, sym, sym.qualifiedName, RANK_GHOST);
        item.detail = `${item.detail ?? "interface"} — ⚠ the PREVIOUS step's callee`;
        item.documentation = { kind: "markdown", value: PREV_STEP_WARNING };
        item.data = undefined; // don't let resolveCompletion overwrite the warning
        out.push(item);
      } else {
        out.push(
          symbolCompletion(workspace, sym, sym.qualifiedName, inFlow(sym.ownerName) ? RANK_PRIMARY : RANK_SYMBOL)
        );
      }
    } else if (sym.kind === "module" || sym.kind === "surface") {
      out.push(
        symbolCompletion(workspace, sym, sym.qualifiedName, fwd && sym.name === fwd ? RANK_FLOW : RANK_SYMBOL)
      );
    }
  }
  out.push(...ghostInterfaceCompletions(workspace));
  out.push(...ghostModuleCompletions(workspace));
  return out;
}

/** After `parallel`: the merge-clause keywords (`join`/`race`/`out`) and the
 *  compositions that may follow (`await`/`select`/`each`). */
function suggestAfterParallel(): CompletionItem[] {
  return [
    kwRanked("join", RANK_PRIMARY),
    kwRanked("race", RANK_PRIMARY),
    kwRanked("out", RANK_PRIMARY),
    kwRanked("await", RANK_SYMBOL),
    kwRanked("select", RANK_SYMBOL),
    kwRanked("each", RANK_SYMBOL),
  ];
}

/** After `select`: the `one` count keyword (`select one` = first match). The
 *  numeric count and the head are free-form. */
function suggestAfterSelect(): CompletionItem[] {
  return [kwRanked("one", RANK_PRIMARY)];
}

/**
 * The `await` head is FREE-FORM (rendered, never validated) — a duration, a
 * cron, or a condition. Offer a snippet template for each, plus the wait-or-
 * timeout race idiom (PROCESS.md §6). Never interfaces/modules — a wait-for-
 * event is an ordinary inbound step, not an await target.
 */
function suggestAwaitHead(): CompletionItem[] {
  const timeoutRace =
    "For a **wait-or-timeout**, race an inbound step against this timer " +
    "(PROCESS.md §6):\n\n```archlang\nparallel race {\n  branch: Them > Us.event\n  branch {\n    await 1 day\n    fail \"timeout\"\n  }\n}\n```";
  const items: CompletionItem[] = [
    snippetItem("duration", "${1:1} ${2|min,hour,day|}", "wait a duration"),
    snippetItem("cron", '"${1:0 3 1 * *}"', "wait for a cron time"),
    snippetItem("condition", '"${1:inventory < 10}"', "wait for a condition"),
    snippetItem("wait-or-timeout", "${1:1} ${2|min,hour,day|}", "timer for a wait-or-timeout race (§6)"),
  ];
  items[items.length - 1]!.documentation = { kind: "markdown", value: timeoutRace };
  return items;
}

/**
 * A root/module `on <plane-list>` slot: plane names = `primary` ∪ every plane in
 * the model (born-on / alsoOn / partialOn / inclusions) ∪ aspect keys (planes
 * are open-world; an aspect key doubles as a plane). Never ghosts.
 */
function suggestOnPlane(workspace: Workspace): CompletionItem[] {
  const out: CompletionItem[] = [];
  const seen = new Set<string>();
  const add = (name: string, detail: string): void => {
    if (seen.has(name)) return;
    seen.add(name);
    out.push(ranked({ label: name, kind: CompletionItemKind.EnumMember, detail }, RANK_PRIMARY));
  };
  add(PRIMARY_PLANE, "the primary plane");
  for (const p of workspace.model().presence?.planes ?? []) add(p, "plane");
  for (const k of collectAspectKeys(workspace.model())) add(k, "plane / aspect key");
  return out;
}

/** A `reversible on <trigger>` slot: the trigger is `error` (a failure inside
 *  the block). Never plane names. */
function suggestOnTrigger(): CompletionItem[] {
  return [
    ranked(
      {
        label: "error",
        kind: CompletionItemKind.EnumMember,
        detail: "saga trigger — roll back on a failure inside the block",
      },
      RANK_PRIMARY
    ),
  ];
}

/** Owner/caller position (`dist <owner>` / `| <owner>`): declared modules /
 *  actors, plus ghost modules a flow already implies. A surface is never a
 *  caller (PROCESS_CALLER_TODO). */
function suggestOwnerNames(workspace: Workspace, symbols: SymbolTable): CompletionItem[] {
  const out: CompletionItem[] = [];
  for (const sym of symbols.declared()) {
    if (sym.kind !== "module") continue;
    out.push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_PRIMARY));
  }
  out.push(...ghostModuleCompletions(workspace));
  return out;
}

/**
 * After the snake glyph (`\ █`, §4.13.4): the glyph continues the previous
 * sibling's TAIL and is call-only — a note / `{` / `do` / `go` / control keyword
 * / terminator after it is `parse.snakeRequiresCall`. So the slot offers exactly
 * one continuation, the call arrow (the callee then completes via `after-arrow`).
 */
function suggestAfterSnake(): CompletionItem[] {
  return [snippetItem(">", "> ${1:Callee}", "continue the run (snake call)")];
}

/** Owned control-flow keywords legal after an owner leads a process statement
 *  (`Orders <kw> …`). Frame-legal in any sub-block; the slot-locked step words
 *  (`unwind`/`branch`) never take an owner prefix, so they are absent by design. */
const OWNED_CONTROL_KEYWORDS = [
  "if",
  "each",
  "select",
  "try",
  "parallel",
  "reversible",
  "await",
] as const;

/**
 * After-owner slot (P1.2): the statement leads with a lone owner identifier
 * (`Orders █` / `Orders i█`). When it names a real caller — a declared
 * module/actor, a ghost caller already in the flow, or a ghost module — offer
 * the owned control-flow keyword set plus a `> ` step-arrow continuation. When
 * it resolves to nothing plausible, fall back to the bare-name (ghost) list so
 * the slot is never worse than before.
 */
function suggestAfterOwner(
  workspace: Workspace,
  symbols: SymbolTable,
  ownerName: string | undefined
): CompletionItem[] {
  // A control-block owner must be a real module/actor: a declared module, or a
  // ghost module (a caller referenced in a flow but not yet defined). It must
  // NOT be a bound step label (`as start`) or a loop variable (`each item`) —
  // those are in the flow's identifier set but are not valid owners, and
  // offering `if`/`each`/… after them yields invalid grammar. So resolve against
  // the symbol table / ghost MODULES only, never the raw flow-identifier set.
  const known =
    !!ownerName &&
    (symbols.resolve(ownerName)?.kind === "module" ||
      workspace.ghostPaths().modules.includes(ownerName));
  if (!known) return suggestNamesOnly(workspace);
  const out: CompletionItem[] = [];
  for (const k of OWNED_CONTROL_KEYWORDS) out.push(kwRanked(k, RANK_PRIMARY));
  // The plain call step — `Orders > <callee>` — the most common owned line.
  out.push(snippetItem(">", "> ${1:Callee}", "call a callee (step)"));
  return out;
}

/** A parallel boolean-merge operand: the block's branch names (`bool_atom`). */
function suggestMergeBranchNames(names: readonly string[] | undefined): CompletionItem[] {
  return (names ?? []).map((n) =>
    ranked({ label: n, kind: CompletionItemKind.EnumMember, detail: "parallel branch" }, RANK_PRIMARY)
  );
}

// =============================================================================
// Structure slots (P1b)
// =============================================================================

/**
 * An aspect membership / roster value (§4.4): declared MODULES (membership
 * candidates — bare refs are strict, NO ghosts / `ASPECT_BAD_REF`), plus, for a
 * classification value, the previously-used values on the key (never for a
 * roster — roster entries are module refs only), plus `as <instance>` once a
 * complete ref precedes the cursor.
 */
function suggestAspectMembership(
  workspace: Workspace,
  symbols: SymbolTable,
  key: string | undefined,
  withAs: boolean | undefined,
  roster: boolean
): CompletionItem[] {
  const out: CompletionItem[] = [];
  for (const sym of symbols.declared()) {
    if (sym.kind !== "module") continue;
    out.push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_PRIMARY));
  }
  // Classification reuse — the identifier/string values already used on this key
  // (a membership slot may also be a plain classification). A roster admits only
  // module refs, so no value reuse there.
  if (!roster && key) out.push(...aspectValueCompletions(workspace, key));
  if (withAs) out.push(snippetItem("as", "as ${1:instance}", "name this member's instance"));
  return out;
}

/**
 * The inherited entity names of the enclosing declaration's type (§4.8) — the
 * only legal `drop █` / `override <kind> █` targets. Uniform across classes:
 * fields, aspects, sub-modules, surfaces, interfaces, sub/processes. Reads the
 * type's `stampedBody` (no engine query). In a `labels` block, the drop targets
 * are inherited aspect keys.
 */
function suggestInheritedEntities(
  workspace: Workspace,
  scope: Scope,
  typeName: string | undefined
): CompletionItem[] {
  if (scope === "labels") return aspectKeyCompletions(workspace, false);
  if (!typeName) return [];
  const kind = workspace.model().registry.kinds.get(typeName);
  if (!kind) return [];
  const body = kind.stampedBody;
  const out: CompletionItem[] = [];
  const seen = new Set<string>();
  const add = (name: string | undefined, detail: string): void => {
    if (!name || seen.has(name)) return;
    seen.add(name);
    out.push(ranked({ label: name, kind: CompletionItemKind.Field, detail }, RANK_PRIMARY));
  };
  for (const f of body.fields) add(f.path.join("."), "inherited field");
  for (const a of body.aspects) if (!a.drop) add(a.path.join("."), "inherited aspect");
  for (const m of body.modules) add(m.name, "inherited module");
  for (const s of body.surfaces) add(s.name, "inherited surface");
  for (const i of body.interfaces) add(i.name, "inherited interface");
  for (const sp of body.subprocesses) add(sp.name, "inherited subprocess");
  for (const p of body.processes) add(p.name, "inherited process");
  return out;
}

/** After `override` (§4.8): `required` (keep the result blank) + the registered
 *  type names (the new, non-subtype kind the override switches to). */
function suggestOverrideType(workspace: Workspace): CompletionItem[] {
  return [kwRanked("required", RANK_PRIMARY), ...suggestExtendableTypes(workspace)];
}

const LAYOUT_ALGORITHMS = ["elk", "dagre", "force", "manual"] as const;

/** After view `layout`: the closed layout-algorithm enum (engine
 *  `LayoutAlgorithm`, GRAMMAR view_layout). */
function suggestLayoutAlgorithms(): CompletionItem[] {
  return LAYOUT_ALGORITHMS.map((a) =>
    ranked({ label: a, kind: CompletionItemKind.EnumMember, detail: "layout algorithm" }, RANK_PRIMARY)
  );
}

/**
 * View `include` / `exclude` glob patterns (GRAMMAR pattern_list): module-path
 * patterns only — never aspect keys. `quoted` wraps the insert in `"…"` (a
 * pre-quote `include █`); the in-string handler inserts bare inside the quotes.
 */
function suggestViewPatterns(
  workspace: Workspace,
  symbols: SymbolTable,
  quoted: boolean
): CompletionItem[] {
  const out: CompletionItem[] = [];
  const seen = new Set<string>();
  const add = (pattern: string): void => {
    if (seen.has(pattern)) return;
    seen.add(pattern);
    out.push(
      ranked(
        {
          label: pattern,
          kind: CompletionItemKind.Value,
          detail: "view pattern",
          insertText: quoted ? `"${pattern}"` : pattern,
        },
        RANK_PRIMARY
      )
    );
  };
  for (const sym of symbols.declared()) {
    if (sym.kind !== "module" && sym.kind !== "surface") continue;
    add(sym.qualifiedName);
    add(`${sym.qualifiedName}.*`);
  }
  add("*");
  return out;
}

/**
 * Stub promotion: the ghost interfaces the enclosing module (`qname`) is
 * referenced with in a flow but doesn't declare, offered as declaration items
 * (`<kind> <name>`) ranked above the body's lead keyword. Closes the
 * process-first loop — sketch flows, open the dashed module, get its skeleton.
 */
function stubPromotionCompletions(workspace: Workspace, qname: string): CompletionItem[] {
  const prefix = `${qname}.`;
  // Order the choice list common-first (interface/rest_*/kafka/…) so the plausible
  // base kinds lead the dropdown, not 40 niche/cloud kinds in registry order (P3.3).
  const ifaceKinds = orderInterfaceKinds(
    [...workspace.model().registry.kinds]
      .filter(([, k]) => k.baseType === "interface")
      .map(([n]) => n)
  );
  const kindChoice = ifaceKinds.length > 0 ? `\${1|${ifaceKinds.join(",")}|}` : "";
  const out: CompletionItem[] = [];
  const seen = new Set<string>();
  for (const ip of workspace.ghostPaths().interfaces) {
    if (!ip.startsWith(prefix)) continue;
    if (ip.endsWith(`.${ANONYMOUS_INTERFACE_NAME}`)) continue;
    const leaf = ip.slice(prefix.length);
    if (leaf.includes(".") || seen.has(leaf)) continue; // direct members only
    seen.add(leaf);
    const item: CompletionItem = {
      label: leaf,
      kind: CompletionItemKind.Method,
      detail: "referenced in a process — declare it here",
      insertText: kindChoice ? `${kindChoice} ${leaf}` : leaf,
      // Ranked ABOVE the body's `aspect` lead (RANK_FLOW = "0aspect"): a leading
      // space sorts before any letter, so this floats to the very top.
      sortText: `0 ${leaf.toLowerCase()}`,
    };
    if (kindChoice) item.insertTextFormat = InsertTextFormat.Snippet;
    out.push(item);
  }
  return out;
}

/**
 * After the `export` modifier (v0.5): the author is about to declare what to
 * publish across the space/package boundary — a `type`, a `subprocess`, a
 * re-exported `use`, or (inside a module body) an interface of some kind.
 */
function suggestAfterExport(workspace: Workspace): CompletionItem[] {
  const out: CompletionItem[] = [
    kw("type", CompletionItemKind.Keyword),
    kw("subprocess", CompletionItemKind.Keyword),
    kw("use", CompletionItemKind.Keyword),
  ];
  for (const [name, kind] of workspace.model().registry.kinds) {
    if (kind.baseType === "process" || kind.baseType === "view") continue;
    out.push(typeCompletion(name, kind.baseType, false, [], kindDoc(kind)));
  }
  return out;
}

/**
 * After `from` in a `use … from <pkg>` declaration (v0.5): suggest the source
 * package names known to the workspace — every distinct library a resolved
 * type originates from (stdlib `arch.*` and declared dependencies).
 */
function suggestPackageNames(workspace: Workspace): CompletionItem[] {
  const seen = new Set<string>();
  const out: CompletionItem[] = [];
  const add = (name: string | undefined): void => {
    if (!name || seen.has(name)) return;
    seen.add(name);
    out.push({ label: name, kind: CompletionItemKind.Module, detail: "package" });
  };
  // The manifest is authoritative: declared dependencies + already-imported
  // source packages. (Type `library` tags only appear once stdlib resolves.)
  const manifest = workspace.manifest();
  if (manifest) {
    for (const dep of manifest.dependencies) add(dep.name.join("."));
    for (const u of manifest.uses) add(u.fromPackage.join("."));
  }
  for (const t of workspace.model().types) add(t.library);
  out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

function suggestExtendableTypes(workspace: Workspace): CompletionItem[] {
  const out: CompletionItem[] = [];
  for (const [name, kind] of workspace.model().registry.kinds) {
    // `process` / `view` are reserved keywords — `type process X` cannot parse,
    // so never offer them as a parent kind (other base kinds are fine).
    if (name === "process" || name === "view") continue;
    out.push(typeCompletion(name, kind.baseType, false, [], kindDoc(kind)));
  }
  return out;
}

function suggestNamesOnly(workspace: Workspace): CompletionItem[] {
  // A bare-identifier slot — a new declaration's name, or a free-form reference.
  // Offer ONLY the ghosts already referenced (promote the name you used but
  // haven't declared). Declared symbols are wrong here (you can't reuse an
  // existing name for a NEW declaration); aspects, stubs, and the synthesized
  // `(anonymous)` sentinel are never authorable identifiers. The old version
  // dumped symbols.all() — every one of those leaked in.
  const out: CompletionItem[] = [];
  out.push(...ghostModuleCompletions(workspace));
  out.push(...ghostInterfaceCompletions(workspace));
  return out;
}

// =============================================================================
// Selector slots (SELECTORS.md)
// =============================================================================

/**
 * Clause heads whose remainder is a SELECTOR slot. Every one of these words is
 * a contextual identifier, not a lexer keyword, so `classifyTokenContext` — which
 * keys on token TYPES — can never see them; the slot is recognised by reading
 * back to the head of the logical line instead.
 *
 * `style` belongs here for its SUBJECT (`style <sel> { … }`); the `{` ends the
 * line-head scan, so a cursor inside the style BODY never reads as a selector.
 */
const SELECTOR_CLAUSE_HEADS: ReadonlySet<string> = new Set([
  "show",
  "hide",
  "focus",
  "style",
  "forbid",
  "require",
  "except",
  "when", // `when <selector> <change verb> { … }` — the policy review gate
]);

/**
 * The blocks whose members are selector clauses. A head word alone is NOT
 * enough: `require` is also a module/type-body modifier (`required`), `style`
 * is also a top-level bundle head, and `when` is also a process-test block —
 * without this gate one keystroke into `required` swapped the body vocabulary
 * for the selector one. `use` carries `use policy P { except … }` (GRAMMAR
 * `policy_use_body`); a `when` gate's own body holds `require review from`,
 * which is NOT a selector slot, so `when` is deliberately absent.
 */
const SELECTOR_BLOCK_HEADS: ReadonlySet<string> = new Set(["view", "policy", "use"]);

/** `query [#id] <Name>: <selector>` — the head is fixed-shape, not a bare word. */
const QUERY_SLOT = /^\s*query\s+(?:#\S+\s+)?[\p{ID_Start}_][\p{ID_Continue}_]*\s*:/u;
/** `group by in <selector>` / `lane by in …` — containment grouping (VIEWS §4). */
const GROUP_BY_IN_SLOT = /^\s*(?:group|lane|pool)\s+by\s+in\b/u;

interface SelectorSlot {
  /** Source between the slot's head and the cursor — decides atom vs operator. */
  readonly before: string;
}

/**
 * A same-length copy of the source with every string literal and comment blanked
 * out, so the structural scan below sees only real syntax. Both matter here: a
 * `{`, `}` or `;` inside a string used to terminate the scan (dropping the slot
 * straight back into the value-slot fallback this router exists to replace), and
 * a block comment's newline used to end the logical line even though SELECTORS
 * §4 counts it as horizontal whitespace. Same length so every offset still
 * indexes the original text.
 */
function maskLiterals(text: string): { masked: string; hidden: readonly number[] } {
  const out = text.split("");
  const hidden: number[] = []; // flat, ascending [start, end) pairs
  let i = 0;
  // An UNCLOSED region is recorded one past the end, so a cursor sitting at the
  // very end of the scanned text still reads as inside it — while a closed
  // one keeps its exact half-open end, which is what puts a cursor glued to a
  // closing quote back in the value slot where it belongs.
  const blank = (from: number, to: number, open = false): void => {
    for (let k = from; k < to && k < out.length; k++) out[k] = " ";
    hidden.push(from, Math.min(to, out.length) + (open ? 1 : 0));
  };
  while (i < text.length) {
    const c = text[i]!;
    if (c === '"' || c === "'") {
      const quote = c;
      let j = i + 1;
      while (j < text.length && text[j] !== quote && text[j] !== "\n") {
        if (text[j] === "\\") j++;
        j++;
      }
      // The quotes go too — they are not syntax here. A string the author has
      // not closed yet ends at the newline, which must SURVIVE: blanking it
      // welded the half-typed line onto the next one.
      if (text[j] === quote) blank(i, j + 1);
      else blank(i, j, j >= text.length);
      i = j + 1;
      continue;
    }
    if (c === "/" && text[i + 1] === "/") {
      let j = i;
      while (j < text.length && text[j] !== "\n") j++;
      blank(i, j, j >= text.length);
      i = j;
      continue;
    }
    if (c === "/" && text[i + 1] === "*") {
      const end = text.indexOf("*/", i + 2);
      const j = end < 0 ? text.length : end + 2;
      blank(i, j, end < 0);
      i = j;
      continue;
    }
    i++;
  }
  return { masked: out.join(""), hidden };
}

/** How far back the slot scan looks. The scan only ever walks BACKWARDS to its
 *  clause head and that clause's opening brace, so masking the whole file cost
 *  ~11 ms per keystroke on a large package for text it never reads. A window
 *  this size holds any clause and its block header many times over. */
const SLOT_WINDOW = 8192;

/** Is `off` inside one of the blanked-out regions? Ranges are half-open, so a
 *  cursor glued to a closing quote (`"Core"█`) is OUTSIDE the string — it is the
 *  value slot's, not the selector's. Ascending pairs, so a scan that stops at
 *  the first range starting past `off` is enough. */
function inHidden(hidden: readonly number[], off: number): boolean {
  for (let k = 0; k < hidden.length; k += 2) {
    if (hidden[k]! > off) return false;
    if (off < hidden[k + 1]!) return true;
  }
  return false;
}

/** The `{` that opens the block the cursor sits in, or -1 at the top level.
 *  Depth-counted, NOT "the first brace going backwards": a clause after a
 *  block-bodied one (`style … { … }`, `table { … }`) meets that block's CLOSING
 *  brace first, and reading the head from there dropped the whole rest of the
 *  body. */
function enclosingBrace(masked: string, from: number): number {
  let depth = 0;
  for (let i = from - 1; i >= 0; i--) {
    const c = masked[i]!;
    if (c === "}") depth++;
    else if (c === "{") {
      if (depth === 0) return i;
      depth--;
    }
  }
  return -1;
}

/** Words that may precede a declaration keyword — GRAMMAR `placed_decl`
 *  (`in <Module> view V { … }`) and the `export` modifier. */
const DECL_PREFIX = /^\s*(?:in\s+[\p{ID_Start}_][\p{ID_Continue}_.-]*\s+|export\s+)+/u;

/** The declaration keyword of the block that `brace` opens. `view V {` → "view";
 *  `export policy P {` and `in Payments view V {` read past their prefixes; a
 *  brace on its own line reads the line above it. */
function blockHeadAt(masked: string, brace: number): string | null {
  let end = brace;
  for (let attempt = 0; attempt < 2; attempt++) {
    let start = 0;
    for (let i = end - 1; i >= 0; i--) {
      const c = masked[i]!;
      if (c === "\n" || c === "{" || c === "}" || c === ";") {
        start = i + 1;
        break;
      }
    }
    const decl = masked.slice(start, end).replace(DECL_PREFIX, "");
    const word = /^\s*([\p{ID_Start}_][\p{ID_Continue}_]*)/u.exec(decl);
    if (word) return word[1]!;
    if (start === 0) return null;
    end = start - 1; // the brace sat alone on its line — try the line above
  }
  return null;
}

/**
 * Is the cursor inside a selector slot, and what has been written in it so far?
 *
 * The scan walks back to the head of the LOGICAL line — a selector terminates at
 * a newline, `;`, or a brace (SELECTORS §4), and only parens span lines, so an
 * unclosed `(` keeps the scan going to the line that opened it. A bare head word
 * additionally has to sit in a block that HOLDS selector clauses; `query X:` is
 * fixed-shape enough to stand on its own anywhere.
 */
function selectorSlotAt(rawText: string, cursor: number, from = -1): SelectorSlot | null {
  // Window snapped to a line start, and carried to the END of the cursor's line
  // so a comment the cursor sits in is seen whole.
  const base = from >= 0 ? from : cursor > SLOT_WINDOW ? rawText.lastIndexOf("\n", cursor - SLOT_WINDOW) + 1 : 0;
  const eol = rawText.indexOf("\n", cursor);
  const { masked: text, hidden } = maskLiterals(rawText.slice(base, eol < 0 ? rawText.length : eol));
  const off = cursor - base;
  // The cursor itself inside a comment (or a string) is nobody's selector slot.
  if (inHidden(hidden, off)) return null;
  // Scanning right-to-left: `pending` counts `)` awaiting their `(`. An
  // UNMATCHED `(` means every newline passed so far sat INSIDE parens, so the
  // recorded line start is dropped and the scan continues to the line that
  // opened them — parens are the one thing that spans lines here.
  let pending = 0;
  let lineStart = -1;
  let start = 0;
  for (let i = off - 1; i >= 0; i--) {
    const c = text[i]!;
    if (c === ")") pending++;
    else if (c === "(") {
      if (pending > 0) pending--;
      else lineStart = -1;
    } else if (c === "\n") {
      if (lineStart < 0) lineStart = i + 1;
    } else if (c === "{" || c === "}" || c === ";") {
      start = lineStart >= 0 ? lineStart : i + 1;
      break;
    }
  }
  if (start === 0 && lineStart >= 0) start = lineStart;
  const line = text.slice(start, off);
  const head = /^\s*([\p{ID_Start}_][\p{ID_Continue}_]*)/u.exec(line);
  if (head && SELECTOR_CLAUSE_HEADS.has(head[1]!)) {
    // A `require` splits subject from obligation at the first depth-0 `:` not
    // glued into an `@@key:value` atom — both sides are selectors, so the slot
    // simply continues past it.
    const brace = enclosingBrace(text, start);
    // No opening brace in the window is ambiguous: the top level (correctly not
    // a slot) or a body longer than the window. Retry unwindowed rather than
    // answer nothing — a one-off full scan in a case measured in the rare, not
    // the whole-file mask on every keystroke this window exists to avoid.
    if (brace < 0 && base > 0) return selectorSlotAt(rawText, cursor, 0);
    const block = brace >= 0 ? blockHeadAt(text, brace) : null;
    if (block !== null && SELECTOR_BLOCK_HEADS.has(block)) {
      return { before: line.slice(head[0].length) };
    }
  }
  const q = QUERY_SLOT.exec(line);
  if (q) return { before: line.slice(q[0].length) };
  const g = GROUP_BY_IN_SLOT.exec(line);
  if (g) return { before: line.slice(g[0].length) };
  return null;
}

/** Re-point aspect-key items at the `@@` axis: the label still reads as the
 *  key (so typed-prefix matching works on what the author types), but what
 *  lands in the buffer is the sigiled getter the grammar requires. */
function sigiled(items: readonly CompletionItem[]): CompletionItem[] {
  return items.map((it) => ({ ...it, insertText: `@@${String(it.label)}` }));
}

/** One selector term the picker offers. `atom` marks a term that NAMES a set
 *  (so it leads at a fresh slot); the rest refine or combine one. */
const SELECTOR_TERMS: readonly {
  readonly label: string;
  readonly insert?: string;
  readonly detail: string;
  readonly atom?: true;
}[] = [
  { label: "*", detail: "every element in scope", atom: true },
  { label: "this", detail: "the bound subject", atom: true },
  { label: "outside", detail: "elements this view does not select", atom: true },
  { label: "violating", insert: "violating ${1:Policy}", detail: "a policy's active findings", atom: true },
  { label: "in", insert: "in ${1:Scope}", detail: "predicate — inside a module / space / process" },
  { label: "depth", insert: "depth ${1:1}", detail: "predicate — containment depth bound" },
  { label: "on", insert: "on ${1:plane}", detail: "predicate — present on a plane" },
  { label: "where", insert: "where (${1:@field})", detail: "predicate — expression filter" },
  { label: "and", detail: "conjunction — intersect" },
  { label: "or", detail: "disjunction — union" },
  { label: "not", detail: "complement within the sort's universe" },
  { label: "within", insert: "within ${1:2}", detail: "hop-distance bound (`>>` / `<>` only)" },
  { label: "exists", insert: "exists (${1:this > *})", detail: "non-empty test" },
  { label: "sources of", insert: "sources of (${1:* > *})", detail: "extraction — edge tails", atom: true },
  { label: "targets of", insert: "targets of (${1:* > *})", detail: "extraction — edge heads", atom: true },
  { label: "nodes of", insert: "nodes of (${1:* > *})", detail: "extraction — both ends", atom: true },
  { label: "owners of", insert: "owners of (${1:this})", detail: "extraction — owning modules", atom: true },
];

/**
 * A selector slot's vocabulary: the operator/predicate words plus everything
 * that NAMES a set — kind atoms, user types, declared elements, `@@aspect`
 * atoms, and (for `violating`) the policies in scope.
 *
 * Ranking follows the cursor: at a fresh term position the set-naming atoms
 * lead; once a term is written the combinators and refinements do.
 */
function selectorCompletions(
  workspace: Workspace,
  symbols: SymbolTable,
  slot: SelectorSlot
): CompletionItem[] {
  // Strip a trailing partial word so "half-typed" still reads as its position.
  // `\p{ID_Continue}`, not `\w`: identifiers are Unicode and may carry internal
  // hyphens (§4.9), and an ASCII-only strip left the half-typed word standing —
  // flipping a Cyrillic or hyphenated name's slot to operator ranking.
  const written = slot.before.replace(/[\p{ID_Continue}.@$*-]+$/u, "");
  // A term is FRESH at the slot's head, after a separator, after a combinator,
  // and after an arrow op or the `require` subject/obligation `:`.
  const freshTerm = /(?:^|[(,:>]|\b(?:and|or|not|of)\b)\s*$/u.test(written);
  // A predicate's own argument is a narrower slot than the selector around it.
  // `in` takes a sel_primary (no combinators, no nested predicate); `depth` /
  // `within` take a number or a `$knob`, and `on` a plane name — none of which
  // this vocabulary holds, so those answer with nothing rather than with the
  // whole menu, every item of which would be illegal there.
  // `[^…-@.$]`, not `\b`: `\b` sits between a hyphen and a word, so an element
  // named `my-in` read as the `in` predicate; a getter `@depth` likewise.
  const argOf = /(?:^|[^\p{ID_Continue}\-@.$])(in|depth|within|on)\s+$/u.exec(written)?.[1];
  if (argOf === "depth" || argOf === "within" || argOf === "on") return [];
  const atomRank = freshTerm ? RANK_PRIMARY : RANK_SYMBOL;
  const opRank = freshTerm ? RANK_KEYWORD : RANK_PRIMARY;
  const out: CompletionItem[] = [];
  // The sources overlap by construction — `registry.kinds` carries the built-in
  // kinds beside the user types, and a user type may share a declared element's
  // name — and a duplicate label is a list defect, not a ranking one. First
  // writer wins: the earlier source is the more specific one.
  const seen = new Set<string>();
  const push = (item: CompletionItem, rank: string): void => {
    const label = String(item.label);
    if (seen.has(label)) return;
    seen.add(label);
    out.push(ranked(item, rank));
  };

  for (const t of SELECTOR_TERMS) {
    // After `in`, only the terms that can BE a sel_primary.
    if (argOf === "in" && !t.atom) continue;
    const item: CompletionItem = {
      label: t.label,
      kind: t.insert ? CompletionItemKind.Snippet : CompletionItemKind.Keyword,
      detail: t.detail,
    };
    if (t.insert) {
      item.insertText = t.insert;
      item.insertTextFormat = InsertTextFormat.Snippet;
    }
    push(item, t.atom ? atomRank : opRank);
  }

  // Kind atoms — every element of a built-in kind (SELECTORS §2).
  for (const k of BUILTIN_KINDS) {
    push({ label: k, kind: CompletionItemKind.Keyword, detail: "kind atom" }, atomRank);
  }
  // User types (instances incl. subtypes).
  for (const [name, kind] of workspace.model().registry.kinds) {
    push(typeCompletion(name, kind.baseType, false, [], kindDoc(kind)), atomRank);
  }
  // Declared elements — a selector never creates a stub, so ghosts stay out.
  for (const sym of symbols.declared()) {
    if (sym.kind !== "module" && sym.kind !== "surface" && sym.kind !== "interface") continue;
    push(symbolCompletion(workspace, sym, sym.qualifiedName, RANK_SYMBOL), RANK_SYMBOL);
  }
  // Aspect atoms — sigiled, which is how the axis reads in query position.
  for (const key of collectAspectKeys(workspace.model())) {
    push({ label: `@@${key}`, kind: CompletionItemKind.Field, detail: "aspect carriers" }, atomRank);
  }
  // Policy names are NOT offered: they live on the parsed file (hoisted by
  // `collectScopedPolicies`), not on the resolved model the workspace exposes.
  // `violating` still inserts its `${1:Policy}` placeholder.
  return out;
}

// =============================================================================
// Ranking
// =============================================================================

// Relevance buckets — the client orders items by `sortText`, so a lower bucket
// floats above a higher one. Within a bucket items sort alphabetically. This is
// what makes the list feel curated: the candidates that fit the cursor's context
// come first, real workspace symbols next, ghosts after, keywords last.
//
// Final lettering (single sortText chars; the digit is `sortText[0]`, which
// `finalize` reads back to re-rank within a bucket). `finalize` then expands a
// symbol item's sortText to `<rank><locality><invFreq><label>`, where the
// locality digit is the P2a tier (same file 0 · same space 1 · same package 2 ·
// imported 3), and a prefix / substring / camelCase-subsequence typed-word match
// prepends a `0` / `0~` / `0~~` super-bucket ahead of every category:
//   "0" flow · "1" primary · "2" symbol/kind · "3" caller · "4" ghost ·
//   "5" boundary (unexported cross-space interface, still shown) · "6" keyword
const RANK_FLOW = "0"; // in-context: a callee of a module already named in this flow
const RANK_PRIMARY = "1"; // the thing this context is really for
const RANK_SYMBOL = "2"; // declared workspace symbols + registered kinds
const RANK_CALLER = "3"; // declared module/actor names offered as bare-step callers (root)
const RANK_GHOST = "4"; // referenced-but-undeclared (ghost) entities
// A real interface a flow may still call, but which is NOT exported across the
// space boundary (§4.11 — coupling visibility is soft): sinks below every
// exported/same-space callee yet stays offerable (P2a item 2).
const RANK_BOUNDARY = "5";
const RANK_KEYWORD = "6"; // grammar keywords with no contextual priority

function ranked<T extends CompletionItem>(item: T, rank: string): T {
  item.sortText = `${rank}${String(item.label).toLowerCase()}`;
  return item;
}

/** A grammar keyword completion carrying an explicit relevance rank (so the
 *  keywords a scope is really about can outrank symbols/ghosts/kinds). */
function kwRanked(label: string, rank: string): CompletionItem {
  return ranked(kw(label, CompletionItemKind.Keyword), rank);
}

/** Overwrite an item's sortText with `<rank><sub><label>` — a SECONDARY key
 *  inside an existing rank bucket, so a bucket's members can be intent-ordered
 *  (common decl keywords before rare, template fields before the structural
 *  tail) without inventing a new global scheme (P2.1 / P2.2). */
function subRanked<T extends CompletionItem>(item: T, rank: string, sub: string): T {
  item.sortText = `${rank}${sub}${String(item.label).toLowerCase()}`;
  return item;
}

/** Container-first ordering digit for a declaration kind's base within its rank
 *  bucket: a module ranks above a surface above an interface. At the top level a
 *  bare root interface is rare (P2.1); in a module body the module/surface kinds
 *  sink below the interface kinds via a different placement, but the same
 *  module<surface relative order holds. */
function baseKindOrder(baseType: string): string {
  return baseType === "module" ? "0" : baseType === "surface" ? "1" : "2";
}

/** A blessed-practice description scaffold (`"$0"`) offered at a module/surface
 *  body or process-header statement start (P3.5). `sortText` places it modestly
 *  per context (below the fields/aspect lead in a body; near the bottom in a
 *  process header). */
function descriptionScaffoldItem(sortText: string): CompletionItem {
  return {
    label: "description",
    kind: CompletionItemKind.Snippet,
    detail: "description",
    insertText: '"$0"',
    insertTextFormat: InsertTextFormat.Snippet,
    sortText,
  };
}

/** Stdlib packages whose kinds are the "90% answer" for a bare prefix — their
 *  auto-import candidates lead same-prefix siblings from niche packages (P3.1). */
const CORE_STDLIB_PACKAGES: ReadonlySet<string> = new Set(["arch.backend"]);

/** The common interface kinds an author reaches for first — they lead the stub-
 *  promotion kind-choice enum ahead of niche/cloud interface kinds (P3.3). */
const COMMON_INTERFACE_KINDS: readonly string[] = [
  "interface",
  "rest",
  "rest_create",
  "rest_read",
  "rest_update",
  "rest_delete",
  "kafka",
  "grpc",
  "amqp",
  "graphql",
  "event",
  "websocket",
  "soap",
  "sql",
];

/** Order interface-kind names common-first (the curated set in priority order),
 *  then the rest alphabetically — a stable, plausible-first choice list (P3.3). */
function orderInterfaceKinds(names: readonly string[]): string[] {
  const rank = new Map(COMMON_INTERFACE_KINDS.map((n, i) => [n, i]));
  const BIG = Number.MAX_SAFE_INTEGER;
  return [...names].sort((a, b) => {
    const ra = rank.get(a) ?? BIG;
    const rb = rank.get(b) ?? BIG;
    return ra !== rb ? ra - rb : a.localeCompare(b);
  });
}

/** The tab-through declaration scaffold a kind expands into — a name placeholder
 *  for an interface (a one-liner), else a block prefilling the kind's `required`
 *  blanks as tab-stops. Shared by local kind items and auto-import items (P3.2)
 *  so accepting either feels identical. */
function declSnippet(
  name: string,
  rootBase: string,
  requiredFields: readonly string[] = [],
  mint?: () => string
): string {
  if (rootBase === "interface") return `${name} \${1:Name}`;
  // A module INSTANCE bears a stable id — bake a freshly minted one into the
  // scaffold. Surfaces/interfaces bear none, and template-stamp scaffolds
  // (a kind offered inside a `type` body) pass no minter: an id baked into a
  // stamp would be copied into every instance, colliding by construction.
  const id = rootBase === "module" && mint ? `#${mint()} ` : "";
  let tab = 2;
  const reqLines = requiredFields.map((p) => `\t${p}: \${${tab++}:TODO}`);
  const inner = reqLines.length > 0 ? `${reqLines.join("\n")}\n\t$0` : "\t$0";
  return `${name} ${id}\${1:Name} {\n${inner}\n}`;
}

/** One-line docs for grammar keywords so the picker explains them. */
const KEYWORD_DOCS: Record<string, string> = {
  type: "Declare a reusable kind: `type <parentKind> <Name> { … }`.",
  process: "Declare a process — an ordered flow of `Caller > Callee` steps.",
  view: "Declare a view — a curated slice of the model to render.",
  use: "Import a type from a dependency package: `use <Type> from <pkg>`.",
  in: "Place declarations into an existing container — a module or a surface: `in <Target> { … }` (spec §4.16).",
  export: "Publish a type / interface / subprocess across a space or package boundary.",
  labels: "Open a labels block — key/value classification on this element.",
  required: "Mark a field as a mandatory blank that instances must fill or drop.",
  cascade: "Propagate this field's value down to descendants (override mode).",
  "cascade *": "Cascade as a group root — replacing the value downstream drops the whole group.",
  append: "Propagate this field's value down to descendants (compose mode).",
  override: "Override an inherited declaration on this instance.",
  drop: "Drop an inherited field, label, or required blank.",
  if: "Exclusive branch in a process; `else if` / `else` follow.",
  else: "Fallback branch of an `if`.",
  select: "Inclusive multi-way choice — every matching case runs (`select N` = first N, `select one` = first match).",
  one: "With `select`: run only the first matching case (`select one`).",
  each: "Iterate steps over a collection (`each item in items`); `each N try …` is a retry loop.",
  try: "Run steps that may fail, paired with `catch`.",
  catch: "Handle a failure from a `try` block.",
  parallel: "Run branches concurrently; `join`/`race`/`out` set re-convergence.",
  branch: "A named lane inside a `parallel` block.",
  join: "Merge policy: wait for branches (`join N` = first N), keep the rest.",
  race: "Merge policy: first branch wins (`race N` = first N), cancel the rest.",
  out: "Detach a `parallel` — let it run without blocking the flow.",
  await: "Wait for a timer or event (`await 1 day`, `await \"until ready\"`).",
  reversible:
    "Open a saga block. Pair each step with an `unwind` that compensates THAT step; when the flow can't finish, the completed steps' compensations run in reverse (PROCESS.md §8).",
  unwind:
    "The compensating transaction for THIS step — undo what it did, targeting the same callee the forward step called (`> Inventory.reserve unwind > Inventory.release`). Never the previous step: a failed step didn't complete, and earlier steps' own `unwind`s already fire on the rethrow.",
  on: "`reversible on error { … }` trigger, or `subprocess X on Caller` default owner.",
  dist: "Distributed (choreographed) control — no single coordinator.",
  as: "Name a step (`> X.y as checkpoint`) so `go` can re-enter it.",
  go: "Re-enter a step named with `as` and resume forward.",
  do: "Invoke a subprocess.",
  fail: "Terminate the flow with a failure.",
  finish: "Terminate the flow successfully.",
  test: "Declare a process test: `test \"<name>\" of <Process> { when {…} then {…} }`. Pin the nondeterminism, assert the trace — a mock by construction (PROCESS.md §14).",
  when: "Open a test's pin block — one `<choice>: <answer>` per choice point (branch, `select`, failure, timeout) so the session resolves to one trace.",
  then: "Open a test's assertion block — modal step occurrences (`happens`/`never`/`sometimes` + counts), a `fail`/`finish` terminator, a `time` budget, ordered by position.",
  happens: "Assert a step occurs on every trace (`happens N` / `happens >= N` for a count). A bare step reference reads as `happens`.",
  sometimes: "Assert a step occurs on at least one trace (existential ◇) — a witness proves it regardless of the OPEN set.",
  elapses: "Answer an `await` boundary as timing OUT (the timeout branch runs).",
  within: "Answer an `await` boundary as completing IN TIME (the awaited event arrives first).",
  fails: "Pin a step to fail (`fails` / `fails \"cause\"`), or a per-attempt sequence (`fails \"x\", ok`) for a retry loop.",
  time: "Assert a latency budget over the trace: `time (system|business) <op> <duration>`.",
  focus: "Restrict a view to the named elements.",
  group: "Group a view's nodes by a label or path.",
  by: "Companion to `group` — the grouping key.",
  layout: "Set a view's layout strategy.",
  include: "Add elements to a view.",
  exclude: "Remove elements from a view.",
};

// =============================================================================
// Ghost (referenced-but-undeclared) entities (§4.13)
// =============================================================================

/** Whether every dotted segment of a ghost path is a lexer-valid identifier.
 *  A draft floor can synthesize parse-junk ghosts (`}`, an empty label) when the
 *  buffer is mid-edit — filter them out (P2.7) so they never reach the list. */
function isLexerValidGhostPath(path: string): boolean {
  const segs = path.split(".");
  if (segs.length === 0) return false;
  for (const seg of segs) {
    if (seg.length === 0) return false;
    for (const ch of seg) {
      if (!isIdentCharCode(ch.codePointAt(0)!)) return false;
    }
  }
  return true;
}

/** Ghost modules a process implies but no declaration defines yet. */
function ghostModuleCompletions(workspace: Workspace): CompletionItem[] {
  return workspace
    .ghostPaths()
    .modules.filter(isLexerValidGhostPath)
    .map((p) => ghostItem(p, "module", CompletionItemKind.Module));
}

/** Ghost interfaces a process implies. The synthesized `(anonymous)` handler is
 *  a placeholder, not a name an author would type — drop it. */
function ghostInterfaceCompletions(workspace: Workspace): CompletionItem[] {
  return workspace
    .ghostPaths()
    .interfaces.filter((p) => !p.endsWith(`.${ANONYMOUS_INTERFACE_NAME}`) && isLexerValidGhostPath(p))
    .map((p) => ghostItem(p, "interface", CompletionItemKind.Method));
}

/** Whether an item is a ghost (referenced-but-undefined) entity — tagged so the
 *  self-echo filter (P1.1) can drop the one that merely echoes the typed run. */
function isGhostEcho(item: CompletionItem): boolean {
  return (item.data as { ghost?: boolean } | undefined)?.ghost === true;
}

/** A model-derived reused value (aspect key, …) that the tolerant parser echoed
 *  back from the half-typed token — dropped when it equals the typed run. */
function isReusedEcho(item: CompletionItem): boolean {
  return (item.data as { reused?: boolean } | undefined)?.reused === true;
}

function ghostItem(
  path: string,
  noun: "module" | "interface",
  kind: CompletionItemKind,
  label: string = path
): CompletionItem {
  const item: CompletionItem = ranked(
    {
      label,
      kind,
      detail: `${noun} — referenced in a process, not defined yet`,
    },
    RANK_GHOST
  );
  item.data = { ghost: true };
  // commitCharacters (P2a item 7): a ghost module is a dotted-path segment too.
  if (noun === "module") item.commitCharacters = ["."];
  return item;
}

// =============================================================================
// Helpers
// =============================================================================

function collectAspectKeys(model: ResolvedModel): string[] {
  const out: string[] = [];
  const push = (aspects: readonly { path: readonly string[]; drop?: boolean }[]): void => {
    for (const l of aspects) if (!l.drop) out.push(l.path.join("."));
  };
  const visitSurface = (s: ResolvedModel["modules"][number]["surfaces"][number]): void => {
    push(s.body.aspects);
    for (const i of s.interfaces) push(i.body.aspects); // interface-level aspects
    for (const sub of s.surfaces) visitSurface(sub);
  };
  const visit = (mods: ResolvedModel["modules"]): void => {
    for (const m of mods) {
      push(m.body.aspects);
      for (const i of m.interfaces) push(i.body.aspects); // interface-level aspects
      for (const s of m.surfaces) visitSurface(s);
      visit(m.modules);
    }
  };
  visit(model.modules);
  return out;
}

/** Distinct header-field keys declared on any process / subprocess in the
 *  workspace — the header zone's field-key offers (PROCESS.md §1). */
function collectProcessFieldKeys(model: ResolvedModel): string[] {
  const out = new Set<string>();
  const add = (
    fields: readonly { path: readonly string[] }[] | undefined
  ): void => {
    for (const f of fields ?? []) out.add(f.path.join("."));
  };
  for (const p of model.processes) add(p.fields);
  for (const s of model.subprocesses) add(s.fields);
  return [...out];
}

/**
 * Walking back from `lastIdx`, the key the cursor is assigning a value to
 * (`domain: |` or `team: Comm|` → `"domain"` / `"team"`), or undefined when not
 * in value position. Stops at a brace (statement boundary).
 */
function keyBeforeColon(tokens: readonly Token[], lastIdx: number): string | undefined {
  let i = lastIdx;
  while (i >= 0) {
    const t = tokens[i]!;
    // A brace bounds the statement; a ';' starts a NEW key/value entry
    // (v0.9.0 member separator), so a colon found past it belongs to the
    // previous entry. A ',' does NOT bound: it continues the current entry's
    // list value (`tags: pii, █` is value position for key `tags`).
    if (t.type === "LBRACE" || t.type === "RBRACE" || t.type === "SEMICOLON") return undefined;
    if (t.type === "COLON") break;
    i--;
  }
  if (i < 0 || tokens[i]?.type !== "COLON") return undefined;
  const path: string[] = [];
  let j = i - 1;
  while (j >= 0 && tokens[j]?.type === "IDENTIFIER") {
    path.unshift(tokens[j]!.value);
    if (tokens[j - 1]?.type === "DOT") j -= 2;
    else break;
  }
  return path.length > 0 ? path.join(".") : undefined;
}

/** Label values already assigned to `key` — keeps a classification
 *  (`domain: orders`) consistent across modules. */
function aspectValueCompletions(workspace: Workspace, key: string): CompletionItem[] {
  return valueItems(collectAssignedValues(workspace, key, "labels"), key);
}

/** Field values already assigned to `key` (`team: "Commerce"`, `widget.icon: …`)
 *  — surfaced so authors reuse existing values rather than reinvent them. */
function fieldValueCompletions(workspace: Workspace, key: string): CompletionItem[] {
  return valueItems(collectAssignedValues(workspace, key, "fields"), key);
}

/** Distinct values assigned to `key` across every module/surface body, mapped to
 *  whether the value was quoted (a string) — so the completion re-quotes it. */
function collectAssignedValues(
  workspace: Workspace,
  key: string,
  which: "fields" | "labels"
): Map<string, boolean> {
  const seen = new Map<string, boolean>();
  const consider = (v: Value | undefined): void => {
    if (!v) return;
    // Drop empty-text seeds (P2.9): an in-progress `key: █` yields an empty value
    // that would render as a blank row (inserting `""`) in the picker.
    if (v.kind === "identifier" && v.text.length > 0) seen.set(v.text, false);
    else if (v.kind === "string" && v.text.length > 0) seen.set(v.text, true);
    else if (v.kind === "list") for (const it of v.items) consider(it);
  };
  const visit = (mods: ResolvedModel["modules"]): void => {
    for (const m of mods) {
      for (const e of which === "fields" ? m.body.fields : m.body.aspects) {
        if (e.path.join(".") === key) consider(e.value);
      }
      for (const s of m.surfaces) {
        for (const e of which === "fields" ? s.body.fields : s.body.aspects) {
          if (e.path.join(".") === key) consider(e.value);
        }
      }
      visit(m.modules);
    }
  };
  visit(workspace.model().modules);
  return seen;
}

function valueItems(seen: Map<string, boolean>, key: string): CompletionItem[] {
  const out: CompletionItem[] = [];
  for (const [val, quoted] of seen) {
    out.push(
      ranked(
        {
          label: val,
          kind: CompletionItemKind.Value,
          detail: `value of ${key}`,
          // `labelDetails.description` distinguishes a reused VALUE row from a
          // same-labelled module-ref row in an aspect value slot (P3.4) — a bare
          // module ref carries "module"; this one carries "value".
          labelDetails: { description: "value" },
          insertText: quoted ? `"${val}"` : val,
          // Reused-value echo guard: the tolerant parser turns the half-typed
          // value (`team: comm█`) into an assigned value, so it re-appears here;
          // the self-echo filter drops it when it equals the typed run (P1.1).
          data: { reused: true },
        },
        RANK_PRIMARY
      )
    );
  }
  return out;
}

function typeCompletion(
  name: string,
  baseType: string,
  snippet = false,
  requiredFields: readonly string[] = [],
  doc?: string,
  mint?: () => string
): CompletionItem {
  const k =
    baseType === "interface"
      ? CompletionItemKind.Method
      : baseType === "surface"
      ? CompletionItemKind.Class
      : baseType === "module"
      ? CompletionItemKind.Class
      : baseType === "process"
      ? CompletionItemKind.Function
      : baseType === "view"
      ? CompletionItemKind.Struct
      : CompletionItemKind.Class;
  const item: CompletionItem = {
    label: name,
    kind: k,
    detail: `kind ${baseType}`,
    // labelDetails (P2a item 5): the base kind on the right, so a body's long
    // kind list is scannable (an interface kind vs. a module kind at a glance).
    labelDetails: { description: baseType },
  };
  // The kind's own docstring is heavy markdown — defer it to `resolveCompletion`
  // (same lazy path as symbol completions) so a keystroke doesn't render docs
  // for every candidate, only the highlighted one.
  if (doc) item.data = { doc };
  // In declaration-introducing positions, expand the kind into a tab-through
  // scaffold. An interface is typically a one-liner (just a name placeholder);
  // a block-bodied kind also prefills the kind's `required` blanks as tab-stops
  // so the author can't forget a mandatory field.
  if (snippet) {
    item.insertTextFormat = InsertTextFormat.Snippet;
    item.insertText = declSnippet(name, baseType, requiredFields, mint);
  }
  return ranked(item, RANK_SYMBOL);
}

/** The `required` blank field paths a kind stamps onto its instances. */
function requiredFieldPaths(kind: {
  stampedBody: { fields: readonly { path: readonly string[]; required?: boolean }[] };
}): string[] {
  return kind.stampedBody.fields
    .filter((f) => f.required)
    .map((f) => f.path.join("."));
}

/** A kind's own docstring (the type declaration's body descriptions), or
 *  undefined when it has none — threaded into a kind-name completion's
 *  `data.doc` so `resolveCompletion` can lazily render it. */
function kindDoc(kind: ResolvedType): string | undefined {
  if (!kind.decl) return undefined;
  const doc = joinDescriptions(kind.decl.body.descriptions);
  return doc.length > 0 ? doc : undefined;
}

// =============================================================================
// Auto-import (P3) — stdlib kinds from not-yet-`use`d packages.
//
// At a declaration-kind position, offer types a bundled `arch.*` package
// exports but the current file hasn't imported, ranked below already-known
// kinds. Accepting one inserts the missing `use <Kind> from <pkg>` via
// `additionalTextEdits`. Visibility: only `export`ed types (the index only
// records those), never instances (§4.11 packages are opaque); only stdlib
// packages (so the inserted `use` can't yield USE_PACKAGE_NOT_FOUND).
// =============================================================================

/** Root base kinds legal as a declaration kind at the top level / a module or
 *  type body (a surface additionally forbids module kinds). Process/view kinds
 *  aren't offered here — they have dedicated snippet keywords. */
const AUTO_IMPORT_BASE_MODULE: ReadonlySet<string> = new Set([
  "module",
  "surface",
  "interface",
]);
const AUTO_IMPORT_BASE_SURFACE: ReadonlySet<string> = new Set(["surface", "interface"]);

/** Defensive ceiling — a typed prefix already bounds candidates hard. */
const AUTO_IMPORT_MAX = 50;

/** The root-base kinds an auto-import may offer at `scope`, or null when the
 *  scope isn't a declaration-kind position. */
function autoImportBaseFilter(scope: Scope): ReadonlySet<string> | null {
  switch (scope) {
    case "top":
    case "module":
    case "type":
      return AUTO_IMPORT_BASE_MODULE;
    case "surface":
      return AUTO_IMPORT_BASE_SURFACE;
    default:
      return null;
  }
}

function autoImportKindCompletions(
  file: FileState,
  uri: string,
  workspace: Workspace,
  stdlibExports: readonly StdlibExport[],
  allow: ReadonlySet<string>,
  offset: number
): CompletionItem[] {
  const word = typedPrefixAt(file.text, offset);
  if (word.length === 0) return []; // no prefix → don't dump the whole stdlib
  const wl = word.toLowerCase();
  const mint = makeIdMint(() => workspace.model());
  const known = workspace.model().registry.kinds; // locally declared/imported/pre-seeded
  const imported = importedUseKeys(file.file.uses);
  const out: CompletionItem[] = [];
  const seen = new Set<string>();
  for (const ex of stdlibExports) {
    if (!allow.has(ex.rootBase)) continue;
    if (!ex.kindName.toLowerCase().startsWith(wl)) continue;
    // Already a known kind (declared here, imported, or a pre-seeded stdlib
    // interface) — the normal kind list handles it; no auto-import dup.
    if (known.has(ex.kindName)) continue;
    // Already `use`d this exact type, or `use *` from its package — no re-insert.
    if (imported.wildcardPkgs.has(ex.packageName)) continue;
    if (imported.pairs.has(`${ex.kindName}|${ex.packageName}`)) continue;
    const dedup = `${ex.kindName}|${ex.packageName}`;
    if (seen.has(dedup)) continue;
    seen.add(dedup);
    // Tie-break within the auto-import (ghost) bucket (P3.1): an EXACT name match
    // leads, then a CORE package (`arch.backend`) — so the 90% answer (`service`)
    // beats niche same-prefix siblings (`server`/`serverless_function`), which
    // otherwise sort alphabetically ahead of it.
    const sub =
      ex.kindName.toLowerCase() === wl ? "0" : CORE_STDLIB_PACKAGES.has(ex.packageName) ? "1" : "2";
    out.push(makeAutoImportItem(file, uri, ex, sub, mint));
    if (out.length >= AUTO_IMPORT_MAX) break;
  }
  return out;
}

/** The identifier run ending at `offset` (the half-typed word under the cursor),
 *  mirroring `finalize`'s word-start scan (ID_Continue + internal hyphens). */
function typedPrefixAt(text: string, offset: number): string {
  const HYPHEN = 0x2d;
  let start = offset;
  while (start > 0) {
    const c = text.charCodeAt(start - 1);
    if (isIdentContinueCode(c)) {
      start--;
      continue;
    }
    if (c === HYPHEN && start - 2 >= 0 && isIdentContinueCode(text.charCodeAt(start - 2))) {
      start--;
      continue;
    }
    break;
  }
  return text.slice(start, offset);
}

/** The set of (type, package) pairs — and whole packages via `use *` — the file
 *  already imports, for auto-import dedup. */
function importedUseKeys(uses: readonly UseDecl[]): {
  wildcardPkgs: ReadonlySet<string>;
  pairs: ReadonlySet<string>;
} {
  const wildcardPkgs = new Set<string>();
  const pairs = new Set<string>();
  for (const u of uses) {
    const pkg = u.fromPackage.join(".");
    if (u.wildcard) {
      wildcardPkgs.add(pkg);
      continue;
    }
    for (const imp of u.imports) pairs.add(`${imp.name}|${pkg}`);
  }
  return { wildcardPkgs, pairs };
}

function makeAutoImportItem(
  file: FileState,
  uri: string,
  ex: StdlibExport,
  sub: string,
  mint?: () => string
): CompletionItem {
  const item: CompletionItem = {
    label: ex.kindName,
    kind: baseKindToCompletionKind(ex.rootBase),
    // `import from …` (never `kind …`) so the P2a hidden-type filter, which keys
    // on a `kind` detail prefix, leaves the item alone.
    detail: `import from ${ex.packageName}`,
    labelDetails: { description: ex.packageName },
    // Carry the SAME declaration scaffold a local kind item inserts (P3.2), so
    // accepting an auto-import feels identical (name placeholder / block body) —
    // the folded/kept `use … from …` still lands via `additionalTextEdits`.
    insertText: declSnippet(ex.kindName, ex.rootBase, [], mint),
    insertTextFormat: InsertTextFormat.Snippet,
    additionalTextEdits: [useInsertionEdit(file, ex.kindName, ex.packageName)],
    // `uri` routes `onCompletionResolve` to the owning services; `doc` is
    // rendered lazily there (same deferral as kind/symbol items).
    data: { uri, doc: ex.doc },
  };
  // Ranked at the ghost tier — below already-known kinds (RANK_SYMBOL). A typed
  // prefix match lifts it to the top via `finalize`'s prefix boost; `sub` orders
  // same-prefix siblings within the bucket (exact/core-package first, P3.1).
  return subRanked(item, RANK_GHOST, sub);
}

function baseKindToCompletionKind(rootBase: string): CompletionItemKind {
  switch (rootBase) {
    case "interface":
      return CompletionItemKind.Method;
    case "surface":
      return CompletionItemKind.Class;
    case "process":
    case "view":
      return CompletionItemKind.Function;
    default:
      return CompletionItemKind.Class; // module
  }
}

/** A `TextEdit` inserting `use <Kind> from <pkg>` — appended right after the
 *  file's existing `use` block, else at the top (after leading blank / `//`
 *  comment lines). Coordinates are 0-based (LSP); `UseDecl.source` is 1-based. */
function useInsertionEdit(
  file: FileState,
  kindName: string,
  pkg: string
): TextEdit {
  const line = `use ${kindName} from ${pkg}`;
  const uses = file.file.uses;
  if (uses.length > 0) {
    let afterLine = 0; // 0-based line to insert BEFORE
    for (const u of uses) {
      const endLine1 = u.source?.end.line ?? 0; // 1-based end line of the use
      if (endLine1 > afterLine) afterLine = endLine1; // 1-based end → 0-based next
    }
    const at = { line: afterLine, character: 0 };
    return { range: { start: at, end: at }, newText: `${line}\n` };
  }
  const topLine = firstContentLine(file.text);
  const at = { line: topLine, character: 0 };
  return { range: { start: at, end: at }, newText: `${line}\n` };
}

/** 0-based index of the first line that is neither blank nor a `//` comment —
 *  where a fresh `use` block should open. Empty/all-comment files → line 0. */
function firstContentLine(text: string): number {
  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i]!.trim();
    if (t === "" || t.startsWith("//")) continue;
    return i;
  }
  return 0;
}

function symbolCompletion(
  workspace: Workspace,
  sym: SymbolEntry,
  label: string = sym.name,
  rank: string = RANK_SYMBOL
): CompletionItem {
  const detail =
    sym.kind === "interface"
      ? `${sym.typeName ?? "interface"} on ${sym.ownerName ?? "?"}`
      : sym.kind === "module"
      ? `module ${sym.typeName ?? ""}`.trim()
      : sym.kind === "surface"
      ? `surface ${sym.typeName ?? ""}`.trim()
      : sym.kind;
  void workspace;
  // Heavy markdown docs are deferred to `resolveCompletion`; carry the raw
  // description + identity (for frequency/proximity ranking) in `data`.
  const item: CompletionItem = {
    label,
    kind: symbolToCompletionKind(sym),
    detail,
    // `qname` = the full structural path — the base the v0.11 twin pass
    // prefixes a space onto (a suffix/elided insert would not re-resolve).
    data: { id: sym.id, uri: sym.definingUri, doc: sym.description, qname: sym.qualifiedName },
  };
  // labelDetails (P2a item 5): the parent path (so same-named leaves under
  // different owners are tellable apart) + the kind at a glance. `hoistSuffix`
  // may later overwrite `detail` with the full path when it shortens the insert.
  const kindHint = symbolKindHint(sym);
  const ld: CompletionItemLabelDetails = {};
  if (sym.ownerName && label === sym.name) ld.detail = ` ${sym.ownerName}`;
  if (kindHint) ld.description = kindHint;
  if (ld.detail !== undefined || ld.description !== undefined) item.labelDetails = ld;
  // commitCharacters (P2a item 7): a module/surface is a dotted-path segment —
  // `.` accepts it and keeps typing the member chain. Leaves/interfaces are
  // terminal, so they carry none.
  if (sym.kind === "module" || sym.kind === "surface") item.commitCharacters = ["."];
  return ranked(item, rank);
}

/** A short kind hint for an item's `labelDetails.description` (P2a item 5). */
function symbolKindHint(sym: SymbolEntry): string | undefined {
  switch (sym.kind) {
    case "interface":
      return sym.typeName ?? "interface";
    case "module":
      return "module";
    case "surface":
      return "surface";
    case "process":
      return "process";
    case "subprocess":
      return "subprocess";
    case "view":
      return "view";
    default:
      return undefined;
  }
}

/**
 * Completion docs render with refs resolved but no label interpolation
 * (the previewed node has no resolved owner here; @label refs render as
 * `<missing:…>` markers, which is the correct sentinel per §4.11.3).
 */
function renderCompletionDescription(
  workspace: Workspace,
  description: string
): string {
  const ctx = makeDescriptionContext(workspace.descriptionIndex(), () => undefined);
  return renderDescription(description, ctx).markdown;
}

function symbolToCompletionKind(sym: SymbolEntry): CompletionItemKind {
  switch (sym.kind) {
    case "module":
      return CompletionItemKind.Module;
    case "surface":
      return CompletionItemKind.Class;
    case "interface":
      return CompletionItemKind.Method;
    case "process":
      return CompletionItemKind.Function;
    case "subprocess":
      return CompletionItemKind.Function;
    case "view":
      return CompletionItemKind.Struct;
    case "type":
      return CompletionItemKind.Interface;
    case "aspect":
      return CompletionItemKind.EnumMember;
    case "reentry":
      return CompletionItemKind.EnumMember;
  }
}

function kw(label: string, kind: CompletionItemKind): CompletionItem {
  const item: CompletionItem = { label, kind };
  const doc = KEYWORD_DOCS[label];
  if (doc) item.documentation = { kind: "markdown", value: doc };
  return ranked(item, RANK_KEYWORD);
}

/** A keyword completion that inserts a tab-through scaffold. */
function snippetKw(label: string, insert: string): CompletionItem {
  const item = kw(label, CompletionItemKind.Keyword);
  item.insertTextFormat = InsertTextFormat.Snippet;
  item.insertText = insert;
  return item;
}

/** A free-standing snippet completion (not tied to a grammar keyword). */
function snippetItem(label: string, insert: string, detail: string): CompletionItem {
  return ranked(
    {
      label,
      kind: CompletionItemKind.Snippet,
      detail,
      insertText: insert,
      insertTextFormat: InsertTextFormat.Snippet,
    },
    RANK_PRIMARY
  );
}

// Suppress an unused-import warning so editors don't strip the type re-export.
void (null as unknown as TokenType);
